import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import { parisCalendarDay, parisCalendarMonth } from "./lib/paris-calendar";
import {
  downloadMissionCalendar,
  googleCalendarMissionUrl,
} from "./calendar";
import { WorkerOfflineDay } from "./worker-offline-day";
import { ReviewQrCode } from "./review-qr";
import { WorkerDevelopmentJourney } from "./worker-development";
import { WorkerScheduleSummary } from "./worker-schedule-templates";
import { formatDurationHours } from "./duration-format";
import "./portal-rich.css";

export type PortalProfile = {
  id: string;
  role: "admin" | "employee" | "contractor" | "client";
  display_name: string;
  organization_id: string;
  email?: string;
  phone?: string;
  calendar_enabled?: boolean;
};
export type PortalClient = {
  id: string;
  name: string;
  profile_id?: string | null;
  category?: string | null;
  client_type?: "standard" | "concierge";
  address?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  notes?: string | null;
  calendar_enabled?: boolean;
  billing_emission?: string;
  billing_grouping?: string;
};
export type PortalWorker = {
  id: string;
  profile_id: string | null;
  kind: "employee" | "contractor";
  first_name: string | null;
  last_name: string | null;
  monthly_hours: number;
  daily_hours: number;
  mileage_enabled: boolean;
  pricing_mode?: string | null;
  hourly_rate?: number | null;
  fixed_fee?: number | null;
  monthly_fee?: number | null;
  business_name?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  address?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  profile_notes?: string | null;
  skills?: string[];
  company_organization_id?: string | null;
  business_relationship_id?: string | null;
};
export type PortalMission = {
  id: string;
  client_id: string;
  property_id?: string | null;
  client_display_name?: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
  workflow_status?: string;
  billing_status?: string;
  location: string;
  planned_hours: number;
  amount_ex_vat?: number;
  billing_mode?: string;
  notes?: string | null;
  client_report?: string | null;
  next_actions?: string | null;
  admin_notes?: string | null;
  required_skills?: string[];
  is_test?: boolean;
  client?: { name: string } | null;
};
export type PortalAssignment = {
  id: string;
  mission_id: string;
  worker_id: string;
  assigned_hours: number;
  status: string;
  pricing_mode?: string | null;
  agreed_rate?: number | null;
  agreed_amount?: number | null;
  worker_rate_card_id?: string | null;
  proposed_at?: string | null;
  responded_at?: string | null;
  response_notes?: string | null;
};
export type PortalWorkerRateCard = {
  id: string;
  worker_id: string;
  name: string;
  pricing_mode: "hourly" | "fixed" | "per_unit";
  rate: number;
  unit_label: string | null;
  notes: string | null;
  active: boolean;
};
export type PortalReport = {
  id: string;
  mission_id: string;
  worker_id: string;
  result: string;
  actual_hours: number;
  observations: string | null;
  client_note: string | null;
  status: string;
  submitted_at: string;
  route_team_id?: string | null;
  submitted_by_worker_id?: string | null;
  team_report_batch_id?: string | null;
};
export type PortalReportSubmission = {
  id: string;
  mission_id: string;
  worker_id: string;
  submitted_by_worker_id: string;
  route_team_id: string | null;
  team_report_batch_id: string | null;
  source: "individual" | "team_leader";
  result: string;
  actual_hours: number;
  observations: string | null;
  client_note: string | null;
  submitted_at: string;
};
export type PortalMissionActivityEntry = {
  id: string;
  report_id: string;
  mission_id: string;
  worker_id: string;
  activity_key: string;
  activity_label: string;
  minutes: number;
  notes: string | null;
  created_at: string;
};
export type PortalRouteTeam = {
  id: string;
  work_date: string;
  name: string;
  notes: string | null;
  leader_worker_id?: string | null;
};
export type PortalRouteTeamMember = {
  team_id: string;
  work_date: string;
  worker_id: string;
};
export type PortalRouteTeamContext = {
  team_id: string;
  work_date: string;
  leader_worker_id: string | null;
  worker_id: string;
  display_name: string;
  mission_id: string | null;
  assigned_hours: number | null;
  assignment_status: string | null;
};
export type PortalInvoice = {
  id: string;
  client_id: string;
  number: string | null;
  billing_period: string | null;
  status: string;
  issue_on: string | null;
  due_on: string | null;
  total_ex_vat: number;
  total_vat: number;
  total_inc_vat: number;
  amount_paid?: number;
  last_payment_on?: string | null;
  henrri_id?: string | null;
  henrri_pdf_url?: string | null;
  payment_terms_mode?: "immediate" | "days_after_issue" | "end_of_month" | null;
  payment_terms_days?: number | null;
};
export type PortalInvoiceLine = {
  id: string;
  invoice_id: string;
  mission_id: string | null;
  description: string;
  quantity: number;
  unit_price_ex_vat: number;
  vat_rate: number;
  position: number;
};
export type PortalInvoicePayment = {
  id: string;
  invoice_id: string;
  received_on: string;
  amount: number;
  method: string;
  reference: string | null;
  status: string;
  created_at: string;
};
export type PortalCommercialEvent = {
  id: string;
  invoice_id: string | null;
  event_type: string;
  status_from: string | null;
  status_to: string | null;
  created_at: string;
};
export type PortalService = {
  id: string;
  client_id: string;
  name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate?: number;
  default_duration_hours?: number | null;
};
export type PortalRequest = {
  id: string;
  client_id: string;
  property_id?: string | null;
  service_id: string | null;
  property_service_id?: string | null;
  requested_service_keys?: string[];
  desired_starts_at: string;
  desired_ends_at: string;
  location: string;
  client_notes: string | null;
  status: string;
  created_at: string;
};
export type PortalDocument = {
  id: string;
  kind: string;
  period: string | null;
  original_name: string;
  storage_path?: string;
  expires_on?: string | null;
  status?: string;
  requirement_id?: string | null;
  client_requirement_id?: string | null;
  created_at?: string;
};
export type PortalSupplierInvoice = {
  id: string;
  worker_id: string;
  source: "pdf" | "henrri";
  invoice_number: string;
  issue_on: string;
  service_period_start: string;
  service_period_end: string;
  due_on: string;
  currency: "EUR";
  total_ex_vat: number;
  total_vat: number;
  total_inc_vat: number;
  document_id: string | null;
  external_provider?: "henrri" | null;
  external_id?: string | null;
  external_pdf_url?: string | null;
  status: "submitted" | "under_review" | "approved" | "rejected" | "paid";
  contractor_notes: string | null;
  review_notes: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  paid_on: string | null;
  payment_reference: string | null;
};
export type PortalSupplierInvoiceMission = {
  invoice_id: string;
  mission_id: string;
};
export type PortalWorkerCancellationRule = {
  id: string;
  worker_id: string;
  contract_id: string;
  enabled: boolean;
  notice_hours: number;
  penalty_mode: "none" | "fixed" | "percentage_of_assignment";
  penalty_value: number;
  clause_reference: string | null;
};
export type PortalWorkerCancellationNotice = {
  id: string;
  mission_id: string;
  assignment_id: string;
  worker_id: string;
  reason: string;
  evidence_document_id?: string | null;
  submitted_at: string;
  notice_hours: number;
  proposed_penalty_ex_vat: number;
  approved_penalty_ex_vat: number | null;
  status: "submitted" | "approved" | "rejected" | "cancelled";
  review_notes: string | null;
};
export type PortalMissionFinancialAdjustment = {
  id: string;
  mission_id: string;
  assignment_id: string | null;
  worker_id: string | null;
  adjustment_kind: "client_supplement" | "supplier_surcharge" | "supplier_penalty" | "supplier_deduction";
  amount_ex_vat: number;
  vat_rate: number;
  reason: string;
  status: string;
  supplier_invoice_id: string | null;
  created_at: string;
};
export type PortalAvailability = {
  id: string;
  worker_id: string;
  starts_at: string;
  ends_at: string;
  availability: "available" | "unavailable";
  notes: string | null;
};
export type PortalRequirement = {
  id: string;
  worker_id: string;
  kind: string;
  label: string;
  due_on: string | null;
  status: string;
  notes: string | null;
};
export type PortalContract = {
  id: string;
  worker_id: string;
  title: string;
  status: string;
  starts_on: string | null;
  ends_on: string | null;
  document_id: string | null;
  rendered_content?: string | null;
  generated_at?: string | null;
  notes: string | null;
};
export type PortalClientContract = {
  id: string;
  client_id: string;
  title: string;
  template_version: number | null;
  status: string;
  starts_on: string | null;
  ends_on: string | null;
  document_id: string | null;
  rendered_content?: string | null;
  generated_at?: string | null;
  notes: string | null;
};
export type PortalSignatureEnvelope = {
  id: string;
  source_type: "client_contract" | "worker_contract";
  source_id: string;
  signer_profile_id: string | null;
  signer_name: string;
  signer_email: string;
  provider: string | null;
  status: string;
  requested_at: string | null;
  viewed_at: string | null;
  signed_at: string | null;
  last_error: string | null;
};
export type PortalClientRequirement = {
  id: string;
  client_id: string;
  contract_id: string | null;
  kind: string;
  label: string;
  due_on: string | null;
  status: string;
  notes: string | null;
};
export type PortalProperty = {
  id: string;
  client_id: string;
  name: string;
  address: string;
  latitude?: number | null;
  longitude?: number | null;
  contact_name?: string | null;
  access_notes?: string | null;
  active: boolean;
};
export type PortalPropertyService = {
  id: string;
  property_id: string;
  name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours?: number | null;
  notes?: string | null;
  active: boolean;
};
export type PortalMissionServiceLine = {
  id: string;
  mission_id: string;
  client_service_id: string | null;
  property_service_id: string | null;
  label_snapshot: string;
  billing_mode_snapshot: string;
  quantity: number;
  unit_price_ex_vat: number;
  amount_ex_vat: number;
  vat_rate: number;
  planned_hours: number | null;
  position: number;
};
export type PortalPropertyRequest = {
  id: string;
  client_id: string;
  request_kind?: "create" | "update" | "archive";
  target_property_id?: string | null;
  name: string;
  address: string;
  contact_name?: string | null;
  access_notes?: string | null;
  requested_reason?: string | null;
  previous_snapshot?: Record<string, unknown> | null;
  status: string;
  admin_notes?: string | null;
  created_property_id?: string | null;
  created_at: string;
};
export type PortalPropertyServiceQuoteRequest = {
  id: string;
  client_id: string;
  property_id: string;
  service_name: string;
  requested_description: string;
  client_notes?: string | null;
  status: string;
  quote_id?: string | null;
  created_property_service_id?: string | null;
  admin_response?: string | null;
  client_response_kind?: "accepted" | "revision_requested" | "rejected" | null;
  client_response_reason?: string | null;
  responded_at?: string | null;
  created_at: string;
};
export type PortalQuote = {
  id: string;
  number: string;
  status: string;
  valid_until: string | null;
  total_ex_vat: number;
  total_vat: number;
  total_inc_vat: number;
};
export type PortalPreferredWorker = {
  id: string;
  client_id: string;
  property_id: string | null;
  worker_id: string;
  priority: number;
  active: boolean;
};
export type PortalServiceProposal = {
  id: string;
  client_id: string;
  property_id: string | null;
  mission_request_id: string | null;
  name: string;
  billing_mode: string;
  unit_price_ex_vat: number;
  vat_rate: number;
  default_duration_hours: number | null;
  notes: string | null;
  status: string;
  client_response_reason?: string | null;
  revision_requested?: boolean;
  responded_at: string | null;
  created_service_id: string | null;
  created_property_service_id: string | null;
  created_at: string;
};
export type PortalReportAttachment = {
  id: string;
  report_id: string;
  worker_id: string;
  storage_path: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  client_visible: boolean;
  created_at: string;
};
export type PortalTimeEntry = {
  id: string;
  worker_id: string;
  work_date: string;
  worked_minutes: number;
  status: string;
};
export type PortalCalendarConnection = {
  id: string;
  profile_id: string;
  status: "not_connected" | "pending" | "connected" | "error" | "disabled";
  calendar_id: string;
  calendar_name: string | null;
  google_account_email: string | null;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_error: string | null;
  connected_at: string | null;
};
export type PortalMissionChangeEvent = {
  id: string;
  mission_id: string;
  event_type: "modification" | "supplement" | "urgent" | "reschedule" | "cancellation" | "access_issue";
  status: "proposed" | "approved" | "applied" | "rejected" | "cancelled";
  reason: string;
  operational_notes: string | null;
  proposed_changes: Record<string, unknown>;
  client_amount_delta_ex_vat: number;
  requires_client_approval: boolean;
  client_approval_status: "not_required" | "pending" | "accepted" | "rejected";
  created_at: string;
};
export type PortalMissionQualityClaim = {
  id: string;
  mission_id: string;
  client_id: string;
  requested_by: string;
  request_kind: "complaint" | "touchup" | "complaint_and_touchup";
  category: "quality" | "missing_item" | "damage" | "delay" | "behavior" | "other";
  description: string;
  status: "submitted" | "under_review" | "retouch_requested" | "retouch_scheduled" | "resolved" | "rejected" | "cancelled";
  admin_qualification: "founded" | "partly_founded" | "unfounded" | "commercial_gesture" | null;
  admin_notes: string | null;
  client_response: string | null;
  retouch_mission_id: string | null;
  reviewed_at: string | null;
  resolved_at: string | null;
  created_at: string;
};
export type PortalMissionQualityClaimEvent = {
  id: string;
  claim_id: string;
  event_type: string;
  previous_status: string | null;
  new_status: string;
  note: string | null;
  client_visible: boolean;
  created_at: string;
};
export type PortalMissionQualityClaimAttachment = {
  id: string;
  claim_id: string;
  storage_path: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
};
export type PortalWorkerQualityMetric = {
  worker_id: string;
  display_name: string;
  worker_kind: "employee" | "contractor";
  completed_mission_count: number;
  total_claim_count: number;
  reviewed_claim_count: number;
  founded_claim_count: number;
  partly_founded_claim_count: number;
  touchup_count: number;
  verified_return_rate: number;
  average_resolution_hours: number | null;
};
export type PortalMissionQualityAnalytic = {
  dimension: "worker" | "team" | "service" | "client";
  entity_id: string;
  label: string;
  completed_mission_count: number;
  raw_claim_count: number;
  reviewed_claim_count: number;
  founded_claim_count: number;
  partly_founded_claim_count: number;
  unfounded_claim_count: number;
  verified_issue_mission_count: number;
  touchup_count: number;
  repeated_verified_return_count: number;
  verified_return_rate: number;
  average_resolution_hours: number | null;
  previous_completed_mission_count: number;
  previous_reviewed_claim_count: number;
  previous_verified_issue_mission_count: number;
  previous_verified_return_rate: number;
  verified_return_rate_delta: number;
};
export type PortalInventoryItem = {
  id: string;
  name: string;
  category?: string | null;
  unit: string;
  current_quantity: number;
  item_kind?: "stock" | "kit";
  active: boolean;
};
export type PortalGoogleReviewRequest = {
  id: string;
  organization_id: string;
  mission_id: string;
  client_id: string;
  recipient_profile_id: string;
  source_report_id: string;
  request_url_snapshot: string;
  status: "prepared" | "opened" | "dismissed" | "expired";
  available_at: string;
  opened_at?: string | null;
  dismissed_at?: string | null;
  created_at: string;
};
export type PortalLeave = {
  id: string;
  worker_id: string;
  starts_on: string;
  ends_on: string;
  reason: string | null;
  status: string;
};
export type PortalWorkerPlannedShift = {
  id: string;
  worker_id: string;
  title: string;
  shift_kind: "work" | "administrative" | "training" | "meeting" | "other";
  starts_at: string;
  ends_at: string;
  paid_break_minutes: number;
  location: string | null;
  notes: string | null;
  status: "draft" | "published" | "cancelled" | "completed";
  acknowledged_at: string | null;
};
export type PortalData = {
  unavailableCapabilities?: string[];
  clients: PortalClient[];
  missions: PortalMission[];
  assignments: PortalAssignment[];
  workers: PortalWorker[];
  services: PortalService[];
  requests: PortalRequest[];
  reports: PortalReport[];
  missionReportSubmissions?: PortalReportSubmission[];
  missionReportActivityEntries?: PortalMissionActivityEntry[];
  invoices: PortalInvoice[];
  invoiceLines?: PortalInvoiceLine[];
  invoicePayments?: PortalInvoicePayment[];
  commercialEvents?: PortalCommercialEvent[];
  documents: PortalDocument[];
  supplierInvoices?: PortalSupplierInvoice[];
  supplierInvoiceMissions?: PortalSupplierInvoiceMission[];
  missionChangeEvents?: PortalMissionChangeEvent[];
  missionQualityClaims?: PortalMissionQualityClaim[];
  missionQualityClaimEvents?: PortalMissionQualityClaimEvent[];
  missionQualityClaimAttachments?: PortalMissionQualityClaimAttachment[];
  workerQualityMetrics?: PortalWorkerQualityMetric[];
  googleReviewRequests?: PortalGoogleReviewRequest[];
  workerCancellationRules?: PortalWorkerCancellationRule[];
  workerCancellationNotices?: PortalWorkerCancellationNotice[];
  missionFinancialAdjustments?: PortalMissionFinancialAdjustment[];
  availability: PortalAvailability[];
  requirements: PortalRequirement[];
  contracts: PortalContract[];
  properties: PortalProperty[];
  propertyServices: PortalPropertyService[];
  missionServiceLines?: PortalMissionServiceLine[];
  propertyRequests?: PortalPropertyRequest[];
  propertyServiceQuoteRequests?: PortalPropertyServiceQuoteRequest[];
  quotes?: PortalQuote[];
  preferredWorkers: PortalPreferredWorker[];
  serviceProposals?: PortalServiceProposal[];
  reportAttachments?: PortalReportAttachment[];
  times?: PortalTimeEntry[];
  leaves?: PortalLeave[];
  clientContracts?: PortalClientContract[];
  clientRequirements?: PortalClientRequirement[];
  workerRateCards?: PortalWorkerRateCard[];
  calendarConnections?: PortalCalendarConnection[];
  signatureEnvelopes?: PortalSignatureEnvelope[];
  inventoryItems?: PortalInventoryItem[];
  dailyRouteTeams?: PortalRouteTeam[];
  dailyRouteTeamMembers?: PortalRouteTeamMember[];
  routeTeamContext?: PortalRouteTeamContext[];
  organization?: {
    name?: string | null;
    address?: string | null;
    postal_code?: string | null;
    city?: string | null;
  } | null;
};

const dateTime = (value: string) =>
  new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
const dayTitle = (value: Date) =>
  new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(value);
const money = (value: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    value || 0,
  );
const compactPortalMoney = (value: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: Math.abs(value) < 1000 ? 0 : 1,
  }).format(value || 0);
const missingPortalObject = (message = "") =>
  /does not exist|schema cache|could not find|PGRST205|PGRST202/i.test(message);

function useWorkerPlannedShifts(workerId: string | undefined) {
  const [rows, setRows] = useState<PortalWorkerPlannedShift[]>([]),
    [available, setAvailable] = useState(true),
    [loading, setLoading] = useState(false);

  const load = async () => {
    if (!workerId) {
      setRows([]);
      return;
    }
    setLoading(true);
    const result = await supabase!
      .from("worker_planned_shifts")
      .select("id,worker_id,title,shift_kind,starts_at,ends_at,paid_break_minutes,location,notes,status,acknowledged_at")
      .eq("worker_id", workerId)
      .order("starts_at", { ascending: false });
    setLoading(false);
    if (result.error) {
      setAvailable(!missingPortalObject(result.error.message));
      setRows([]);
      return;
    }
    setAvailable(true);
    setRows((result.data || []).filter((row) => row.status !== "cancelled") as PortalWorkerPlannedShift[]);
  };

  useEffect(() => {
    void load();
  }, [workerId]);

  return { rows, available, loading, reload: load };
}
const parisDateKey = (value: string) => {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};
const routeTeamForMission = (
  data: PortalData,
  worker: PortalWorker | null,
  mission: PortalMission,
) => {
  if (!worker) return null;
  const date = parisDateKey(mission.starts_at),
    membership = (data.dailyRouteTeamMembers || []).find(
      (item) => item.worker_id === worker.id && item.work_date === date,
    );
  return membership
    ? (data.dailyRouteTeams || []).find((team) => team.id === membership.team_id) || null
    : null;
};
const statusLabels: Record<string, string> = {
  pending: "En attente",
  proposed: "À accepter",
  accepted: "Acceptée",
  refused: "Refusée",
  assigned: "Affectée",
  confirmed: "Confirmée",
  reported: "Compte rendu envoyé",
  approved: "Validée",
  completed: "Terminée",
  cancelled: "Annulée",
  planned: "À venir",
  to_invoice: "À facturer",
  queued: "Facturation programmée",
  invoiced: "Facturée",
  draft: "Brouillon",
  ready: "À valider",
  quoted: "Devis à valider",
  changes_requested: "Nouvelle proposition demandée",
  sent: "Envoyé",
  signed: "Signé",
  active: "Actif",
  paid: "Payée",
  partially_paid: "Partiellement payée",
  overdue: "En retard",
  credited: "Avoir",
  required: "À déposer",
  submitted: "Déposé",
  validated: "Validé",
  rejected: "Refusé",
  expired: "Expiré",
  waived: "Dispensé",
  available: "Disponible",
  unavailable: "Indisponible",
  terminated: "Résilié",
  connected: "Connecté",
  not_connected: "Non connecté",
  error: "Erreur",
  disabled: "Désactivé",
};
const statusLabel = (value?: string) =>
  statusLabels[value || ""] || value || "Non défini";
const missionAgendaStatus = (
  mission: PortalMission,
  claims: PortalMissionQualityClaim[] = [],
) => {
  const hasOpenClaim = claims.some(
    (claim) =>
      claim.mission_id === mission.id &&
      !["resolved", "rejected", "cancelled"].includes(claim.status),
  );
  if (hasOpenClaim) return { label: "Problème signalé", tone: "problem" };
  if (mission.status === "completed" || mission.workflow_status === "approved")
    return { label: "Terminée", tone: "done" };
  if (["in_progress", "started"].includes(mission.status))
    return { label: "En cours", tone: "progress" };
  return { label: "À faire", tone: "todo" };
};
const workerName = (worker?: PortalWorker | null) =>
  worker
    ? [worker.first_name, worker.last_name].filter(Boolean).join(" ")
    : "Intervenant";
const startOfWeek = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};
const dayKey = (date: Date) => parisDateKey(date.toISOString());
const missionDay = (mission: PortalMission) =>
  parisDateKey(mission.starts_at);
const missionClient = (
  mission: PortalMission,
  properties?: PortalProperty[],
) => {
  const property = properties?.find((p) => p.id === mission.property_id);
  return property
    ? `${property.name} · ${mission.client_display_name || mission.client?.name || ""}`
    : mission.client_display_name || mission.client?.name || "Mission";
};
const missionServiceSummary = (
  mission: PortalMission,
  serviceLines: PortalMissionServiceLine[] = [],
) =>
  serviceLines
    .filter((line) => line.mission_id === mission.id)
    .sort((left, right) => left.position - right.position)
    .map((line) => line.label_snapshot)
    .join(" · ");

const contractorMonthRevenue = (
  data: PortalData,
  worker: PortalWorker | null,
  year: number,
  monthIndex: number,
) => {
  if (!worker) return 0;
  const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
    missions = new Map(data.missions.map((mission) => [mission.id, mission])),
    reports = new Map(data.reports.map((report) => [report.mission_id, report])),
    rateCards = new Map(
      (data.workerRateCards || []).map((rate) => [rate.id, rate]),
    ),
    assignments = data.assignments.filter((assignment) => {
      const mission = missions.get(assignment.mission_id);
      return (
        assignment.worker_id === worker.id &&
        ["accepted", "assigned"].includes(assignment.status) &&
        !!mission &&
        mission.ends_at.slice(0, 7) === monthKey &&
        mission.status !== "cancelled" &&
        (new Date(mission.ends_at) <= new Date() ||
          !!reports.get(mission.id) ||
          ["reported", "approved"].includes(mission.workflow_status || ""))
      );
    });
  const nonMonthly = assignments
    .filter(
      (assignment) =>
        (assignment.pricing_mode || worker.pricing_mode) !== "monthly",
    )
    .reduce((sum, assignment) => {
      const mission = missions.get(assignment.mission_id)!,
        report = reports.get(assignment.mission_id),
        mode = assignment.pricing_mode || worker.pricing_mode,
        worked =
          report?.actual_hours ||
          assignment.assigned_hours ||
          mission.planned_hours ||
          0,
        rateCard = assignment.worker_rate_card_id
          ? rateCards.get(assignment.worker_rate_card_id)
          : null;
      if (assignment.agreed_amount != null)
        return sum + Number(assignment.agreed_amount);
      if (mode === "hourly")
        return sum + worked * Number(assignment.agreed_rate ?? worker.hourly_rate ?? 0);
      if (["fixed", "per_unit"].includes(mode || ""))
        return sum + Number(rateCard?.rate ?? worker.fixed_fee ?? 0);
      return sum;
    }, 0);
  const monthly = assignments.some(
    (assignment) =>
      (assignment.pricing_mode || worker.pricing_mode) === "monthly",
  )
    ? Number(
        worker.monthly_fee ??
          assignments.find(
            (assignment) =>
              (assignment.pricing_mode || worker.pricing_mode) === "monthly",
          )?.agreed_amount ??
          0,
      )
    : 0;
  return nonMonthly + monthly;
};

function smoothRevenuePath(points: Array<{ x: number; y: number }>) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  return points.slice(1).reduce((path, point, index) => {
    const previous = points[index],
      middleX = (previous.x + point.x) / 2;
    return `${path} C ${middleX} ${previous.y}, ${middleX} ${point.y}, ${point.x} ${point.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
}

function ContractorAnnualRevenueChart({
  data,
  worker,
  year,
  compact = false,
}: {
  data: PortalData;
  worker: PortalWorker | null;
  year: number;
  compact?: boolean;
}) {
  const months = Array.from({ length: 12 }, (_, monthIndex) => ({
      label: new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(
        new Date(year, monthIndex, 1),
      ),
      amount: contractorMonthRevenue(data, worker, year, monthIndex),
    })),
    total = months.reduce((sum, month) => sum + month.amount, 0),
    maximum = Math.max(1, ...months.map((month) => month.amount)),
    points = months.map((month, index) => ({
      x: 4 + (index / 11) * 92,
      y: 88 - (month.amount / maximum) * 74,
    })),
    path = smoothRevenuePath(points),
    currentMonth = new Date().getFullYear() === year ? new Date().getMonth() : -1;
  return (
    <section className={`panel contractor-revenue-chart executive-chart-card profile-financial-chart${compact ? " compact" : ""}`}>
      <div className="executive-chart-card-head">
        <div>
          <span className="eyebrow">Activité avec ARD · {year}</span>
          <h2>Chiffre d’affaires annuel</h2>
          <small>Montants HT · même lecture visuelle que le pilotage administrateur</small>
        </div>
        <div>
          <div className="contractor-revenue-total">
            <span>Total réalisé</span>
            <strong>{money(total)} HT</strong>
          </div>
          <div className="executive-chart-legend" aria-label="Légende du graphique">
            <span><i className="revenue" /> CA réalisé avec ARD</span>
          </div>
        </div>
      </div>
      <div className="executive-three-lines-layout profile-financial-trend-layout">
        <div className="annual-performance-scale" aria-hidden="true">
          <span>{compactPortalMoney(maximum)}</span>
          <span>0 €</span>
        </div>
        <div className="executive-three-lines-plot">
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-label={`Évolution mensuelle du chiffre d’affaires réalisé avec ARD en ${year}`}
          >
            <line className="trend-zero-line" x1="0" x2="100" y1="88" y2="88" vectorEffect="non-scaling-stroke" />
            <path className="revenue-line" d={path} vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="executive-trend-months" style={{ gridTemplateColumns: "repeat(12, minmax(30px, 1fr))" }}>
            {months.map((month, index) => (
              <span className={index === currentMonth ? "current" : ""} key={month.label}>{month.label.replace(".", "")}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="contractor-revenue-months" aria-label="Montants mensuels">
        {months.map((month, index) => (
          <span className={index === currentMonth ? "current" : ""} key={month.label}>
            <small>{month.label}</small>
            <strong>{money(month.amount)}</strong>
          </span>
        ))}
      </div>
      <p className="contractor-revenue-note">
        Les forfaits restent comptés au montant convenu, quel que soit le temps réellement passé.
      </p>
    </section>
  );
}

const escapeDocumentText = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

function ClientAnnualBillingChart({
  invoices,
  year,
}: {
  invoices: PortalInvoice[];
  year: number;
}) {
  const months = Array.from({ length: 12 }, (_, monthIndex) => {
      const key = `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
        rows = invoices.filter(
          (invoice) =>
            invoice.issue_on?.slice(0, 7) === key && invoice.status !== "draft",
        );
      return {
        label: new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(
          new Date(year, monthIndex, 1),
        ),
        exVat: rows.reduce((sum, invoice) => sum + Number(invoice.total_ex_vat || 0), 0),
        incVat: rows.reduce((sum, invoice) => sum + Number(invoice.total_inc_vat || 0), 0),
      };
    }),
    totalExVat = months.reduce((sum, month) => sum + month.exVat, 0),
    totalIncVat = months.reduce((sum, month) => sum + month.incVat, 0),
    maximum = Math.max(1, ...months.map((month) => month.incVat)),
    points = months.map((month, index) => ({
      x: 4 + (index / 11) * 92,
      y: 88 - (month.incVat / maximum) * 74,
    })),
    path = smoothRevenuePath(points),
    currentMonth = new Date().getFullYear() === year ? new Date().getMonth() : -1;
  return (
    <section className="panel contractor-revenue-chart executive-chart-card profile-financial-chart client-billing-chart">
      <div className="executive-chart-card-head">
        <div>
          <span className="eyebrow">Factures émises · {year}</span>
          <h2>Évolution annuelle</h2>
          <small>Factures TTC · même lecture visuelle que le pilotage administrateur</small>
        </div>
        <div>
          <div className="client-billing-chart-totals">
            <span><small>Total HT</small><strong>{money(totalExVat)}</strong></span>
            <span><small>Total TTC</small><strong>{money(totalIncVat)}</strong></span>
          </div>
          <div className="executive-chart-legend" aria-label="Légende du graphique">
            <span><i className="revenue" /> Factures TTC</span>
          </div>
        </div>
      </div>
      <div className="executive-three-lines-layout profile-financial-trend-layout">
        <div className="annual-performance-scale" aria-hidden="true">
          <span>{compactPortalMoney(maximum)}</span>
          <span>0 €</span>
        </div>
        <div className="executive-three-lines-plot">
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-label={`Évolution mensuelle des factures TTC en ${year}`}
          >
            <line className="trend-zero-line" x1="0" x2="100" y1="88" y2="88" vectorEffect="non-scaling-stroke" />
            <path className="revenue-line" d={path} vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="executive-trend-months" style={{ gridTemplateColumns: "repeat(12, minmax(30px, 1fr))" }}>
            {months.map((month, index) => (
              <span className={index === currentMonth ? "current" : ""} key={month.label}>{month.label.replace(".", "")}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="contractor-revenue-months" aria-label="Facturation mensuelle TTC">
        {months.map((month, index) => (
          <span className={index === currentMonth ? "current" : ""} key={month.label}>
            <small>{month.label}</small>
            <strong>{money(month.incVat)}</strong>
          </span>
        ))}
      </div>
    </section>
  );
}

function StatusPill({ value }: { value?: string }) {
  return (
    <span className={`portal-status ${value || ""}`}>{statusLabel(value)}</span>
  );
}

export function MapsLink({
  location,
  label = "Itinéraire",
}: {
  location: string;
  label?: string;
}) {
  return (
    <a
      className="portal-link"
      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`}
      target="_blank"
      rel="noreferrer"
    >
      🧭 {label}
    </a>
  );
}

type MissionActivityCatalogItem = {
  activity_key: string;
  activity_label: string;
  source_kind: "questionnaire" | "standard";
};

type PortalMissionChecklistEvidence = {
  id: string;
  storage_path: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
};

type PortalMissionChecklistItem = {
  id: string;
  run_id: string;
  checklist_name: string;
  item_key: string;
  label: string;
  instructions: string | null;
  position: number;
  is_required: boolean;
  minimum_photo_count: number;
  is_completed: boolean;
  notes: string | null;
  zone_key: string;
  zone_label: string;
  zone_display_label: string;
  zone_repeatable: boolean;
  zone_instance_index: number;
  proof_exception: {
    id: string;
    reason: string;
    status: "pending_review" | "accepted" | "rejected" | "resolved_by_evidence";
    requested_at: string;
    review_note: string | null;
    reviewed_at: string | null;
  } | null;
  evidence: PortalMissionChecklistEvidence[];
};

type PortalMissionChecklist = {
  available: boolean;
  mission_id: string;
  run_id?: string;
  name?: string;
  version?: number;
  instructions?: string | null;
  status?: "active" | "ready" | "review_required" | "submitted" | "overridden";
  override_reason?: string | null;
  pending_exception_count?: number;
  run_count?: number;
  can_edit?: boolean;
  missing?: Array<Record<string, unknown>>;
  runs?: Array<{
    run_id: string;
    name: string;
    version: number;
    status: "active" | "ready" | "review_required" | "submitted" | "overridden";
    service_labels?: string[];
    missing?: Array<Record<string, unknown>>;
  }>;
  repeatable_zones?: Array<{
    run_id: string;
    checklist_name: string;
    zone_key: string;
    zone_label: string;
    instance_count: number;
  }>;
  items: PortalMissionChecklistItem[];
};

const missionActivitySuggestions: MissionActivityCatalogItem[] = [
  { activity_key: "system:preparation", activity_label: "Préparation et protection", source_kind: "standard" },
  { activity_key: "system:cleaning", activity_label: "Nettoyage principal", source_kind: "standard" },
  { activity_key: "system:windows", activity_label: "Vitres", source_kind: "standard" },
  { activity_key: "system:floors", activity_label: "Traitement des sols", source_kind: "standard" },
  { activity_key: "system:debris", activity_label: "Évacuation de gravats", source_kind: "standard" },
  { activity_key: "system:finishing", activity_label: "Finitions et contrôle", source_kind: "standard" },
  { activity_key: "system:storage", activity_label: "Rangement du matériel", source_kind: "standard" },
];

const normalizedActivityLabel = (value: string) =>
  value.trim().toLocaleLowerCase("fr-FR").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function MissionDetail({
  mission,
  mode,
  showTimeObjectives = false,
  property,
  serviceLines = [],
  assignment,
  report,
  qualityClaims = [],
  reviewRequest,
  onQualityClaim,
  onReviewOpen,
  onReviewDismiss,
  onComplete,
  onCancel,
  cancellationPending,
  onClose,
}: {
  mission: PortalMission;
  mode: "client" | "worker";
  showTimeObjectives?: boolean;
  property?: PortalProperty;
  serviceLines?: PortalMissionServiceLine[];
  assignment?: PortalAssignment;
  report?: PortalReport;
  qualityClaims?: PortalMissionQualityClaim[];
  reviewRequest?: PortalGoogleReviewRequest;
  onQualityClaim?: () => void;
  onReviewOpen?: () => void;
  onReviewDismiss?: () => void;
  onComplete?: () => void;
  onCancel?: () => void;
  cancellationPending?: boolean;
  onClose: () => void;
}) {
  return (
    <div
      className="portal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className="portal-detail"
        role="dialog"
        aria-modal="true"
        aria-label="Détail de la mission"
      >
        <div className="portal-detail-head">
          <div>
            <span className="eyebrow">Détail de la mission</span>
            <h2>{missionClient(mission)}</h2>
          </div>
          <button className="icon-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        {mode === "worker" && onComplete && !report && (
          <button className="portal-button success mission-complete-primary" onClick={onComplete}>
            Clôturer la mission
          </button>
        )}
        <div className="detail-grid">
          <div>
            <span>Début</span>
            <strong>{dateTime(mission.starts_at)}</strong>
          </div>
          {(mode === "client" || showTimeObjectives) && (
            <div>
              <span>{mode === "client" ? "Fin prévue" : "Fin théorique"}</span>
              <strong>{dateTime(mission.ends_at)}</strong>
            </div>
          )}
          <div>
            <span>Adresse</span>
            <strong>{mission.location || property?.address || "À préciser"}</strong>
          </div>
          <div>
            <span>Bien concerné</span>
            <strong>{property?.name || "Bien non renseigné"}</strong>
          </div>
          <div>
            <span>État</span>
            <StatusPill value={mission.workflow_status || mission.status} />
          </div>
          {mode === "client" ? (
            <>
              <div>
                <span>Montant prévu HT</span>
                <strong>{money(mission.amount_ex_vat || 0)}</strong>
              </div>
              <div>
                <span>Facturation</span>
                <StatusPill value={mission.billing_status} />
              </div>
            </>
          ) : showTimeObjectives ? (
            <>
              <div>
                <span>Objectif temps</span>
                <strong>
                  {formatDurationHours(
                    assignment?.assigned_hours || mission.planned_hours || 0,
                  )}
                </strong>
              </div>
              <div>
                <span>Compte rendu</span>
                <StatusPill value={report?.status || "pending"} />
              </div>
            </>
          ) : (
            <div>
              <span>Compte rendu</span>
              <StatusPill value={report?.status || "pending"} />
            </div>
          )}
        </div>
        <div className="detail-note mission-operational-scope">
          <strong>Prestations attendues</strong>
          {serviceLines.length ? (
            <ul className="mission-service-summary-list">
              {[...serviceLines]
                .sort((left, right) => left.position - right.position)
                .map((line) => (
                  <li key={line.id}>
                    <span>{line.label_snapshot}</span>
                    {showTimeObjectives && line.planned_hours != null && (
                      <small>{line.planned_hours.toLocaleString("fr-FR")} h d’objectif</small>
                    )}
                  </li>
                ))}
            </ul>
          ) : (
            <p className="notice warning">
              Le contenu de cette mission doit être précisé par l’administration avant l’intervention.
            </p>
          )}
          {mode === "worker" && (
            <small>
              Les montants facturés au client restent confidentiels. Cette fiche affiche uniquement ce qui est nécessaire pour réaliser la mission.
            </small>
          )}
        </div>
        {mode === "worker" && mission.notes && (
          <div className="detail-note">
            <strong>Consignes</strong>
            <p>{mission.notes}</p>
          </div>
        )}
        {mode === "client" && (
          <>
            <div className="detail-note">
              <strong>Compte rendu ARD</strong>
              <p>
                {mission.client_report ||
                  "Le compte rendu sera visible ici après validation de la mission."}
              </p>
            </div>
            {mission.next_actions && (
              <div className="detail-note action">
                <strong>Actions réalisées ou à prévoir</strong>
                <p>{mission.next_actions}</p>
              </div>
            )}
            {!!qualityClaims.length && (
              <div className="detail-note quality-claim-summary">
                <strong>Réclamations et retouches</strong>
                {qualityClaims.map((claim) => (
                  <div className="quality-claim-line" key={claim.id}>
                    <span>{claim.description}</span>
                    <StatusPill value={claim.status} />
                    {claim.client_response && <small>{claim.client_response}</small>}
                  </div>
                ))}
              </div>
            )}
            {reviewRequest &&
              reviewRequest.status !== "dismissed" &&
              reviewRequest.status !== "expired" &&
              new Date(reviewRequest.available_at) <= new Date() && (
                <div className="detail-note action google-review-request">
                  <strong>Votre avis sur la prestation</strong>
                  <p>
                    Si vous le souhaitez, vous pouvez partager un avis Google.
                    Cette démarche est facultative et n’a aucun effet sur votre dossier.
                  </p>
                  <ReviewQrCode url={reviewRequest.request_url_snapshot} />
                  <div className="detail-actions">
                    <a
                      className="portal-button"
                      href={reviewRequest.request_url_snapshot}
                      target="_blank"
                      rel="noreferrer"
                      onClick={onReviewOpen}
                    >
                      Donner un avis Google
                    </a>
                    {onReviewDismiss && (
                      <button className="portal-button secondary" type="button" onClick={onReviewDismiss}>
                        Ne plus afficher pour cette mission
                      </button>
                    )}
                  </div>
                  <small>
                    ARD enregistre uniquement l’ouverture de ce lien, jamais le contenu ni la publication de votre avis.
                  </small>
                </div>
              )}
          </>
        )}
        {mode === "worker" && report && (
          <div className="detail-note">
            <strong>Votre compte rendu</strong>
            <p>
              {report.observations || "Sans observation"}
              {report.client_note
                ? ` — Message client : ${report.client_note}`
                : ""}
            </p>
          </div>
        )}
        <div className="detail-actions">
          <MapsLink location={mission.location} />
          {mode === "client" && onQualityClaim &&
            (mission.status === "completed" || mission.workflow_status === "approved") && (
              <button className="portal-button danger" type="button" onClick={onQualityClaim}>
                Signaler un problème / demander une retouche
              </button>
            )}
          <a
            className="portal-button secondary"
            href={googleCalendarMissionUrl(
              mission,
              mode === "client"
                ? mission.client_report
                : mission.notes,
            )}
            target="_blank"
            rel="noreferrer"
          >
            Ajouter à Google Agenda
          </a>
          {mode === "worker" && onCancel && (
            <button className="portal-button danger" disabled={cancellationPending} onClick={onCancel}>
              {cancellationPending ? "Annulation en cours de contrôle" : "Signaler une annulation"}
            </button>
          )}
          <button className="portal-button secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </section>
    </div>
  );
}

function ClientQualityClaimDialog({profile,mission,reload,onClose}:{
  profile:PortalProfile;
  mission:PortalMission;
  reload:()=>void;
  onClose:()=>void;
}){
  const [requestKind,setRequestKind]=useState<PortalMissionQualityClaim["request_kind"]>("complaint_and_touchup"),
    [category,setCategory]=useState<PortalMissionQualityClaim["category"]>("quality"),
    [description,setDescription]=useState(""),
    [files,setFiles]=useState<File[]>([]),
    [busy,setBusy]=useState(false),
    [error,setError]=useState("");
  async function submit(event:React.FormEvent){
    event.preventDefault();
    if(description.trim().length<10){setError("Décrivez la situation en au moins 10 caractères.");return;}
    if(files.length>8){setError("Ajoutez au maximum 8 photos.");return;}
    setBusy(true);setError("");
    const uploaded:string[]=[],attachments:Array<Record<string,unknown>>=[];
    for(const file of files){
      if(!["image/jpeg","image/png","image/webp"].includes(file.type)||file.size<=0||file.size>10_485_760){
        setError(`${file.name} : utilisez une image JPEG, PNG ou WebP de 10 Mo maximum.`);setBusy(false);return;
      }
      const safe=file.name.normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-zA-Z0-9._-]/g,"-"),
        path=`${profile.id}/quality-claims/${mission.id}/${Date.now()}-${safe}`,
        stored=await supabase!.storage.from("quality-claims").upload(path,file,{upsert:false});
      if(stored.error){
        if(uploaded.length)await supabase!.storage.from("quality-claims").remove(uploaded);
        setError(`${file.name} : ${stored.error.message}`);setBusy(false);return;
      }
      uploaded.push(path);
      attachments.push({storage_path:path,original_name:file.name,mime_type:file.type,size_bytes:file.size});
    }
    const result=await supabase!.rpc("submit_mission_quality_claim",{
      p_mission_id:mission.id,p_request_kind:requestKind,p_category:category,
      p_description:description.trim(),p_attachments:attachments,
    });
    if(result.error){
      if(uploaded.length)await supabase!.storage.from("quality-claims").remove(uploaded);
      setError(result.error.message);setBusy(false);return;
    }
    setBusy(false);reload();onClose();
  }
  return <div className="portal-overlay" role="presentation" onMouseDown={(event)=>{if(event.target===event.currentTarget)onClose();}}>
    <form className="portal-detail quality-claim-dialog" role="dialog" aria-modal="true" aria-label="Signaler un problème ou demander une retouche" onSubmit={submit}>
      <div className="portal-detail-head"><div><span className="eyebrow">Suivi qualité</span><h2>Signaler un problème</h2><p>{missionClient(mission)} · {dateTime(mission.starts_at)}</p></div><button type="button" className="icon-close" onClick={onClose}>×</button></div>
      <p className="notice info">Votre signalement sera contrôlé par ARD avant toute décision. Il ne pénalise automatiquement aucun intervenant.</p>
      <div className="form-grid">
        <label>Votre demande<select value={requestKind} onChange={(event)=>setRequestKind(event.target.value as PortalMissionQualityClaim["request_kind"])}><option value="complaint">Signaler un problème</option><option value="touchup">Demander une retouche</option><option value="complaint_and_touchup">Signaler et demander une retouche</option></select></label>
        <label>Ce qui est concerné<select value={category} onChange={(event)=>setCategory(event.target.value as PortalMissionQualityClaim["category"])}><option value="quality">Qualité de la prestation</option><option value="missing_item">Élément oublié</option><option value="damage">Dommage constaté</option><option value="delay">Horaire ou retard</option><option value="behavior">Comportement</option><option value="other">Autre</option></select></label>
        <label className="wide">Décrivez simplement la situation<textarea required minLength={10} maxLength={4000} value={description} onChange={(event)=>setDescription(event.target.value)} placeholder="Ce qui était attendu, ce qui a été constaté et la solution souhaitée…" /></label>
        <label className="wide">Photos utiles (facultatif, 8 maximum)<input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event)=>setFiles(Array.from(event.target.files||[]).slice(0,8))} /></label>
      </div>
      {!!files.length&&<small>{files.length} photo(s) prête(s) à être transmise(s).</small>}
      {error&&<p className="notice error">{error}</p>}
      <div className="detail-actions"><button type="button" className="portal-button secondary" onClick={onClose}>Annuler</button><button className="portal-button" disabled={busy}>{busy?"Transmission…":"Transmettre à ARD"}</button></div>
    </form>
  </div>;
}

function WorkerCancellationDialog({mission,assignment,rule,documents=[],reload,onClose,onSubmitted}:{
  mission:PortalMission;
  assignment:PortalAssignment;
  rule?:PortalWorkerCancellationRule;
  documents?:PortalDocument[];
  reload:()=>void;
  onClose:()=>void;
  onSubmitted:(message:string)=>void;
}){
  const [reason,setReason]=useState(""),[evidenceId,setEvidenceId]=useState(""),[busy,setBusy]=useState(false),[error,setError]=useState("");
  const noticeHours=Math.max(0,(new Date(mission.starts_at).getTime()-Date.now())/3_600_000),
    late=!!rule&&noticeHours<rule.notice_hours,
    assignmentAmount=assignment.agreed_amount??(assignment.assigned_hours*(assignment.agreed_rate||0)),
    proposal=!late||!rule?0:rule.penalty_mode==="fixed"?rule.penalty_value:rule.penalty_mode==="percentage_of_assignment"?assignmentAmount*rule.penalty_value/100:0;
  async function submit(event:React.FormEvent){
    event.preventDefault();setBusy(true);setError("");
    const {error}=await supabase!.rpc("submit_worker_cancellation_notice",{p_assignment_id:assignment.id,p_reason:reason.trim(),p_evidence_document_id:evidenceId||null});
    setBusy(false);
    if(error){setError(error.message);return;}
    reload();onSubmitted("Demande d’annulation transmise à ARD. La mission reste affectée jusqu’au contrôle administratif.");
  }
  return <div className="portal-overlay" role="presentation" onMouseDown={(event)=>{if(event.target===event.currentTarget)onClose();}}>
    <form className="portal-detail cancellation-dialog" role="dialog" aria-modal="true" aria-label="Signaler une annulation de mission" onSubmit={submit}>
      <div className="portal-detail-head"><div><span className="eyebrow">Contrôle administratif obligatoire</span><h2>Signaler une annulation</h2><p>{missionClient(mission)} · {dateTime(mission.starts_at)}</p></div><button type="button" className="icon-close" onClick={onClose}>×</button></div>
      <p className={`notice ${late&&proposal>0?"warning":"info"}`}>{late&&proposal>0?`Aucune pénalité n’est appliquée automatiquement. Le délai contractuel de ${rule!.notice_hours} h n’est pas respecté : le contrat conduit à une proposition de ${money(proposal)} HT, que l’administrateur pourra valider, réduire ou écarter.`:"Aucune pénalité n’est appliquée automatiquement. ARD contrôlera la demande et réattribuera la mission si elle est acceptée."}</p>
      <div className="detail-grid"><div><span>Préavis au moment de la demande</span><strong>{noticeHours.toLocaleString("fr-FR",{maximumFractionDigits:1})} h</strong></div><div><span>Règle contractuelle</span><strong>{rule?.enabled?`${rule.notice_hours} h minimum`:"Aucune règle active"}</strong></div></div>
      {rule?.clause_reference&&<div className="detail-note"><strong>Référence contractuelle</strong><p>{rule.clause_reference}</p></div>}
      <label className="modal-field">Motif détaillé<textarea required minLength={5} value={reason} onChange={(event)=>setReason(event.target.value)} placeholder="Expliquez la situation afin qu’ARD puisse organiser le remplacement." /></label>
      <label className="modal-field">Justificatif déjà déposé (facultatif)<select aria-label="Justificatif déjà déposé (facultatif)" value={evidenceId} onChange={(event)=>setEvidenceId(event.target.value)}><option value="">Aucun justificatif</option>{documents.map((document)=><option key={document.id} value={document.id}>{document.original_name}</option>)}</select></label>
      {error&&<p className="notice error">{error}</p>}
      <div className="detail-actions"><button type="button" className="portal-button secondary" onClick={onClose}>Conserver la mission</button><button className="portal-button danger" disabled={busy}>{busy?"Transmission…":"Transmettre la demande"}</button></div>
    </form>
  </div>;
}

function MissionCompletionDialog({
  profile,
  worker,
  mission,
  assignment,
  inventoryItems,
  team,
  teamMembers,
  workers,
  assignments,
  teamContext,
  activityLearningAvailable,
  missionChecklistAvailable,
  reload,
  onClose,
  onCompleted,
}: {
  profile: PortalProfile;
  worker: PortalWorker;
  mission: PortalMission;
  assignment?: PortalAssignment;
  inventoryItems: PortalInventoryItem[];
  team?: PortalRouteTeam | null;
  teamMembers: PortalRouteTeamMember[];
  workers: PortalWorker[];
  assignments: PortalAssignment[];
  teamContext: PortalRouteTeamContext[];
  activityLearningAvailable: boolean;
  missionChecklistAvailable: boolean;
  reload: () => void;
  onClose: () => void;
  onCompleted: (message: string) => void;
}) {
  const isTeamLeader = !!team && team.leader_worker_id === worker.id,
    activeTeamMembers = team
      ? teamMembers.filter((item) => item.team_id === team.id)
      : [],
    activeTeamContext = team
      ? teamContext.filter(
          (item) =>
            item.team_id === team.id && item.mission_id === mission.id,
        )
      : [],
    missionAssignments = assignments.filter(
      (item) =>
        item.mission_id === mission.id &&
        ["accepted", "assigned"].includes(item.status),
    );
  const [actual, setActual] = useState(
      worker.kind === "employee"
        ? String(assignment?.assigned_hours || mission.planned_hours || "")
        : "",
    ),
    [memberHours, setMemberHours] = useState<Record<string, string>>(() =>
      Object.fromEntries(
        activeTeamMembers.map((member) => [
          member.worker_id,
          String(
            activeTeamContext.find(
              (item) => item.worker_id === member.worker_id,
            )?.assigned_hours ||
              missionAssignments.find(
                (item) => item.worker_id === member.worker_id,
              )?.assigned_hours ||
              "",
          ),
        ]),
      ),
    ),
    [result, setResult] = useState("completed"),
    [observations, setObservations] = useState(""),
    [clientNote, setClientNote] = useState(""),
    [files, setFiles] = useState<File[]>([]),
    [stockUsages, setStockUsages] = useState<Array<{ item_id: string; quantity: number; notes: string }>>([]),
    [activityCatalog, setActivityCatalog] = useState<MissionActivityCatalogItem[]>(missionActivitySuggestions),
    [activityEntries, setActivityEntries] = useState<Array<{ activity_key: string; label: string; hours: number; notes: string }>>([]),
    [missionChecklist, setMissionChecklist] = useState<PortalMissionChecklist | null>(null),
    [checklistLoading, setChecklistLoading] = useState(missionChecklistAvailable),
    [checklistBusyItem, setChecklistBusyItem] = useState(""),
    [proofExceptionItem, setProofExceptionItem] = useState<PortalMissionChecklistItem | null>(null),
    [proofExceptionReason, setProofExceptionReason] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const refreshMissionChecklist = async () => {
    if (!missionChecklistAvailable) return;
    const result = await supabase!.rpc("get_mission_checklist", {
      p_mission_id: mission.id,
    });
    if (result.error) {
      setMessage(result.error.message);
      setMissionChecklist(null);
    } else {
      setMissionChecklist(result.data as PortalMissionChecklist);
    }
    setChecklistLoading(false);
  };
  useEffect(() => {
    let active = true;
    if (!missionChecklistAvailable) {
      setChecklistLoading(false);
      return () => { active = false; };
    }
    supabase!.rpc("get_mission_checklist", { p_mission_id: mission.id })
      .then(({ data: payload, error }) => {
        if (!active) return;
        if (error) setMessage(error.message);
        else setMissionChecklist(payload as PortalMissionChecklist);
        setChecklistLoading(false);
      });
    return () => { active = false; };
  }, [mission.id, missionChecklistAvailable]);
  useEffect(() => {
    let active = true;
    if (!activityLearningAvailable) return () => { active = false; };
    supabase!.rpc("get_my_mission_activity_catalog", { p_mission_id: mission.id })
      .then(({ data: rows, error }) => {
        if (!active || error || !Array.isArray(rows)) return;
        const merged = [...rows, ...missionActivitySuggestions]
          .filter((item): item is MissionActivityCatalogItem =>
            Boolean(item?.activity_key && item?.activity_label),
          )
          .filter((item, index, all) =>
            all.findIndex((candidate) =>
              normalizedActivityLabel(candidate.activity_label) === normalizedActivityLabel(item.activity_label),
            ) === index,
          );
        setActivityCatalog(merged);
      });
    return () => { active = false; };
  }, [activityLearningAvailable, mission.id]);
  const reportedLaborHours = isTeamLeader && activeTeamMembers.length > 1
      ? activeTeamMembers.reduce((sum, member) => sum + Number(memberHours[member.worker_id] || 0), 0)
      : Number(actual || 0),
    activityHours = activityEntries.reduce((sum, item) => sum + Number(item.hours || 0), 0),
    unallocatedHours = Math.max(0, reportedLaborHours - activityHours),
    checklistZoneGroups = Object.values(
      (missionChecklist?.items || []).reduce<Record<string, {
        key: string;
        runId: string;
        checklistName: string;
        zoneKey: string;
        zoneLabel: string;
        zoneInstanceIndex: number;
        repeatable: boolean;
        items: PortalMissionChecklistItem[];
      }>>((groups, item) => {
        const key = `${item.run_id}:${item.zone_key || "general"}:${item.zone_instance_index || 1}`;
        groups[key] ||= {
          key,
          runId: item.run_id,
          checklistName: item.checklist_name || "Checklist",
          zoneKey: item.zone_key || "general",
          zoneLabel: item.zone_display_label || item.zone_label || "Général",
          zoneInstanceIndex: item.zone_instance_index || 1,
          repeatable: Boolean(item.zone_repeatable),
          items: [],
        };
        groups[key].items.push(item);
        return groups;
      }, {}),
    );

  async function toggleChecklistItem(item: PortalMissionChecklistItem) {
    setChecklistBusyItem(item.id);
    setMessage("");
    const result = await supabase!.rpc("set_mission_checklist_item", {
      p_item_result_id: item.id,
      p_completed: !item.is_completed,
      p_notes: item.notes || null,
    });
    if (result.error) setMessage(result.error.message);
    else setMissionChecklist(result.data as PortalMissionChecklist);
    setChecklistBusyItem("");
  }

  async function uploadChecklistEvidence(
    item: PortalMissionChecklistItem,
    file: File,
  ) {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMessage("La preuve obligatoire doit être une photo JPEG, PNG ou WebP.");
      return;
    }
    if (!file.size || file.size > 10 * 1024 * 1024) {
      setMessage("Chaque photo obligatoire doit peser au maximum 10 Mo.");
      return;
    }
    setChecklistBusyItem(item.id);
    setMessage("");
    const safe = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      path = `${profile.organization_id}/${profile.id}/${mission.id}/checklist/${item.id}/${Date.now()}-${safe}`,
      stored = await supabase!.storage
        .from("mission-evidence")
        .upload(path, file, { upsert: false });
    if (stored.error) {
      setChecklistBusyItem("");
      setMessage(stored.error.message);
      return;
    }
    const linked = await supabase!.rpc("register_mission_checklist_evidence", {
      p_item_result_id: item.id,
      p_storage_path: path,
      p_original_name: file.name,
      p_mime_type: file.type,
      p_size_bytes: file.size,
    });
    if (linked.error) {
      await supabase!.storage.from("mission-evidence").remove([path]);
      setMessage(linked.error.message);
    } else {
      await refreshMissionChecklist();
    }
    setChecklistBusyItem("");
  }

  async function removeChecklistEvidence(
    item: PortalMissionChecklistItem,
    evidence: PortalMissionChecklistEvidence,
  ) {
    setChecklistBusyItem(item.id);
    setMessage("");
    const unlinked = await supabase!.rpc("remove_mission_checklist_evidence", {
      p_evidence_id: evidence.id,
    });
    if (unlinked.error) setMessage(unlinked.error.message);
    else {
      const path = String(unlinked.data || evidence.storage_path);
      const removed = await supabase!.storage.from("mission-evidence").remove([path]);
      if (removed.error)
        setMessage("La preuve a été retirée de la checklist, mais le nettoyage du fichier doit être contrôlé par l’administration.");
      await refreshMissionChecklist();
    }
    setChecklistBusyItem("");
  }

  async function markProofImpossible() {
    if (!proofExceptionItem) return;
    if (proofExceptionReason.trim().length < 10) {
      setMessage("Expliquez précisément pourquoi la photo ne peut pas être fournie.");
      return;
    }
    setChecklistBusyItem(proofExceptionItem.id);
    setMessage("");
    const result = await supabase!.rpc("mark_mission_checklist_proof_impossible", {
      p_item_result_id: proofExceptionItem.id,
      p_reason: proofExceptionReason.trim(),
    });
    if (result.error) setMessage(result.error.message);
    else {
      setMissionChecklist(result.data as PortalMissionChecklist);
      setProofExceptionItem(null);
      setProofExceptionReason("");
    }
    setChecklistBusyItem("");
  }

  async function addChecklistZone(runId: string, zoneKey: string) {
    setChecklistBusyItem(`zone:${runId}:${zoneKey}`);
    setMessage("");
    const zone = (missionChecklist?.repeatable_zones || []).find((candidate) => candidate.run_id === runId && candidate.zone_key === zoneKey),
      nextLabel = zone ? `${zone.zone_label} ${Number(zone.instance_count || 1) + 1}` : null;
    const result = await supabase!.rpc("add_mission_checklist_zone_instance", {
      p_mission_id: mission.id,
      p_run_id: runId,
      p_zone_key: zoneKey,
      p_display_label: nextLabel,
    });
    if (result.error) setMessage(result.error.message);
    else setMissionChecklist(result.data as PortalMissionChecklist);
    setChecklistBusyItem("");
  }

  async function removeChecklistZone(runId: string, zoneKey: string, zoneInstanceIndex: number) {
    setChecklistBusyItem(`zone:${runId}:${zoneKey}:${zoneInstanceIndex}`);
    setMessage("");
    const result = await supabase!.rpc("remove_mission_checklist_zone_instance", {
      p_mission_id: mission.id,
      p_run_id: runId,
      p_zone_key: zoneKey,
      p_zone_instance_index: zoneInstanceIndex,
    });
    if (result.error) setMessage(result.error.message);
    else setMissionChecklist(result.data as PortalMissionChecklist);
    setChecklistBusyItem("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (checklistLoading)
      return setMessage("La checklist obligatoire est encore en cours de chargement.");
    if (
      missionChecklist?.available &&
      !["ready", "review_required", "overridden"].includes(missionChecklist.status || "active")
    )
      return setMessage("Validez toutes les étapes obligatoires et ajoutez toutes les photos demandées avant de clôturer la mission.");
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"],
      invalid = files.find(
        (file) => !allowed.includes(file.type) || file.size > 10 * 1024 * 1024,
      );
    if (invalid)
      return setMessage(`${invalid.name} doit être une image ou un PDF de moins de 10 Mo.`);
    if (activityEntries.some((entry) => entry.label.trim().length < 2 || Number(entry.hours) <= 0))
      return setMessage("Chaque étape mesurée doit avoir un nom et une durée positive.");
    if (activityHours > reportedLaborHours + 0.01)
      return setMessage("La somme des étapes ne peut pas dépasser le total des heures réalisées.");
    setBusy(true);
    if (
      isTeamLeader &&
      activeTeamMembers.some(
        (member) => !Number(memberHours[member.worker_id]),
      )
    ) {
      setBusy(false);
      setMessage("Renseignez les heures réellement effectuées par chaque membre.");
      return;
    }
    let reportId: string | null = null,
      saveError: { message: string } | null = null;
    if (isTeamLeader && activeTeamMembers.length > 1) {
      const saved = await supabase!.rpc("submit_route_team_mission_report", {
        p_mission_id: mission.id,
        p_result: result,
        p_member_hours: Object.fromEntries(
          activeTeamMembers.map((member) => [
            member.worker_id,
            Number(memberHours[member.worker_id]),
          ]),
        ),
        p_observations: observations || null,
        p_client_note: clientNote || null,
      });
      reportId = saved.data as string | null;
      saveError = saved.error;
    } else {
      const saved = await supabase!.rpc("submit_individual_mission_report", {
        p_mission_id: mission.id,
        p_result: result,
        p_actual_hours: +actual,
        p_observations: observations || null,
        p_client_note: clientNote || null,
      });
      reportId = saved.data as string | null;
      saveError = saved.error;
      if (
        saveError &&
        /submit_individual_mission_report|schema cache|function/i.test(
          saveError.message,
        )
      ) {
        const legacy = await supabase!
          .from("mission_reports")
          .upsert(
            {
              organization_id: profile.organization_id,
              mission_id: mission.id,
              worker_id: worker.id,
              result,
              actual_hours: +actual,
              observations: observations || null,
              client_note: clientNote || null,
              status: "pending",
            },
            { onConflict: "mission_id,worker_id" },
          )
          .select("id")
          .single();
        reportId = legacy.data?.id || null;
        saveError = legacy.error;
      }
    }
    if (saveError || !reportId) {
      setBusy(false);
      setMessage(
        saveError?.message || "Le compte rendu n’a pas pu être enregistré.",
      );
      return;
    }
    if (activityLearningAvailable) {
      const activityResult = await supabase!.rpc("replace_mission_report_activity_entries", {
        p_report_id: reportId,
        p_items: activityEntries.map((entry) => ({
          activity_key: entry.activity_key || null,
          activity_label: entry.label.trim(),
          minutes: Math.round(Number(entry.hours) * 60),
          notes: entry.notes.trim() || null,
        })),
      });
      if (activityResult.error) {
        setBusy(false);
        setMessage(`Compte rendu enregistré, mais la répartition du temps reste à contrôler : ${activityResult.error.message}`);
        return;
      }
    }
    const stockResult = await supabase!.rpc("replace_mission_stock_usages", {
      p_report_id: reportId,
      p_items: stockUsages,
    });
    if (stockResult.error) {
      setBusy(false);
      setMessage(`Compte rendu enregistré, mais le stock n’a pas été validé : ${stockResult.error.message}`);
      return;
    }
    const uploadErrors: string[] = [];
    for (const file of files) {
      const safe = file.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9._-]/g, "-"),
        path = `${profile.organization_id}/${profile.id}/${reportId}/${Date.now()}-${safe}`,
        stored = await supabase!.storage.from("mission-evidence").upload(path, file, { upsert: false });
      if (stored.error) {
        uploadErrors.push(`${file.name} : ${stored.error.message}`);
        continue;
      }
      const linked = await supabase!.from("mission_report_attachments").insert({
        organization_id: profile.organization_id,
        report_id: reportId,
        worker_id: worker.id,
        storage_path: path,
        original_name: file.name,
        mime_type: file.type,
        size_bytes: file.size,
      });
      if (linked.error) {
        await supabase!.storage.from("mission-evidence").remove([path]);
        uploadErrors.push(`${file.name} : ${linked.error.message}`);
      }
    }
    setBusy(false);
    if (uploadErrors.length) {
      setMessage(`Compte rendu enregistré, mais certaines pièces n’ont pas été jointes : ${uploadErrors.join(" ; ")}`);
      return;
    }
    reload();
    onCompleted(`${isTeamLeader && activeTeamMembers.length > 1 ? "Compte rendu du binôme" : "Mission"} clôturé et transmis à l’administration${files.length ? ` avec ${files.length} pièce(s) jointe(s)` : ""}.`);
    onClose();
  }

  return (
    <div className="portal-overlay" role="presentation">
      <section className="portal-detail mission-completion-dialog" role="dialog" aria-modal="true" aria-label="Clôturer la mission">
        <div className="portal-detail-head">
          <div><span className="eyebrow">Action rapide</span><h2>Clôturer la mission</h2><p>{missionClient(mission)} · {dateTime(mission.starts_at)}</p></div>
          <button className="icon-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <form className="form-grid" onSubmit={submit}>
          {missionChecklistAvailable && (
            <section className="wide mission-checklist-panel" data-testid="mission-checklist-panel">
              <div className="section-heading">
                <div>
                  <strong>{missionChecklist?.name || "Checklists obligatoires"}</strong>
                  <small>
                    {checklistLoading
                      ? "Chargement des contrôles…"
                      : missionChecklist?.available
                        ? `${missionChecklist.run_count || 1} checklist(s) · ${missionChecklist.status === "ready" ? "prêtes à transmettre" : missionChecklist.status === "review_required" ? "transmissibles avec justification à contrôler" : missionChecklist.status === "overridden" ? "dérogation administrateur" : "à compléter"}`
                        : "Aucune checklist particulière pour les prestations de cette mission."}
                  </small>
                </div>
                {missionChecklist?.available && (
                  <span className={`status ${["ready", "overridden"].includes(missionChecklist.status || "") ? "success" : "warning"}`}>
                    {["ready", "overridden"].includes(missionChecklist.status || "") ? "Prête" : missionChecklist.status === "review_required" ? "Justifiée" : "Obligatoire"}
                  </span>
                )}
              </div>
              {missionChecklist?.instructions && <p className="section-intro">{missionChecklist.instructions}</p>}
              {missionChecklist?.status === "overridden" && (
                <p className="notice warning">Dérogation enregistrée par l’administration : {missionChecklist.override_reason}</p>
              )}
              {missionChecklist?.status === "review_required" && (
                <p className="notice warning">La mission peut être transmise, mais l’administration devra contrôler la justification avant validation et facturation.</p>
              )}
              {missionChecklist?.available && (missionChecklist.runs || []).length > 1 && (
                <div className="mission-checklist-run-overview" data-testid="mission-checklist-run-overview">
                  {(missionChecklist.runs || []).map((run) => (
                    <article key={run.run_id}>
                      <div><strong>{run.name}</strong><small>{(run.service_labels || []).join(" · ") || "Affectation administrative"}</small></div>
                      <span className={`status ${["ready", "overridden", "submitted"].includes(run.status) ? "success" : "warning"}`}>
                        {(run.missing || []).length ? `${(run.missing || []).length} à terminer` : "Complète"}
                      </span>
                    </article>
                  ))}
                </div>
              )}
              {missionChecklist?.available && checklistZoneGroups.map((group) => (
                <section className="mission-checklist-zone" key={group.key}>
                  <div className="mission-checklist-zone-heading">
                    <div><strong>{group.checklistName} · {group.zoneLabel}</strong><small>{group.items.length} point(s) à valider</small></div>
                    {missionChecklist.can_edit && group.zoneInstanceIndex > 1 && (
                      <button type="button" className="secondary compact" disabled={checklistBusyItem === `zone:${group.runId}:${group.zoneKey}:${group.zoneInstanceIndex}`} onClick={() => removeChecklistZone(group.runId, group.zoneKey, group.zoneInstanceIndex)}>Retirer cette pièce</button>
                    )}
                  </div>
                  {group.items.map((item) => {
                    const photoCount = item.evidence?.length || 0,
                      exceptionStatus = item.proof_exception?.status,
                      exceptionSatisfies = exceptionStatus === "pending_review" || exceptionStatus === "accepted",
                      photoReady = photoCount >= item.minimum_photo_count || exceptionSatisfies,
                      ready = (!item.is_required || item.is_completed) && photoReady;
                    return (
                      <article className={`mission-checklist-item ${ready ? "complete" : "pending"}`} key={item.id}>
                        <label className="mission-checklist-toggle">
                          <input
                            type="checkbox"
                            checked={item.is_completed}
                            disabled={!missionChecklist.can_edit || checklistBusyItem === item.id}
                            onChange={() => toggleChecklistItem(item)}
                          />
                          <span>
                            <strong>{item.label}</strong>
                            <small>{item.is_required ? "Obligatoire" : "Facultatif"}{item.minimum_photo_count ? ` · ${item.minimum_photo_count} photo(s) obligatoire(s)` : ""}</small>
                          </span>
                        </label>
                        {item.instructions && <p>{item.instructions}</p>}
                        {item.evidence?.length > 0 && (
                          <div className="mission-checklist-evidence-list">
                            {item.evidence.map((evidence) => (
                              <span key={evidence.id}>
                                📷 {evidence.original_name}
                                {missionChecklist.can_edit && (
                                  <button type="button" aria-label={`Retirer ${evidence.original_name}`} disabled={checklistBusyItem === item.id} onClick={() => removeChecklistEvidence(item, evidence)}>×</button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {item.proof_exception && item.proof_exception.status !== "resolved_by_evidence" && (
                          <div className={`checklist-proof-exception ${item.proof_exception.status}`}>
                            <strong>{item.proof_exception.status === "accepted" ? "Justification acceptée" : item.proof_exception.status === "rejected" ? "Justification refusée" : "Justification à contrôler"}</strong>
                            <p>{item.proof_exception.reason}</p>
                            {item.proof_exception.review_note && <small>Retour ARD : {item.proof_exception.review_note}</small>}
                          </div>
                        )}
                        {missionChecklist.can_edit && (
                          <div className="mission-checklist-proof-actions">
                            <label className="mission-checklist-photo">
                              {item.minimum_photo_count > 0 ? `Ajouter une photo (${photoCount}/${item.minimum_photo_count})` : "Ajouter une photo utile"}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                disabled={checklistBusyItem === item.id}
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  if (file) uploadChecklistEvidence(item, file);
                                  event.currentTarget.value = "";
                                }}
                              />
                            </label>
                            {item.minimum_photo_count > photoCount && (
                              <button type="button" className="secondary compact" onClick={() => { setProofExceptionItem(item); setProofExceptionReason(item.proof_exception?.reason || ""); }}>Preuve impossible</button>
                            )}
                          </div>
                        )}
                        {!photoReady && <small className="checklist-missing">Il manque {item.minimum_photo_count - photoCount} photo(s) ou une justification précise.</small>}
                        {proofExceptionItem?.id === item.id && (
                          <div className="checklist-proof-exception-editor">
                            <label>Pourquoi la photo est-elle impossible ?<textarea value={proofExceptionReason} onChange={(event) => setProofExceptionReason(event.target.value)} placeholder="Ex. accès condamné, équipement absent, risque de sécurité constaté…" /></label>
                            <small>La justification sera horodatée, contrôlée par ARD et bloquera la facturation tant qu’elle n’est pas acceptée.</small>
                            <div className="row-actions"><button type="button" className="secondary compact" onClick={() => { setProofExceptionItem(null); setProofExceptionReason(""); }}>Annuler</button><button type="button" className="compact" disabled={checklistBusyItem === item.id || proofExceptionReason.trim().length < 10} onClick={markProofImpossible}>Transmettre la justification</button></div>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </section>
              ))}
              {missionChecklist?.available && missionChecklist.can_edit && (missionChecklist.repeatable_zones || []).length > 0 && (
                <div className="mission-checklist-add-zone">
                  <strong>Une pièce supplémentaire sur place ?</strong>
                  <div className="row-actions">
                    {(missionChecklist.repeatable_zones || []).map((zone) => (
                      <button type="button" className="secondary compact" key={`${zone.run_id}:${zone.zone_key}`} disabled={checklistBusyItem === `zone:${zone.run_id}:${zone.zone_key}`} onClick={() => addChecklistZone(zone.run_id, zone.zone_key)}>＋ {zone.checklist_name} : ajouter {zone.zone_label.toLocaleLowerCase("fr-FR")}</button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
          <label>Résultat<select value={result} onChange={(e) => setResult(e.target.value)}><option value="completed">Terminée</option><option value="issue">Incident rencontré</option><option value="followup">Action complémentaire nécessaire</option></select></label>
          {isTeamLeader && activeTeamMembers.length > 1 ? (
            <div className="wide team-hours-editor">
              <p className="notice info">
                Vous êtes responsable de {team?.name}. Indiquez le temps réellement
                effectué par chaque personne.
                {worker.kind === "employee" && ` L’objectif global de ${mission.planned_hours || 0} h a été réparti dans les affectations sans être multiplié par le nombre de membres.`}
              </p>
              <div className="form-grid compact-grid">
                {activeTeamMembers.map((member) => {
                  const memberWorker = workers.find(
                      (item) => item.id === member.worker_id,
                    ),
                    context = activeTeamContext.find(
                      (item) => item.worker_id === member.worker_id,
                    ),
                    planned =
                      context?.assigned_hours ||
                      missionAssignments.find(
                        (item) => item.worker_id === member.worker_id,
                      )?.assigned_hours,
                    displayName =
                      context?.display_name ||
                      (memberWorker
                        ? `${memberWorker.first_name || ""} ${memberWorker.last_name || ""}`.trim()
                        : "Membre");
                  return (
                    <label key={member.worker_id}>
                      {displayName}
                      <input
                        aria-label={`Heures de ${displayName.split(" ")[0] || "membre"}`}
                        type="number"
                        min="0.25"
                        max="24"
                        step="0.25"
                        value={memberHours[member.worker_id] || ""}
                        onChange={(event) =>
                          setMemberHours((current) => ({
                            ...current,
                            [member.worker_id]: event.target.value,
                          }))
                        }
                        required
                      />
                      {worker.kind === "employee" && <small>{planned || 0} h d’objectif pour cette personne</small>}
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <label>Heures réalisées<input type="number" min="0.25" max="24" step="0.25" value={actual} onChange={(e) => setActual(e.target.value)} required /></label>
          )}
          {activityLearningAvailable && (
            <details className="wide mission-activity-breakdown" data-testid="mission-activity-breakdown">
              <summary>Répartir le temps par étape <small>facultatif</small></summary>
              <p className="section-intro">
                Ajoutez seulement les grandes étapes utiles. Le total peut rester partiel : ARD Manager n’inventera jamais le temps manquant.
              </p>
              <datalist id="mission-activity-suggestions">
                {activityCatalog.map((suggestion) => <option key={suggestion.activity_key} value={suggestion.activity_label} />)}
              </datalist>
              {activityEntries.map((entry, index) => (
                <div className="mission-activity-row" key={`${index}-${entry.label}`}>
                  <label>Étape<input list="mission-activity-suggestions" value={entry.label} onChange={(event) => { const label = event.target.value, known = activityCatalog.find((item) => normalizedActivityLabel(item.activity_label) === normalizedActivityLabel(label)); setActivityEntries((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, label, activity_key: known?.activity_key || "" } : item)); }} placeholder="Ex. Vitres ou évacuation de gravats" required /></label>
                  <label>Temps humain (h)<input type="number" min="0.1" max="48" step="0.1" value={entry.hours} onChange={(event) => setActivityEntries((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, hours: Number(event.target.value) } : item))} required /></label>
                  <label>Précision<input value={entry.notes} onChange={(event) => setActivityEntries((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, notes: event.target.value } : item))} placeholder="Facultatif" /></label>
                  <button type="button" className="danger compact" onClick={() => setActivityEntries((items) => items.filter((_item, itemIndex) => itemIndex !== index))}>Retirer</button>
                </div>
              ))}
              <div className="mission-activity-summary">
                <span>Temps réparti <strong>{activityHours.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h</strong></span>
                <span>Temps non détaillé <strong>{unallocatedHours.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h</strong></span>
              </div>
              <button type="button" className="secondary compact" onClick={() => setActivityEntries((items) => [...items, { activity_key: "", label: "", hours: Math.max(0.1, Number(unallocatedHours.toFixed(2)) || 0.1), notes: "" }])}>＋ Ajouter une étape</button>
            </details>
          )}
          <label className="wide">Observations pour ARD<textarea value={observations} onChange={(e) => setObservations(e.target.value)} required autoFocus /></label>
          <label className="wide">Compte rendu visible par le client<textarea value={clientNote} onChange={(e) => setClientNote(e.target.value)} /></label>
          <div className="wide mission-stock-usage">
            <div className="section-heading"><div><strong>Produits déposés ou consommés</strong><small>Indiquez uniquement l’article ou le kit et la quantité réellement utilisés.</small></div><button type="button" className="secondary compact" onClick={() => setStockUsages((items) => [...items, { item_id: inventoryItems[0]?.id || "", quantity: 1, notes: "" }])} disabled={!inventoryItems.length}>Ajouter un produit</button></div>
            {stockUsages.map((usage, index) => {
              return <div className="mission-stock-row" key={`${index}-${usage.item_id}`}>
                <label>Produit<select value={usage.item_id} onChange={(e) => setStockUsages((items) => items.map((entry, itemIndex) => itemIndex === index ? { ...entry, item_id: e.target.value } : entry))} required><option value="">Choisir</option>{inventoryItems.filter((entry) => entry.active).map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></label>
                <label>Quantité<input type="number" min="0.001" step="0.001" value={usage.quantity} onChange={(e) => setStockUsages((items) => items.map((entry, itemIndex) => itemIndex === index ? { ...entry, quantity: +e.target.value } : entry))} required /></label>
                <label>Précision<input value={usage.notes} onChange={(e) => setStockUsages((items) => items.map((entry, itemIndex) => itemIndex === index ? { ...entry, notes: e.target.value } : entry))} placeholder="Déposé ou utilisé…" /></label>
                <button type="button" className="danger compact" onClick={() => setStockUsages((items) => items.filter((_, itemIndex) => itemIndex !== index))}>Retirer</button>
              </div>;
            })}
            {!stockUsages.length && <p className="empty">Aucun produit utilisé sur cette mission.</p>}
          </div>
          <label className="wide">Photos ou documents utiles (facultatif)<input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} /><small>PDF ou images, 10 Mo maximum par fichier.</small></label>
          {message && <p className="notice error wide">{message}</p>}
          <div className="row-actions wide"><button type="button" className="secondary" onClick={onClose}>Annuler</button><button disabled={busy || checklistLoading || Boolean(missionChecklist?.available && !["ready", "review_required", "overridden"].includes(missionChecklist.status || "active"))}>{busy ? "Transmission…" : "Clôturer et transmettre"}</button></div>
        </form>
      </section>
    </div>
  );
}

function AgendaWeek({
  missions,
  onSelect,
  showAmount = false,
  properties = [],
  qualityClaims,
}: {
  missions: PortalMission[];
  onSelect: (mission: PortalMission) => void;
  showAmount?: boolean;
  properties?: PortalProperty[];
  qualityClaims?: PortalMissionQualityClaim[];
}) {
  const [offset, setOffset] = useState(0);
  const base = useMemo(() => {
    const d = startOfWeek(new Date());
    d.setDate(d.getDate() + offset * 7);
    return d;
  }, [offset]);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    return d;
  });
  return (
    <section className="panel agenda-panel">
      <div className="agenda-head">
        <div>
          <span className="eyebrow">Vue agenda</span>
          <h2>Planning des missions</h2>
        </div>
        <div>
          <button
            className="agenda-today"
            onClick={() =>
              downloadMissionCalendar(
                missions,
                "planning-ard.ics",
                "Missions ARD",
              )
            }
          >
            Exporter .ics
          </button>
          <button
            className="agenda-nav"
            onClick={() => setOffset((v) => v - 1)}
            aria-label="Semaine précédente"
          >
            ←
          </button>
          <button className="agenda-today" onClick={() => setOffset(0)}>
            Aujourd’hui
          </button>
          <button
            className="agenda-nav"
            onClick={() => setOffset((v) => v + 1)}
            aria-label="Semaine suivante"
          >
            →
          </button>
        </div>
      </div>
      <div className="week-grid">
        {days.map((day) => {
          const items = missions.filter((m) => missionDay(m) === dayKey(day));
          return (
            <div
              className={`agenda-day ${items.length ? "has-items" : "empty-day"} ${dayKey(day) === dayKey(new Date()) ? "today" : ""}`}
              key={dayKey(day)}
            >
              <header>
                <strong>{dayTitle(day)}</strong>
                <span>{items.length || ""}</span>
              </header>
              <div>
                {items.map((m) => {
                  const property = properties.find((item) => item.id === m.property_id),
                    agendaStatus = qualityClaims
                      ? missionAgendaStatus(m, qualityClaims)
                      : {
                          label: statusLabel(m.workflow_status || m.status),
                          tone: m.workflow_status || m.status,
                        };
                  return <button
                    className="agenda-event"
                    key={m.id}
                    onClick={() => onSelect(m)}
                  >
                    <time>
                      {new Date(m.starts_at).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </time>
                    <strong>{property?.name || missionClient(m)}</strong>
                    <small>
                      {m.location}
                      {showAmount ? ` · ${money(m.amount_ex_vat || 0)}` : ""}
                    </small>
                    <span className={`agenda-event-status ${agendaStatus.tone}`}>
                      {agendaStatus.label}
                    </span>
                  </button>;
                })}
                {!items.length && <span className="agenda-empty">—</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function WorkerAgendaRich({
  worker,
  data,
  onSelectMission,
  onRequestLeave,
}: {
  worker: PortalWorker | null;
  data: PortalData;
  onSelectMission: (mission: PortalMission) => void;
  onRequestLeave: () => void;
}) {
  const [view, setView] = useState<"week" | "month">("week");
  const [cursor, setCursor] = useState(() => new Date());
  const plannedShifts = useWorkerPlannedShifts(worker?.id);
  const assignments = data.assignments.filter(
      (assignment) =>
        assignment.worker_id === worker?.id &&
        ["accepted", "assigned"].includes(assignment.status),
    ),
    missionIds = new Set(assignments.map((assignment) => assignment.mission_id)),
    missions = data.missions.filter(
      (mission) => missionIds.has(mission.id) && mission.status !== "cancelled",
    ),
    leaves = (data.leaves || []).filter(
      (leave) =>
        leave.worker_id === worker?.id &&
        !["rejected", "cancelled"].includes(leave.status),
    );
  const periodStart = useMemo(() => {
    if (view === "week") return startOfWeek(cursor);
    return startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  }, [cursor, view]);
  const days = useMemo(
    () =>
      Array.from({ length: view === "week" ? 7 : 42 }, (_, index) => {
        const day = new Date(periodStart);
        day.setDate(day.getDate() + index);
        return day;
      }),
    [periodStart, view],
  );
  const periodLabel =
    view === "week"
      ? `${dayTitle(days[0])} – ${dayTitle(days[6])}`
      : new Intl.DateTimeFormat("fr-FR", {
          month: "long",
          year: "numeric",
        }).format(cursor);
  const move = (direction: -1 | 1) => {
    const next = new Date(cursor);
    if (view === "week") next.setDate(next.getDate() + direction * 7);
    else next.setMonth(next.getMonth() + direction, 1);
    setCursor(next);
  };

  return (
    <>
      <div className="page-title employee-agenda-title">
        <div>
          <span className="eyebrow">Organisation personnelle</span>
          <h1>Mon agenda</h1>
          <p>Mes missions et mes congés pour organiser les semaines à venir.</p>
        </div>
        <button type="button" onClick={onRequestLeave}>Demander un congé</button>
      </div>
      <section className="panel agenda-panel employee-agenda-panel">
        <div className="agenda-head">
          <div>
            <span className="eyebrow">{view === "week" ? "Semaine" : "Mois"}</span>
            <h2 className="employee-agenda-period">{periodLabel}</h2>
          </div>
          <div className="employee-agenda-actions">
            <div className="agenda-view-switch" role="group" aria-label="Période de l’agenda">
              <button type="button" className={view === "week" ? "active" : ""} onClick={() => setView("week")}>Semaine</button>
              <button type="button" className={view === "month" ? "active" : ""} onClick={() => setView("month")}>Mois</button>
            </div>
            <button type="button" className="agenda-nav" onClick={() => move(-1)} aria-label="Période précédente">←</button>
            <button type="button" className="agenda-today" onClick={() => setCursor(new Date())}>Aujourd’hui</button>
            <button type="button" className="agenda-nav" onClick={() => move(1)} aria-label="Période suivante">→</button>
          </div>
        </div>
        <div className={`employee-agenda-grid ${view}`}>
          {days.map((day) => {
            const key = dayKey(day),
              dayMissions = missions.filter((mission) => missionDay(mission) === key),
              dayShifts = plannedShifts.rows.filter(
                (shift) => shift.status === "published" && dayKey(new Date(shift.starts_at)) === key,
              ),
              dayLeaves = leaves.filter(
                (leave) => leave.starts_on <= key && leave.ends_on >= key,
              ),
              outsideMonth = view === "month" && day.getMonth() !== cursor.getMonth(),
              empty = !dayMissions.length && !dayShifts.length && !dayLeaves.length;
            return (
              <article
                className={`agenda-day${key === dayKey(new Date()) ? " today" : ""}${outsideMonth ? " outside" : ""}${empty ? " empty-day" : ""}`}
                key={key}
              >
                <header><strong>{dayTitle(day)}</strong><span>{dayMissions.length || dayLeaves.length || ""}</span></header>
                <div>
                  {dayMissions.map((mission) => (
                    <button type="button" className="agenda-event" key={mission.id} onClick={() => onSelectMission(mission)}>
                      <time>{new Date(mission.starts_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</time>
                      <strong>{missionClient(mission, data.properties)}</strong>
                      <small>{mission.location}</small>
                    </button>
                  ))}
                  {dayShifts.map((shift) => (
                    <div className={`agenda-shift ${shift.shift_kind}`} key={shift.id}>
                      <time>{new Date(shift.starts_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</time>
                      <strong>{shift.title}</strong>
                      <small>{shift.shift_kind === "training" ? "Formation" : shift.shift_kind === "meeting" ? "Réunion" : "Planning RH"}{shift.location ? ` · ${shift.location}` : ""}</small>
                    </div>
                  ))}
                  {dayLeaves.map((leave) => (
                    <button type="button" className={`agenda-leave ${leave.status}`} key={`${leave.id}-${key}`} onClick={onRequestLeave}>
                      <strong>{leave.reason || "Congé"}</strong>
                      <small>{statusLabel(leave.status)}</small>
                    </button>
                  ))}
                  {empty && <span className="agenda-empty">—</span>}
                </div>
              </article>
            );
          })}
        </div>
        <p className="employee-agenda-help">Une demande de congé reste soumise à validation. Les missions et créneaux RH publiés constituent votre planning de référence.</p>
      </section>
    </>
  );
}

export function WorkerJourneyRich({
  worker,
  data,
}: {
  worker: PortalWorker | null;
  data: PortalData;
}) {
  const plannedShifts = useWorkerPlannedShifts(worker?.id),
    [message, setMessage] = useState(""),
    [busyShiftId, setBusyShiftId] = useState("");
  const assignments = data.assignments.filter(
      (assignment) => assignment.worker_id === worker?.id && !["cancelled", "refused"].includes(assignment.status),
    ),
    missionIds = new Set(assignments.map((assignment) => assignment.mission_id)),
    completedMissionIds = new Set(
      data.missions
        .filter((mission) => missionIds.has(mission.id) && (mission.status === "completed" || data.reports.some((report) => report.mission_id === mission.id)))
        .map((mission) => mission.id),
    ),
    qualityMetric = (data.workerQualityMetrics || []).find((metric) => metric.worker_id === worker?.id),
    skills = Array.from(new Set((worker?.skills || []).map((skill) => skill.trim()).filter(Boolean))).sort((left, right) => left.localeCompare(right, "fr")),
    mobilizedSkills = Array.from(
      new Set(
        data.missions
          .filter((mission) => missionIds.has(mission.id))
          .flatMap((mission) => mission.required_skills || [])
          .map((skill) => skill.trim())
          .filter(Boolean),
      ),
    ).sort((left, right) => left.localeCompare(right, "fr")),
    developmentSkills = mobilizedSkills.filter(
      (skill) => !skills.some((known) => known.localeCompare(skill, "fr", { sensitivity: "base" }) === 0),
    ),
    currentPeriod = parisCalendarMonth(),
    workedHours = (data.times || [])
      .filter((entry) => entry.worker_id === worker?.id && entry.work_date.startsWith(currentPeriod) && entry.status !== "rejected")
      .reduce((sum, entry) => sum + Number(entry.worked_minutes || 0) / 60, 0),
    targetHours = Number(worker?.monthly_hours || 0),
    progress = targetHours > 0 ? Math.min(100, (workedHours / targetHours) * 100) : 0,
    developmentShifts = plannedShifts.rows
      .filter((shift) => ["training", "meeting"].includes(shift.shift_kind) && ["published", "completed"].includes(shift.status))
      .sort((left, right) => right.starts_at.localeCompare(left.starts_at)),
    contracts = data.contracts
      .filter((contract) => contract.worker_id === worker?.id)
      .sort((left, right) => (right.generated_at || right.starts_on || "").localeCompare(left.generated_at || left.starts_on || "")),
    currentContract = contracts.find((contract) => !["expired", "terminated"].includes(contract.status)) || contracts[0];

  async function acknowledge(shift: PortalWorkerPlannedShift) {
    setBusyShiftId(shift.id);
    const result = await supabase!.rpc("acknowledge_worker_planned_shift", { p_shift_id: shift.id });
    setBusyShiftId("");
    setMessage(result.error ? result.error.message : "Prise de connaissance enregistrée.");
    if (!result.error) await plannedShifts.reload();
  }

  return (
    <>
      <div className="page-title worker-journey-title">
        <div>
          <span className="eyebrow">Évolution professionnelle</span>
          <h1>Mon parcours</h1>
          <p>Mes compétences, mes formations et les repères qualité réellement validés.</p>
        </div>
      </div>
      <div className="stats portal-stats worker-journey-stats">
        <article><span>Compétences reconnues</span><strong>{skills.length}</strong><small>Renseignées dans ma fiche RH</small></article>
        <article><span>Missions réalisées</span><strong>{qualityMetric?.completed_mission_count ?? completedMissionIds.size}</strong><small>Année en cours</small></article>
        <article><span>Objectif du mois</span><strong>{workedHours.toFixed(1)} / {targetHours.toFixed(1)} h</strong><small>{progress.toFixed(0)} % du volume contractuel</small></article>
        <article><span>Retours fondés contrôlés</span><strong>{qualityMetric?.founded_claim_count ?? 0}</strong><small>Jamais calculé depuis une déclaration brute</small></article>
      </div>
      <div className="worker-journey-grid">
        <section className="panel worker-contract-card" data-testid="worker-journey-contract">
          <div className="section-heading"><div><span className="eyebrow">Relation avec ARD</span><h2>Mon contrat</h2></div>{currentContract && <StatusPill value={currentContract.status} />}</div>
          {currentContract ? <>
            <div className="detail-grid compact-grid">
              <div><span>Document</span><strong>{currentContract.title}</strong></div>
              <div><span>Début</span><strong>{currentContract.starts_on ? new Date(`${currentContract.starts_on}T12:00:00`).toLocaleDateString("fr-FR") : "À préciser"}</strong></div>
              <div><span>Fin</span><strong>{currentContract.ends_on ? new Date(`${currentContract.ends_on}T12:00:00`).toLocaleDateString("fr-FR") : "Sans date de fin renseignée"}</strong></div>
              <div><span>Versions conservées</span><strong>{contracts.length}</strong></div>
            </div>
            {currentContract.rendered_content && <details className="contract-content-preview"><summary>Consulter le contenu du contrat</summary><pre>{currentContract.rendered_content}</pre></details>}
            <p className="section-intro">Le document signé et ses anciennes versions restent également disponibles dans « Mes documents ».</p>
          </> : <p className="empty">Aucun contrat n’est encore publié dans votre espace. Votre responsable RH peut le préparer depuis votre fiche.</p>}
        </section>
        <WorkerScheduleSummary workerId={worker?.id} />
        <section className="panel worker-skill-card" data-testid="worker-journey-skills">
          <div className="section-heading"><div><span className="eyebrow">Savoir-faire</span><h2>Mes compétences reconnues</h2></div></div>
          {skills.length ? <div className="worker-skill-list">{skills.map((skill) => <span key={skill}>{skill}</span>)}</div> : <p className="empty">Aucune compétence n’a encore été confirmée dans votre fiche RH.</p>}
          {!!developmentSkills.length && <div className="worker-development-skills"><strong>Compétences mobilisées à confirmer</strong><p>{developmentSkills.join(" · ")}</p><small>Une mission ne valide jamais automatiquement une compétence. Votre responsable doit la confirmer.</small></div>}
        </section>
        <section className="panel worker-development-card" data-testid="worker-journey-development">
          <div className="section-heading"><div><span className="eyebrow">Accompagnement</span><h2>Formations et rendez-vous</h2></div></div>
          {plannedShifts.loading ? <p>Chargement…</p> : !plannedShifts.available ? <p className="empty">En cours de développement sur cet environnement.</p> : developmentShifts.length ? <div className="worker-development-list">{developmentShifts.map((shift) => <article key={shift.id}><time>{new Date(shift.starts_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</time><div><strong>{shift.title}</strong><small>{shift.shift_kind === "training" ? "Formation" : "Entretien / réunion"}{shift.location ? ` · ${shift.location}` : ""}</small>{shift.notes && <p>{shift.notes}</p>}</div>{shift.acknowledged_at ? <span className="status-badge success">Vu</span> : shift.status === "published" ? <button type="button" className="secondary compact" disabled={busyShiftId === shift.id} onClick={() => void acknowledge(shift)}>{busyShiftId === shift.id ? "Enregistrement…" : "J’ai pris connaissance"}</button> : <span className="status-badge">Terminé</span>}</article>)}</div> : <p className="empty">Aucune formation ni entretien publié pour le moment.</p>}
        </section>
        <section className="panel worker-quality-card" data-testid="worker-journey-quality">
          <div className="section-heading"><div><span className="eyebrow">Qualité vérifiée</span><h2>Mes repères terrain</h2></div></div>
          {qualityMetric ? <><div className="detail-grid compact-grid"><div><span>Missions terminées</span><strong>{qualityMetric.completed_mission_count}</strong></div><div><span>Retours contrôlés</span><strong>{qualityMetric.reviewed_claim_count}</strong></div><div><span>Retouches suivies</span><strong>{qualityMetric.touchup_count}</strong></div><div><span>Taux de retour vérifié</span><strong>{Number(qualityMetric.verified_return_rate).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %</strong></div></div><small>Seuls les retours examinés par l’administration apparaissent ici. Ils ne déclenchent automatiquement ni sanction, ni prime, ni changement de rémunération.</small></> : <p className="empty">Aucun indicateur qualité validé n’est encore disponible.</p>}
        </section>
        <WorkerDevelopmentJourney />
      </div>
      {message && <p className={`notice ${message.startsWith("Prise") ? "success" : "error"}`}>{message}</p>}
    </>
  );
}

function MissionRows({
  missions,
  onSelect,
  mode,
  properties = [],
  serviceLines = [],
}: {
  missions: PortalMission[];
  onSelect: (mission: PortalMission) => void;
  mode: "client" | "worker";
  properties?: PortalProperty[];
  serviceLines?: PortalMissionServiceLine[];
}) {
  if (!missions.length)
    return <p className="empty">Aucune mission dans cette rubrique.</p>;
  return (
    <div className="rich-mission-list">
      {missions.map((m) => {
        const services = missionServiceSummary(m, serviceLines);
        return (
          <button key={m.id} onClick={() => onSelect(m)}>
            <time>{dateTime(m.starts_at)}</time>
            <span>
              <strong>{missionClient(m, properties)}</strong>
              <small className="mission-list-services">
                {services || "Contenu de la mission à confirmer"}
              </small>
              <small>{m.location}</small>
            </span>
            {mode === "client" && <b>{money(m.amount_ex_vat || 0)}</b>}
            <StatusPill value={m.workflow_status || m.status} />
            <i>›</i>
          </button>
        );
      })}
    </div>
  );
}

function RouteToday({
  missions,
  headquarters,
  onSelect,
}: {
  missions: PortalMission[];
  headquarters?: string;
  onSelect: (mission: PortalMission) => void;
}) {
  const today = dayKey(new Date()),
    items = missions
      .filter((m) => missionDay(m) === today && m.status !== "cancelled")
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const routeUrl = items.length && headquarters
    ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(headquarters)}&destination=${encodeURIComponent(items.at(-1)!.location)}${
        items.length > 2
          ? `&waypoints=${encodeURIComponent(
              items
                .slice(1, -1)
                .map((m) => m.location)
                .join("|"),
            )}`
          : ""
      }`
    : "";
  return (
    <section className="panel route-panel">
      <div className="route-head">
        <div>
          <span className="eyebrow">Organisation terrain</span>
          <h2>Ma tournée du jour</h2>
        </div>
        {routeUrl && (
          <a
            className="portal-button"
            href={routeUrl}
            target="_blank"
            rel="noreferrer"
          >
            🧭 Ouvrir l’itinéraire
          </a>
        )}
      </div>
      {items.length ? (
        <div className="route-list">
          <div className="route-headquarters">
            <span className="route-index">S</span>
            <span>
              <strong>Départ du siège social</strong>
              <small>{headquarters || "Adresse du siège à renseigner"}</small>
            </span>
          </div>
          {items.map((m, index) => (
            <button key={m.id} onClick={() => onSelect(m)}>
              <span className="route-index">{index + 1}</span>
              <time>
                {new Date(m.starts_at).toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </time>
              <span>
                <strong>{missionClient(m)}</strong>
                <small>{m.location}</small>
              </span>
              <i>›</i>
            </button>
          ))}
        </div>
      ) : (
        <p className="empty">Aucune mission aujourd’hui.</p>
      )}
      {items.length > 0 && !headquarters && (
        <p className="notice error">
          L’itinéraire reste verrouillé tant que l’adresse du siège social
          n’est pas renseignée.
        </p>
      )}
    </section>
  );
}

const clientEmergencyCommitments: Record<string,string> = {
  low:"Sous 48 h",moderate:"Sous 24 h",high:"Sous 12 h",critical:"Le plus vite possible",
};
const clientEmergencyBillingLabels: Record<string,string> = {
  eligible_for_review:"Facturation d’urgence possible · décision ARD à venir",
  awaiting_acknowledgement:"Facturation critique · activation à la prise en charge",
  draft_requested:"Préparation de facturation d’urgence ouverte",
  waived:"Intervention non facturée en urgence",
  invoiced:"Intervention d’urgence facturée",
};

function ClientEmergencyPanel({ profile, data }: { profile: PortalProfile; data: PortalData }) {
  const client = data.clients[0], identityId = client?.profile_id || profile.id,
    [available, setAvailable] = useState(true), [rows, setRows] = useState<any[]>([]),
    [sharedReports,setSharedReports]=useState<any[]>([]),
    [message, setMessage] = useState(""), [busy, setBusy] = useState(false),
    [commercial,setCommercial]=useState<any|null>(null),
    [report, setReport] = useState<{ propertyId:string;missionId: string; title: string; description: string; severity: string; acceptedCriticalBilling:boolean;siteAddress:string;siteContactName:string;siteContactPhone:string;accessInstructions:string;expectedAction:string;equipmentNotes:string } | null>(null);
  const load = async () => {
    const [result,sharedReportResult,commercialResult] = await Promise.all([
      supabase!.from("operational_emergencies")
        .select("id,client_id,mission_id,title,description,severity,status,reported_by,reported_origin,occurred_at,acknowledgement_due_at,intervention_commitment,intervention_target_at,critical_billing_accepted_at,emergency_billing_status,emergency_billing_triggered_at,root_cause,resolution_summary,resolved_at")
        .order("occurred_at", { ascending: false }),
      supabase!.rpc("get_my_shared_operational_emergency_reports"),
      supabase!.rpc("get_my_emergency_commercial_context"),
    ]);
    if (result.error && /schema cache|does not exist|could not find|PGRST205|42P01/i.test(result.error.message)) { setAvailable(false); return; }
    if (result.error) { setMessage(result.error.message); return; }
    const reports=sharedReportResult.error?[]:(sharedReportResult.data||[]),
      visibleEmergencyIds=new Set(reports.map((item:any)=>item.emergency_id)),
      visibleRows=(result.data||[]).filter((item:any)=>item.reported_by===identityId||visibleEmergencyIds.has(item.id));
    setRows(visibleRows);
    setSharedReports(reports.map((report:any)=>({
      ...report,
      emergency:{
        id:report.emergency_id,title:report.title,severity:report.severity,
        occurred_at:report.occurred_at,root_cause:report.root_cause,
        resolution_summary:report.resolution_summary,
      },
      tasks:Array.isArray(report.tasks)?report.tasks:[],
    })));
    if(!commercialResult.error)setCommercial(Array.isArray(commercialResult.data)?commercialResult.data[0]||null:commercialResult.data||null);
  };
  useEffect(() => { void load(); }, [identityId]);
  if (!available) return null;
  const openRows = rows.filter((item) => !["resolved", "closed"].includes(item.status));
  async function submit(event: React.FormEvent) {
    event.preventDefault(); if (!report) return; setBusy(true);
    if(report.severity==="critical"&&!report.acceptedCriticalBilling){setBusy(false);setMessage("Lisez et acceptez les conditions de facturation avant d’envoyer une urgence critique.");return;}
    const result = await supabase!.rpc("report_client_operational_emergency_v3", {
      p_property_id:report.propertyId||null,p_mission_id:report.missionId||null,p_title:report.title,p_description:report.description,
      p_severity:report.severity,p_emergency_kind:"client",p_occurred_at:new Date().toISOString(),
      p_recurrence_key:null,p_accept_critical_billing:report.acceptedCriticalBilling,
      p_site_address:report.siteAddress,p_site_contact_name:report.siteContactName,
      p_site_contact_phone:report.siteContactPhone,p_access_instructions:report.accessInstructions,
      p_expected_action:report.expectedAction,p_equipment_notes:report.equipmentNotes,
    });
    setBusy(false); setMessage(result.error ? result.error.message : "Urgence enregistrée : l’astreinte N1 est alertée avec relances jusqu’à prise en charge ou escalade N2.");
    if (!result.error) {
      const created = { ...(result.data || {}), reported_by: identityId, reported_origin: "client_relay" };
      setRows((current) => [created, ...current.filter((item) => item.id !== created.id)]);
      setReport(null);
    }
  }
  const clientProperties=data.properties.filter((property)=>property.client_id===client?.id&&property.active),
    clientMissions=data.missions.filter((mission)=>mission.client_id===client?.id),
    openReport=()=>{const mission=clientMissions[0],property=clientProperties.find((item)=>item.id===mission?.property_id)||clientProperties[0];setReport({propertyId:property?.id||"",missionId:mission?.id||"",title:"",description:"",severity:"moderate",acceptedCriticalBilling:false,siteAddress:property?.address||mission?.location||client?.address||"",siteContactName:property?.contact_name||"",siteContactPhone:"",accessInstructions:property?.access_notes||"",expectedAction:"",equipmentNotes:""});};
  return <section className="panel client-emergency-panel" data-testid="client-emergency-panel">
    <div className="section-heading"><div><span className="eyebrow">Assistance prioritaire</span><h2>Signaler une urgence</h2><p className="section-intro">Vous pouvez relayer immédiatement l’appel d’un locataire ou un incident concernant une intervention.</p></div><button type="button" disabled={commercial?.enabled===false||commercial?.amendment_required} onClick={openReport}>Signaler une urgence</button></div>
    <p className="client-emergency-explainer">La déclaration crée immédiatement le dossier et des alertes prioritaires vers l’astreinte. Les délais indicatifs dépendent du niveau choisi. Toute demande peut donner lieu à une facturation d’intervention ou de déplacement d’urgence ; seule une urgence critique acceptée l’ouvre automatiquement dès sa prise en charge.</p>
    {openRows.length > 0 && <div className="client-emergency-list">{openRows.map((row) => <article key={row.id}><div><strong>{row.title}</strong><small>{row.reported_origin === "client_relay" ? "Appel relayé par le client" : "Signalement client"} · {new Date(row.occurred_at).toLocaleString("fr-FR")}</small><small>Délai annoncé : {row.intervention_commitment||clientEmergencyCommitments[row.severity]}</small>{clientEmergencyBillingLabels[row.emergency_billing_status]&&<small className="emergency-billing-state">{clientEmergencyBillingLabels[row.emergency_billing_status]}</small>}</div><span>{row.status === "reported" ? "Reçu" : row.status === "acknowledged_n1" ? "Pris en charge" : row.status === "escalated_n2" ? "Escaladé au responsable" : row.status}</span></article>)}</div>}
    {sharedReports.length>0&&<div className="client-emergency-reports" data-testid="client-emergency-reports"><div className="section-heading compact"><div><span className="eyebrow">Transmis après contrôle ARD</span><h3>Comptes rendus d’urgence partagés</h3></div></div>{sharedReports.map((report:any)=><article key={report.id||report.intervention_id}><div className="client-emergency-report-head"><div><strong>{report.emergency.title}</strong><small>{report.completed_at?new Date(report.completed_at).toLocaleString("fr-FR"):"Intervention terminée"} · {report.actual_minutes||0} min</small></div><span>Validé</span></div><p><strong>Actions réalisées :</strong> {report.action_summary||report.emergency.resolution_summary||"Compte rendu validé par ARD."}</p><p><strong>Cause constatée :</strong> {report.emergency.root_cause||"Non déterminée"}</p><p><strong>Reste à faire :</strong> {report.remaining_actions||"Aucune action restante signalée."}</p>{report.tasks.length>0&&<ol>{report.tasks.map((task:any)=><li key={task.id}><strong>{task.label}</strong>{task.notes&&<span>{task.notes}</span>}</li>)}</ol>}<small className="client-report-privacy">Les positions, coordonnées privées de l’astreinte et données de rémunération restent internes à ARD.</small></article>)}</div>}
    {message && <p className={`notice ${message.includes("enregistrée") ? "success" : "error"}`}>{message}</p>}
    {report && <div className="portal-overlay"><form className="portal-detail" role="dialog" aria-modal="true" aria-label="Signaler une urgence client" onSubmit={submit}><div className="portal-detail-head"><div><span className="eyebrow">Appel d’un locataire ou incident</span><h2>Transmettre l’urgence à ARD</h2></div><button type="button" className="icon-close" onClick={() => setReport(null)}>×</button></div><div className="form-grid"><label className="wide">Bien concerné<select value={report.propertyId} onChange={(event)=>{const property=clientProperties.find((item)=>item.id===event.target.value),mission=clientMissions.find((item)=>item.id===report.missionId);setReport({...report,propertyId:event.target.value,missionId:mission?.property_id&&mission.property_id!==event.target.value?"":report.missionId,siteAddress:property?.address||client?.address||report.siteAddress,siteContactName:property?.contact_name||report.siteContactName,accessInstructions:property?.access_notes||report.accessInstructions});}}><option value="">Autre adresse / aucun bien enregistré</option>{clientProperties.map((property)=><option key={property.id} value={property.id}>{property.name} · {property.address}</option>)}</select></label><label className="wide">Mission concernée<select value={report.missionId} onChange={(event)=>{const mission=clientMissions.find((item)=>item.id===event.target.value),property=clientProperties.find((item)=>item.id===mission?.property_id);setReport({...report,missionId:event.target.value,propertyId:property?.id||report.propertyId,siteAddress:property?.address||mission?.location||report.siteAddress,siteContactName:property?.contact_name||report.siteContactName,accessInstructions:property?.access_notes||report.accessInstructions});}}><option value="">Aucune mission précise</option>{clientMissions.filter((mission)=>!report.propertyId||!mission.property_id||mission.property_id===report.propertyId).map((mission)=><option key={mission.id} value={mission.id}>{mission.client_display_name||mission.location} · {new Date(mission.starts_at).toLocaleDateString("fr-FR")}</option>)}</select></label><label className="wide">Sujet de l’urgence<input required minLength={3} value={report.title} onChange={(event) => setReport({ ...report, title: event.target.value })} placeholder="Ex. fuite signalée par un locataire" /></label><label>Gravité estimée<select value={report.severity} onChange={(event) => setReport({ ...report, severity: event.target.value,acceptedCriticalBilling:false })}><option value="low">Faible · sous 48 h</option><option value="moderate">Modérée · sous 24 h</option><option value="high">Élevée · sous 12 h</option><option value="critical">Critique · le plus vite possible</option></select></label><label className="wide">Adresse précise de l’intervention<input required minLength={3} value={report.siteAddress} onChange={(event)=>setReport({...report,siteAddress:event.target.value})}/></label><label>Contact sur place<input required minLength={2} value={report.siteContactName} onChange={(event)=>setReport({...report,siteContactName:event.target.value})}/></label><label>Téléphone du contact<input required type="tel" minLength={6} value={report.siteContactPhone} onChange={(event)=>setReport({...report,siteContactPhone:event.target.value})}/></label><label className="wide">Ce qui se passe<textarea required minLength={5} rows={4} value={report.description} onChange={(event) => setReport({ ...report, description: event.target.value })} placeholder="Décrivez les faits observés ou transmis." /></label><label className="wide">Ce qui est attendu d’ARD<textarea required minLength={3} rows={3} value={report.expectedAction} onChange={(event)=>setReport({...report,expectedAction:event.target.value})} placeholder="Ex. couper la fuite, sécuriser la zone et faire un compte rendu."/></label><label className="wide">Accès au site<textarea rows={2} value={report.accessInstructions} onChange={(event)=>setReport({...report,accessInstructions:event.target.value})} placeholder="Code, clé, étage, personne à prévenir…"/></label><label className="wide">Matériel probablement nécessaire<textarea rows={2} value={report.equipmentNotes} onChange={(event)=>setReport({...report,equipmentNotes:event.target.value})} placeholder="Si vous le savez ; ARD confirmera avant le départ."/></label></div><div className="emergency-commercial-notice"><strong>Délai indicatif : {clientEmergencyCommitments[report.severity]}</strong><p>Tous les niveaux peuvent entraîner des frais d’intervention ou de déplacement d’urgence selon les conditions applicables à votre contrat.{commercial?.rates&&(()=>{const rate=commercial.rates.find((item:any)=>item.severity===report.severity);return rate?` Tarif contractuel actuel : ${Number(rate.hourly_rate_ex_vat).toLocaleString("fr-FR",{style:"currency",currency:commercial.currency||"EUR"})} HT par heure entamée.`:"";})()}</p></div>{report.severity==="critical"&&<label className="critical-billing-consent"><input required type="checkbox" checked={report.acceptedCriticalBilling} onChange={(event)=>setReport({...report,acceptedCriticalBilling:event.target.checked})}/><span><strong>J’ai lu et j’accepte le déclenchement de la facturation d’urgence.</strong> Dès que l’astreinte accuse réception de cette urgence critique, ARD ouvre automatiquement la préparation de la facturation. Le tarif reste celui de mes conditions contractuelles ; aucune somme n’est inventée par l’application.</span></label>}<p className="notice">L’envoi déclenche des alertes prioritaires répétées vers l’astreinte. Le futur appel vocal automatique ne sera indiqué actif qu’après connexion réelle d’un fournisseur téléphonique.</p><div className="detail-actions"><button type="button" className="secondary" onClick={() => setReport(null)}>Annuler</button><button disabled={busy||(report.severity==="critical"&&!report.acceptedCriticalBilling)}>{busy ? "Transmission…" : "Déclarer et alerter l’astreinte"}</button></div></form></div>}
  </section>;
}

type PublishedOperatingProcedure = {
  id:string; procedure_key:string; title:string; version:number; summary:string; steps:string[];
  content_format:"one_page"|"pdf"|"image"|"video"; resource_url:string|null;
  requires_acknowledgement:boolean; published_at:string|null;
};
type OperatingProcedureAcknowledgement = { id:string; procedure_id:string; profile_id:string; acknowledged_at:string };

function MyOperatingProcedures({ profile }:{ profile:PortalProfile }){
  const [procedures,setProcedures]=useState<PublishedOperatingProcedure[]>([]),[acknowledgements,setAcknowledgements]=useState<OperatingProcedureAcknowledgement[]>([]),[selected,setSelected]=useState<PublishedOperatingProcedure|null>(null),[available,setAvailable]=useState(true),[loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[message,setMessage]=useState("");
  async function load(){
    const [procedureRows,ackRows]=await Promise.all([
      supabase!.from("operating_procedure_versions").select("id,procedure_key,title,version,summary,steps,content_format,resource_url,requires_acknowledgement,published_at").eq("status","published").order("title"),
      supabase!.from("operating_procedure_acknowledgements").select("id,procedure_id,profile_id,acknowledged_at").eq("profile_id",profile.id),
    ]);
    setLoading(false);
    if(procedureRows.error||ackRows.error){const error=procedureRows.error||ackRows.error;if(error&&(/does not exist|schema cache|PGRST205|42P01/i.test(error.message||""))){setAvailable(false);return;}setMessage(error?.message||"Chargement impossible.");return;}
    setProcedures((procedureRows.data||[]) as PublishedOperatingProcedure[]);setAcknowledgements((ackRows.data||[]) as OperatingProcedureAcknowledgement[]);
  }
  useEffect(()=>{void load();},[profile.id]);
  async function acknowledge(){if(!selected)return;setBusy(true);const {error}=await supabase!.rpc("acknowledge_operating_procedure",{p_procedure_id:selected.id});setBusy(false);setMessage(error?error.message:"Accusé de lecture horodaté. Cela confirme la lecture, pas l’application terrain.");if(!error)await load();}
  if(!available||loading||(!procedures.length&&!message))return null;
  return <section className="panel my-operating-procedures" data-testid="my-operating-procedures"><div className="section-heading"><div><span className="eyebrow">Consignes publiées</span><h2>Mes procédures</h2><p className="section-intro">Les instructions qui concernent votre profil, dans leur version publiée.</p></div><strong>{acknowledgements.length}/{procedures.filter((item)=>item.requires_acknowledgement).length}</strong></div>{message&&<p className={`notice ${message.startsWith("Accusé")?"success":"error"}`}>{message}</p>}<div className="my-procedure-list">{procedures.map((item)=>{const ack=acknowledgements.find((row)=>row.procedure_id===item.id);return <button type="button" key={item.id} onClick={()=>{setSelected(item);setMessage("");}}><span><strong>{item.title}</strong><small>Version {item.version} · {item.summary}</small></span><em className={ack?"read":"pending"}>{ack?`Lu le ${new Date(ack.acknowledged_at).toLocaleDateString("fr-FR")}`:item.requires_acknowledgement?"Lecture à confirmer":"Consultable"}</em></button>;})}</div>{selected&&<div className="portal-overlay"><section className="portal-detail procedure-reader" role="dialog" aria-modal="true" aria-label="Consulter la procédure"><div className="portal-detail-head"><div><span className="eyebrow">Procédure publiée · version {selected.version}</span><h2>{selected.title}</h2></div><button type="button" className="icon-close" onClick={()=>setSelected(null)}>×</button></div><p>{selected.summary}</p><ol>{selected.steps.map((step,index)=><li key={index}>{step}</li>)}</ol>{selected.resource_url&&<a className="button-link" href={selected.resource_url} target="_blank" rel="noreferrer">Ouvrir le support {selected.content_format}</a>}<p className="procedure-proof-limit">L’accusé confirme uniquement que vous avez pris connaissance de cette version. L’application correcte reste prouvée par les checklists, photos, vidéos et comptes rendus liés à l’opération.</p><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setSelected(null)}>Fermer</button>{selected.requires_acknowledgement&&!acknowledgements.some((row)=>row.procedure_id===selected.id)&&<button type="button" disabled={busy} onClick={()=>void acknowledge()}>{busy?"Enregistrement…":"J’ai lu et compris cette procédure"}</button>}{acknowledgements.some((row)=>row.procedure_id===selected.id)&&<span className="procedure-ack-state">Lecture confirmée et horodatée</span>}</div></section></div>}</section>;
}

export function ClientDashboardRich({ profile, data }: { profile: PortalProfile; data: PortalData }) {
  const [selected, setSelected] = useState<PortalMission | null>(null),
    now = Date.now(),
    calendarMissions = data.missions.filter((mission) => mission.status !== "cancelled"),
    upcoming = data.missions.filter(
      (m) => new Date(m.ends_at).getTime() >= now && m.status !== "cancelled",
    ),
    past = data.missions.filter(
      (m) => new Date(m.ends_at).getTime() < now || m.status === "completed",
    ),
    due = data.invoices.filter((i) => ["sent", "overdue"].includes(i.status)),
    paid = data.invoices.filter((i) => i.status === "paid"),
    toInvoice = data.missions.filter((m) =>
      ["to_invoice", "queued"].includes(m.billing_status || ""),
    ),
    nextMission = upcoming[0],
    nextProperty = data.properties.find(
      (property) => property.id === nextMission?.property_id,
    ),
    nextServices = nextMission
      ? missionServiceSummary(nextMission, data.missionServiceLines || [])
      : "";
  return (
    <>
      <div className="portal-hero">
        <div>
          <span className="eyebrow">Espace client</span>
          <h1>Votre activité avec ARD</h1>
          <p>Missions, comptes rendus et facturation au même endroit.</p>
        </div>
        {nextMission && (
          <button onClick={() => setSelected(nextMission)}>
            <span>Prochaine intervention</span>
            <strong>{dateTime(nextMission.starts_at)}</strong>
            <small>
              {nextProperty?.name || missionClient(nextMission)}
              {nextServices ? ` · ${nextServices}` : ""}
            </small>
            <small>{nextMission.location}</small>
          </button>
        )}
      </div>
      <div className="stats portal-stats">
        <article>
          <span>Missions à venir</span>
          <strong>{upcoming.length}</strong>
        </article>
        <article>
          <span>Prestations à facturer</span>
          <strong>
            {money(toInvoice.reduce((s, m) => s + (m.amount_ex_vat || 0), 0))}
          </strong>
          <small>HT</small>
        </article>
        <article>
          <span>Factures à payer</span>
          <strong>{money(due.reduce((s, i) => s + i.total_inc_vat, 0))}</strong>
          <small>{due.length} document(s)</small>
        </article>
        <article>
          <span>Déjà réglé</span>
          <strong>
            {money(paid.reduce((s, i) => s + i.total_inc_vat, 0))}
          </strong>
        </article>
      </div>
      <MyOperatingProcedures profile={profile} />
      <ClientEmergencyPanel profile={profile} data={data} />
      <AgendaWeek missions={calendarMissions} onSelect={setSelected} showAmount properties={data.properties} qualityClaims={data.missionQualityClaims || []} />
      <section className="panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Historique</span>
            <h2>Dernières missions terminées</h2>
          </div>
        </div>
        <MissionRows
          missions={past.slice(-6).reverse()}
          onSelect={setSelected}
          mode="client"
          properties={data.properties}
          serviceLines={data.missionServiceLines || []}
        />
      </section>
      {selected && (
        <MissionDetail
          mission={selected}
          mode="client"
          property={data.properties.find((item) => item.id === selected.property_id)}
          serviceLines={(data.missionServiceLines || []).filter(
            (item) => item.mission_id === selected.id,
          )}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

export function ClientMissionsRich({
  profile,
  data,
  reload,
}: {
  profile: PortalProfile;
  data: PortalData;
  reload: () => void;
}) {
  const client = data.clients[0],
    properties = data.properties.filter(
      (p) => p.client_id === client?.id && p.active,
    ),
    [selected, setSelected] = useState<PortalMission | null>(null),
    [qualityClaimMission, setQualityClaimMission] = useState<PortalMission | null>(null),
    [start, setStart] = useState(""),
    [end, setEnd] = useState(""),
    [propertyId, setPropertyId] = useState(""),
    [selectedServiceKeys, setSelectedServiceKeys] = useState<string[]>([]),
    [location, setLocation] = useState(client?.address || ""),
    [notes, setNotes] = useState(""),
    [message, setMessage] = useState(""),
    [proposalBusy, setProposalBusy] = useState(""),
    [proposalRefusal, setProposalRefusal] = useState<{
      id: string;
      reason: string;
      requestRevision: boolean;
    } | null>(null);
  const clientServices = data.services.filter(
      (s) => s.client_id === client?.id,
    ),
    propertyServices = data.propertyServices.filter(
      (s) => s.property_id === propertyId && s.active,
    ),
    serviceOptions = [
      ...propertyServices.map((service) => ({
        ...service,
        key: `property:${service.id}`,
        scopeLabel: "Tarif de ce bien",
      })),
      ...clientServices.map((service) => ({
        ...service,
        key: `client:${service.id}`,
        scopeLabel: "Prestation ou supplément du client",
      })),
    ],
    selectedServices = serviceOptions.filter((service) =>
      selectedServiceKeys.includes(service.key),
    ),
    proposals = (data.serviceProposals || []).filter(
      (p) => p.client_id === client?.id,
    ),
    pendingMissionChanges = (data.missionChangeEvents || []).filter(
      (event) => event.client_approval_status === "pending" && event.status === "approved",
    ),
    qualityClaims = data.missionQualityClaims || [],
    qualityClaimsAvailable = !(data.unavailableCapabilities || []).includes("mission_quality_claims"),
    reviewRequests = data.googleReviewRequests || [];
  const upcoming = data.missions.filter(
      (m) => new Date(m.ends_at) >= new Date() && m.status !== "cancelled",
    ),
    calendarMissions = data.missions.filter((mission) => mission.status !== "cancelled"),
    past = data.missions
      .filter(
        (m) => new Date(m.ends_at) < new Date() || m.status === "completed",
      )
      .sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!client) return setMessage("Aucune fiche client reliée à ce compte.");
    const s = new Date(start),
      f = new Date(end);
    if (!(f > s))
      return setMessage("La fin souhaitée doit être postérieure au début.");
    const requestPayload = {
        organization_id: profile.organization_id,
        client_id: client.id,
        property_id: propertyId || null,
        service_id:
          selectedServices[0]?.key.startsWith("client:")
            ? selectedServices[0].id
            : null,
        property_service_id:
          selectedServices[0]?.key.startsWith("property:")
            ? selectedServices[0].id
            : null,
        requested_by: profile.id,
        desired_starts_at: s.toISOString(),
        desired_ends_at: f.toISOString(),
        location,
        client_notes: notes || null,
        status: "pending",
      };
    let result = await supabase!.rpc("create_mission_request_with_services", {
      p_client_id: client.id,
      p_property_id: propertyId || null,
      p_service_keys: selectedServiceKeys,
      p_desired_starts_at: s.toISOString(),
      p_desired_ends_at: f.toISOString(),
      p_location: location,
      p_client_notes: notes || null,
    });
    if (
      result.error &&
      /create_mission_request_with_services|schema cache|function/i.test(
        result.error.message,
      )
    ) {
      if (selectedServiceKeys.length > 1) {
        setMessage(
          "La sélection de plusieurs prestations attend la mise à jour de la base. Votre demande n’a pas été envoyée.",
        );
        return;
      }
      result = await supabase!.from("mission_requests").insert(requestPayload);
    }
    const { error } = result;
    setMessage(error ? error.message : "Demande de mission transmise à ARD.");
    if (!error) {
      setStart("");
      setEnd("");
      setSelectedServiceKeys([]);
      setNotes("");
      reload();
    }
  }
  async function respondProposal(
    id: string,
    accept: boolean,
    reason = "",
    requestRevision = false,
  ) {
    setProposalBusy(id);
    const { error } = await supabase!.rpc("respond_service_proposal", {
      p_proposal_id: id,
      p_accept: accept,
      p_reason: reason.trim() || null,
      p_request_revision: requestRevision,
    });
    setProposalBusy("");
    setMessage(
      error
        ? error.message
        : accept
          ? "Prestation acceptée. Elle est maintenant rattachée à votre compte."
          : requestRevision
            ? "Votre retour a été transmis. ARD va étudier une nouvelle proposition."
            : "Votre refus motivé a été transmis à ARD.",
    );
    if (!error) {
      setProposalRefusal(null);
      reload();
    }
  }
  async function respondMissionChange(id: string, accept: boolean) {
    setProposalBusy(id);
    const { error } = await supabase!.rpc("respond_mission_change_client", {
      p_event_id: id,
      p_accept: accept,
    });
    setProposalBusy("");
    setMessage(
      error
        ? error.message
        : accept
          ? "Modification acceptée et appliquée à la mission."
          : "Modification refusée. ARD a été informé et la mission reste inchangée.",
    );
    if (!error) reload();
  }
  async function recordReviewAction(id: string, action: "opened" | "dismissed") {
    const { error } = await supabase!.rpc("record_google_review_request_action", {
      p_request_id: id,
      p_action: action,
    });
    if (error) {
      setMessage(error.message);
      return;
    }
    setMessage(
      action === "opened"
        ? "Le lien Google a été ouvert. ARD ne suit ni le contenu ni la publication de votre avis."
        : "Cette invitation ne sera plus affichée pour cette mission.",
    );
    reload();
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Planning et interventions</span>
          <h1>Mes missions</h1>
        </div>
      </div>
      {proposals.some((p) => p.status === "pending") && (
        <section className="panel proposal-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Votre accord est nécessaire</span>
              <h2>Prestations proposées par ARD</h2>
            </div>
            <strong>
              {proposals.filter((p) => p.status === "pending").length}
            </strong>
          </div>
          {proposals
            .filter((p) => p.status === "pending")
            .map((proposal) => (
              <article className="service-proposal" key={proposal.id}>
                <div>
                  <strong>{proposal.name}</strong>
                  <small>
                    {proposal.property_id
                      ? data.properties.find(
                          (p) => p.id === proposal.property_id,
                        )?.name || "Bien concerné"
                      : "Compte client"}
                  </small>
                  {proposal.notes && <p>{proposal.notes}</p>}
                </div>
                <div className="proposal-price">
                  <strong>{money(proposal.unit_price_ex_vat)} HT</strong>
                  <small>TVA {proposal.vat_rate} %</small>
                </div>
                <div className="proposal-actions">
                  <button
                    className="portal-button danger"
                    disabled={proposalBusy === proposal.id}
                    onClick={() =>
                      setProposalRefusal({
                        id: proposal.id,
                        reason: "",
                        requestRevision: true,
                      })
                    }
                  >
                    Discuter ou refuser
                  </button>
                  <button
                    className="portal-button"
                    disabled={proposalBusy === proposal.id}
                    onClick={() => respondProposal(proposal.id, true)}
                  >
                    Accepter la prestation
                  </button>
                </div>
              </article>
            ))}
        </section>
      )}
      {proposalRefusal && (
        <div
          className="portal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setProposalRefusal(null);
          }}
        >
          <form
            className="portal-detail service-proposal-response-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Expliquer votre réponse à la proposition"
            onSubmit={(event) => {
              event.preventDefault();
              void respondProposal(
                proposalRefusal.id,
                false,
                proposalRefusal.reason,
                proposalRefusal.requestRevision,
              );
            }}
          >
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Votre retour aide ARD à ajuster son offre</span>
                <h2>Que souhaitez-vous faire ?</h2>
              </div>
              <button
                type="button"
                className="icon-close"
                onClick={() => setProposalRefusal(null)}
              >
                ×
              </button>
            </div>
            <div className="proposal-response-choice">
              <label>
                <input
                  type="radio"
                  name="proposal-response"
                  checked={proposalRefusal.requestRevision}
                  onChange={() =>
                    setProposalRefusal((current) =>
                      current ? { ...current, requestRevision: true } : current,
                    )
                  }
                />
                <span>
                  <strong>Je souhaite une nouvelle proposition</strong>
                  <small>ARD pourra revoir le périmètre, le prix ou les conditions.</small>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="proposal-response"
                  checked={!proposalRefusal.requestRevision}
                  onChange={() =>
                    setProposalRefusal((current) =>
                      current ? { ...current, requestRevision: false } : current,
                    )
                  }
                />
                <span>
                  <strong>Je ne souhaite pas poursuivre</strong>
                  <small>La proposition sera clôturée après transmission de votre motif.</small>
                </span>
              </label>
            </div>
            <label className="modal-field">
              Pourquoi cette proposition ne vous convient-elle pas ?
              <textarea
                required
                minLength={5}
                rows={5}
                value={proposalRefusal.reason}
                onChange={(event) =>
                  setProposalRefusal((current) =>
                    current ? { ...current, reason: event.target.value } : current,
                  )
                }
                placeholder="Ex. budget trop élevé, prestation à simplifier, autre date souhaitée…"
              />
            </label>
            <div className="detail-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => setProposalRefusal(null)}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={
                  proposalBusy === proposalRefusal.id ||
                  proposalRefusal.reason.trim().length < 5
                }
              >
                {proposalBusy === proposalRefusal.id
                  ? "Transmission…"
                  : proposalRefusal.requestRevision
                    ? "Demander une nouvelle proposition"
                    : "Confirmer mon refus"}
              </button>
            </div>
          </form>
        </div>
      )}
      {pendingMissionChanges.length > 0 && (
        <section className="panel proposal-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Avant modification</span>
              <h2>Changements de mission à valider</h2>
            </div>
            <strong>{pendingMissionChanges.length}</strong>
          </div>
          {pendingMissionChanges.map((change) => {
            const mission = data.missions.find((item) => item.id === change.mission_id),
              newStart = String(change.proposed_changes?.starts_at || ""),
              newEnd = String(change.proposed_changes?.ends_at || "");
            return (
              <article className="service-proposal" key={change.id}>
                <div>
                  <strong>{mission ? dateTime(mission.starts_at) : "Mission"} · changement proposé</strong>
                  <p>{change.reason}</p>
                  {newStart && newEnd && (
                    <small>Nouvel horaire : {dateTime(newStart)} → {dateTime(newEnd)}</small>
                  )}
                  {change.client_amount_delta_ex_vat > 0 && (
                    <small>Supplément proposé : {money(change.client_amount_delta_ex_vat)} HT</small>
                  )}
                  {change.operational_notes && <small>{change.operational_notes}</small>}
                </div>
                <div className="proposal-actions">
                  <button type="button" className="danger" disabled={proposalBusy === change.id} onClick={() => respondMissionChange(change.id, false)}>
                    Refuser
                  </button>
                  <button type="button" className="success" disabled={proposalBusy === change.id} onClick={() => respondMissionChange(change.id, true)}>
                    Accepter
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}
      <AgendaWeek missions={calendarMissions} onSelect={setSelected} showAmount properties={data.properties} qualityClaims={qualityClaims} />
      <section className="panel">
        <h2>Demander une nouvelle mission</h2>
        <p className="section-intro">
          Choisissez un service et un créneau : ARD vérifiera la disponibilité
          avant confirmation.
        </p>
        <form className="form-grid" onSubmit={submit}>
          {client?.client_type === "concierge" && (
            <label>
              Bien concerné
              <select
                value={propertyId}
                onChange={(e) => {
                  const id = e.target.value,
                    set = properties.find((p) => p.id === id);
                  setPropertyId(id);
                  setSelectedServiceKeys([]);
                  if (set) setLocation(set.address);
                }}
                required
              >
                <option value="">Choisir un bien</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <fieldset className="wide mission-service-picker client-mission-service-picker">
            <legend>Prestations demandées</legend>
            <p>
              Vous pouvez combiner le forfait du bien avec les options et
              suppléments prévus pour votre compte.
            </p>
            <div className="mission-service-options">
              {serviceOptions.map((service) => (
                <label key={service.key} className="mission-service-option">
                  <input
                    type="checkbox"
                    checked={selectedServiceKeys.includes(service.key)}
                    onChange={(event) =>
                      setSelectedServiceKeys((current) =>
                        event.target.checked
                          ? [...current, service.key]
                          : current.filter((key) => key !== service.key),
                      )
                    }
                  />
                  <span>
                    <strong>{service.name}</strong>
                    <small>
                      {service.scopeLabel} · {money(service.unit_price_ex_vat)} HT
                    </small>
                  </span>
                </label>
              ))}
            </div>
            {serviceOptions.length === 0 ? (
              <small>
                Aucune prestation préenregistrée : décrivez votre besoin dans
                les précisions, ARD préparera la proposition adaptée.
              </small>
            ) : (
              <small data-testid="client-request-service-summary">
                {selectedServices.length
                  ? `${selectedServices.length} prestation(s) · ${money(
                      selectedServices.reduce(
                        (sum, service) =>
                          sum + Number(service.unit_price_ex_vat || 0),
                        0,
                      ),
                    )} HT à confirmer par ARD`
                  : "Aucune prestation sélectionnée — besoin à préciser avec ARD"}
              </small>
            )}
          </fieldset>
          <label>
            Adresse d’intervention
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              required
            />
          </label>
          <label>
            Début souhaité
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
            />
          </label>
          <label>
            Fin souhaitée
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
            />
          </label>
          <label className="wide">
            Précisions
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <button>Envoyer la demande</button>
        </form>
        {message && (
          <p
            className={`notice ${message.startsWith("Demande") ? "success" : "error"}`}
          >
            {message}
          </p>
        )}
      </section>
      <section className="panel">
        <h2>Mes demandes</h2>
        {data.requests.length ? (
          <div className="request-list">
            {data.requests.map((r) => (
              <div key={r.id}>
                <span>
                  <strong>{dateTime(r.desired_starts_at)}</strong>
                  <small>{r.location}</small>
                  <small>
                    {(r.requested_service_keys || [])
                      .map((key) => {
                        const [scope, id] = key.split(":"),
                          service =
                            scope === "property"
                              ? data.propertyServices.find((item) => item.id === id)
                              : data.services.find((item) => item.id === id);
                        return service?.name;
                      })
                      .filter(Boolean)
                      .join(" · ") || "Besoin à préciser avec ARD"}
                  </small>
                </span>
                <StatusPill value={r.status} />
              </div>
            ))}
          </div>
        ) : (
          <p className="empty">Aucune demande en cours.</p>
        )}
      </section>
      <section className="panel">
        <h2>Missions à venir</h2>
        <MissionRows
          missions={upcoming}
          onSelect={setSelected}
          mode="client"
          properties={data.properties}
          serviceLines={data.missionServiceLines || []}
        />
      </section>
      <section className="panel">
        <h2>Missions passées et comptes rendus</h2>
        <MissionRows
          missions={past}
          onSelect={setSelected}
          mode="client"
          properties={data.properties}
          serviceLines={data.missionServiceLines || []}
        />
      </section>
      {selected && (
        <MissionDetail
          mission={selected}
          mode="client"
          property={data.properties.find((item) => item.id === selected.property_id)}
          serviceLines={(data.missionServiceLines || []).filter(
            (item) => item.mission_id === selected.id,
          )}
          qualityClaims={qualityClaims.filter((claim)=>claim.mission_id===selected.id)}
          reviewRequest={reviewRequests.find((request)=>request.mission_id===selected.id)}
          onQualityClaim={qualityClaimsAvailable ? ()=>setQualityClaimMission(selected) : undefined}
          onReviewOpen={() => {
            const request = reviewRequests.find((item)=>item.mission_id===selected.id);
            if (request) void recordReviewAction(request.id,"opened");
          }}
          onReviewDismiss={() => {
            const request = reviewRequests.find((item)=>item.mission_id===selected.id);
            if (request) void recordReviewAction(request.id,"dismissed");
          }}
          onClose={() => setSelected(null)}
        />
      )}
      {qualityClaimMission&&(
        <ClientQualityClaimDialog profile={profile} mission={qualityClaimMission} reload={reload} onClose={()=>setQualityClaimMission(null)} />
      )}
    </>
  );
}

function ClientInvoiceDetail({
  invoice,
  data,
  onClose,
}: {
  invoice: PortalInvoice;
  data: PortalData;
  onClose: () => void;
}) {
  const [pdfBusy, setPdfBusy] = useState(false),
    [pdfMessage, setPdfMessage] = useState("");
  const henrriDocumentsAvailable = !(data.unavailableCapabilities || []).includes(
      "henrri_documents",
    ),
    lines = (data.invoiceLines || []).filter(
      (line) => line.invoice_id === invoice.id,
    ),
    payments = (data.invoicePayments || []).filter(
      (payment) => payment.invoice_id === invoice.id,
    ),
    events = (data.commercialEvents || []).filter(
      (event) => event.invoice_id === invoice.id,
    ),
    paymentMethodLabels: Record<string, string> = {
      transfer: "Virement",
      bank_transfer: "Virement",
      direct_debit: "Prélèvement",
      card: "Carte",
      cash: "Espèces",
      cheque: "Chèque",
      other: "Autre",
    },
    paymentTerms =
      invoice.payment_terms_mode === "immediate"
        ? "Paiement comptant"
        : invoice.payment_terms_mode === "end_of_month"
          ? "Paiement en fin de mois"
          : `Paiement à ${invoice.payment_terms_days ?? 30} jours`,
    eventLabel = (event: PortalCommercialEvent) => {
      if (event.event_type === "status_changed")
        return `${statusLabel(event.status_from || undefined)} → ${statusLabel(event.status_to || undefined)}`;
      if (["sent", "issued"].includes(event.event_type)) return "Facture émise";
      if (event.event_type === "payment_recorded") return "Règlement enregistré";
      if (event.event_type === "reminder_prepared") return "Relance préparée";
      if (event.event_type === "attachment_added") return "Pièce ajoutée";
      if (event.event_type === "created") return "Facture créée";
      if (event.event_type.startsWith("line_")) return "Contenu de la facture mis à jour";
      return "Mise à jour de la facture";
    };
  async function openPdf() {
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    setPdfBusy(true);
    setPdfMessage("");
    const result = await supabase!.functions.invoke<{
      url: string;
      cached: boolean;
      expires_in: number;
    }>("henrri-document-pdf", {
      body: {
        document_type: "invoice",
        document_id: invoice.id,
        format: "pdf",
      },
    });
    setPdfBusy(false);
    if (result.error || !result.data?.url) {
      popup?.close();
      setPdfMessage(result.error?.message || "Le PDF de la facture est indisponible.");
      return;
    }
    if (popup) popup.location.href = result.data.url;
    else window.open(result.data.url, "_blank", "noopener,noreferrer");
    setPdfMessage("PDF ouvert via un lien ARD privé valable 5 minutes.");
  }
  return (
    <div className="portal-overlay" role="dialog" aria-modal="true">
      <section className="portal-detail commercial-document-modal client-invoice-detail">
        <div className="portal-detail-head">
          <div>
            <span className="eyebrow">Détail de la facture</span>
            <h2>{invoice.number || "Facture en préparation"}</h2>
          </div>
          <button className="icon-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="detail-grid">
          <div><span>Émise le</span><strong>{invoice.issue_on ? new Date(invoice.issue_on).toLocaleDateString("fr-FR") : "Non émise"}</strong></div>
          <div><span>Échéance</span><strong>{invoice.due_on ? new Date(invoice.due_on).toLocaleDateString("fr-FR") : "Non renseignée"}</strong></div>
          <div><span>Conditions de paiement</span><strong>{paymentTerms}</strong></div>
          <div><span>État</span><StatusPill value={invoice.status} /></div>
          <div><span>Total HT</span><strong>{money(invoice.total_ex_vat)}</strong></div>
          <div><span>TVA</span><strong>{money(invoice.total_vat)}</strong></div>
          <div><span>Total TTC</span><strong>{money(invoice.total_inc_vat)}</strong></div>
          <div><span>Montant réglé</span><strong>{money(invoice.amount_paid || 0)}</strong></div>
          <div><span>Reste à régler</span><strong>{money(Math.max(0, Number(invoice.total_inc_vat || 0) - Number(invoice.amount_paid || 0)))}</strong></div>
          <div><span>Validée comme payée le</span><strong>{invoice.last_payment_on ? new Date(invoice.last_payment_on).toLocaleDateString("fr-FR") : "Pas encore validée"}</strong></div>
        </div>
        <section className="document-lines">
          <h3>Prestations facturées</h3>
          {lines.length ? (
            <div className="invoice-detail-lines">
              {lines.map((line) => {
                const mission = data.missions.find((item) => item.id === line.mission_id),
                  totalExVat = Number(line.quantity) * Number(line.unit_price_ex_vat);
                return (
                  <article key={line.id}>
                    <span>
                      <strong>{line.description}</strong>
                      {mission && <small>Intervention du {new Date(mission.starts_at).toLocaleDateString("fr-FR")} · {mission.location}</small>}
                    </span>
                    <small>{line.quantity} × {money(line.unit_price_ex_vat)} HT · TVA {line.vat_rate} %</small>
                    <b>{money(totalExVat * (1 + line.vat_rate / 100))} TTC</b>
                  </article>
                );
              })}
            </div>
          ) : <p className="empty">Aucune ligne détaillée disponible.</p>}
        </section>
        <div className="commercial-support-grid">
          <section className="detail-note">
            <h3>Règlements</h3>
            {payments.length ? (
              <div className="payment-list">
                {payments.map((payment) => (
                  <div key={payment.id}>
                    <strong>{money(payment.amount)}</strong>
                    <span>Reçu le {new Date(payment.received_on).toLocaleDateString("fr-FR")} · {paymentMethodLabels[payment.method] || payment.method}</span>
                    <small>Validé dans ARD Manager le {dateTime(payment.created_at)}{payment.reference ? ` · Réf. ${payment.reference}` : ""}</small>
                    <StatusPill value={payment.status} />
                  </div>
                ))}
              </div>
            ) : <p className="empty">Aucun règlement confirmé.</p>}
          </section>
          <section className="detail-note">
            <h3>Historique</h3>
            {events.length ? (
              <ol className="commercial-timeline">
                {events.map((event) => (
                  <li key={event.id}>
                    <strong>{eventLabel(event)}</strong>
                    <span>{dateTime(event.created_at)}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="empty">Aucun historique complémentaire.</p>}
          </section>
        </div>
        {invoice.henrri_id && henrriDocumentsAvailable && (
          <button className="secondary" type="button" disabled={pdfBusy} onClick={openPdf}>
            {pdfBusy ? "Préparation du PDF…" : "Afficher le PDF de la facture"}
          </button>
        )}
        {invoice.henrri_id && !henrriDocumentsAvailable && (
          <p className="form-message">
            Le PDF reste disponible auprès de l’administration. Son ouverture
            directe sera activée après installation du module documentaire
            sécurisé.
          </p>
        )}
        {pdfMessage && <p className="form-message">{pdfMessage}</p>}
      </section>
    </div>
  );
}

export function ClientBillingRich({ data }: { data: PortalData }) {
  const today = parisCalendarDay(),
    currentYear = Number(today.slice(0, 4)),
    [selected, setSelected] = useState<PortalInvoice | null>(null),
    [selectedMission, setSelectedMission] = useState<PortalMission | null>(null),
    [period, setPeriod] = useState(today.slice(0, 7)),
    [statementFrom, setStatementFrom] = useState(`${currentYear}-01-01`),
    [statementTo, setStatementTo] = useState(today),
    [statementMessage, setStatementMessage] = useState("");
  const periodInvoices = data.invoices.filter((invoice) => {
      const invoicePeriod = invoice.issue_on?.slice(0, 7) || invoice.billing_period?.slice(0, 7);
      return invoicePeriod === period;
    });
  const upcoming = data.missions.filter(
      (m) =>
        ["planned", "to_invoice", "queued"].includes(
          m.billing_status || "planned",
        ) &&
        m.status !== "cancelled" &&
        m.ends_at.slice(0, 7) === period,
    ),
    groups = [
      {
        title: "À payer",
        items: periodInvoices.filter((i) =>
          ["sent", "partially_paid", "overdue"].includes(i.status),
        ),
      },
      {
        title: "À venir",
        items: periodInvoices.filter((i) =>
          ["draft", "ready"].includes(i.status),
        ),
      },
      {
        title: "Payées",
        items: periodInvoices.filter((i) => i.status === "paid"),
      },
    ];
  function printAccountingStatement() {
    if (!statementFrom || !statementTo || statementFrom > statementTo) {
      setStatementMessage("Choisissez une période valide.");
      return;
    }
    const rows = data.invoices
        .filter(
          (invoice) =>
            !!invoice.issue_on &&
            invoice.status !== "draft" &&
            invoice.issue_on >= statementFrom &&
            invoice.issue_on <= statementTo,
        )
        .sort((left, right) => (left.issue_on || "").localeCompare(right.issue_on || "")),
      totalExVat = rows.reduce((sum, invoice) => sum + Number(invoice.total_ex_vat || 0), 0),
      totalVat = rows.reduce((sum, invoice) => sum + Number(invoice.total_vat || 0), 0),
      totalIncVat = rows.reduce((sum, invoice) => sum + Number(invoice.total_inc_vat || 0), 0),
      companyName = data.organization?.name || "Entreprise utilisatrice d’ARD Manager",
      client = data.clients[0],
      customerName = client?.name || "Client",
      clientProperties = new Set(
        data.properties
          .filter((property) => property.client_id === client?.id)
          .map((property) => property.id),
      ),
      pricingRows = [
        ...data.services
          .filter((service) => service.client_id === client?.id)
          .map((service) => ({
            name: service.name,
            mode: service.billing_mode,
            amount: service.unit_price_ex_vat,
            vat: null as number | null,
            detail: "Prestation générale",
          })),
        ...data.propertyServices
          .filter(
            (service) =>
              clientProperties.has(service.property_id) && service.active,
          )
          .map((service) => ({
            name: service.name,
            mode: service.billing_mode,
            amount: service.unit_price_ex_vat,
            vat: service.vat_rate,
            detail: service.notes || "Prestation rattachée à un bien",
          })),
      ],
      companyAddress = [
        data.organization?.address,
        data.organization?.postal_code,
        data.organization?.city,
      ].filter(Boolean).join(" "),
      popup = window.open("", "_blank");
    if (!rows.length) {
      setStatementMessage("Aucune facture émise sur cette période.");
      popup?.close();
      return;
    }
    if (!popup) {
      setStatementMessage("Autorisez l’ouverture de la fenêtre pour créer le PDF.");
      return;
    }
    popup.opener = null;
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Récapitulatif comptable ${escapeDocumentText(statementFrom)} au ${escapeDocumentText(statementTo)}</title><style>
      @page{size:A4;margin:16mm}*{box-sizing:border-box}body{margin:0;color:#191b20;font:13px Arial,sans-serif}header{display:flex;justify-content:space-between;gap:24px;padding:22px;border-radius:16px;background:#191b20;color:white}header h1{margin:0 0 6px;font-size:24px}header b{color:#c6a15b;letter-spacing:.08em}header p{margin:3px 0;color:#dfe1e5}.period{margin:24px 0 14px}.period h2,.terms h2{margin:0 0 6px}.terms{margin-top:18px;padding:14px;border:1px solid #d8c28f;border-radius:13px;background:#fffdf7;break-inside:avoid}.terms table{margin-top:4px}.terms small{display:block;color:#666;margin-top:2px}table{width:100%;border-collapse:collapse}th,td{padding:10px 8px;border-bottom:1px solid #e2e4e8;text-align:right}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2){text-align:left}thead{background:#f5f1e8}.totals{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:18px}.totals div{padding:14px;border:1px solid #d8c28f;border-radius:12px;background:#fbf8f0}.totals span{display:block;color:#666;font-size:11px}.totals strong{display:block;margin-top:5px;font-size:18px}.notice{margin-top:24px;padding-top:12px;border-top:1px solid #ddd;color:#666;font-size:11px}footer{margin-top:28px;color:#777;font-size:10px;text-align:center}@media print{button{display:none}}
    </style></head><body><header><div><b>${escapeDocumentText(companyName)}</b><h1>Récapitulatif comptable</h1><p>${escapeDocumentText(companyAddress)}</p></div><div><p>Destinataire</p><strong>${escapeDocumentText(customerName)}</strong></div></header><section class="period"><h2>Factures du ${escapeDocumentText(new Date(`${statementFrom}T12:00:00`).toLocaleDateString("fr-FR"))} au ${escapeDocumentText(new Date(`${statementTo}T12:00:00`).toLocaleDateString("fr-FR"))}</h2><p>${rows.length} facture(s) émise(s)</p></section><table><thead><tr><th>Facture</th><th>Date</th><th>HT</th><th>TVA</th><th>TTC</th></tr></thead><tbody>${rows.map((invoice) => `<tr><td>${escapeDocumentText(invoice.number || "Sans numéro")}</td><td>${escapeDocumentText(new Date(`${invoice.issue_on}T12:00:00`).toLocaleDateString("fr-FR"))}</td><td>${escapeDocumentText(money(invoice.total_ex_vat))}</td><td>${escapeDocumentText(money(invoice.total_vat))}</td><td><strong>${escapeDocumentText(money(invoice.total_inc_vat))}</strong></td></tr>`).join("")}</tbody></table><div class="totals"><div><span>Total HT</span><strong>${escapeDocumentText(money(totalExVat))}</strong></div><div><span>Total TVA</span><strong>${escapeDocumentText(money(totalVat))}</strong></div><div><span>Total TTC</span><strong>${escapeDocumentText(money(totalIncVat))}</strong></div></div><section class="terms"><h2>Prestations et conditions tarifaires enregistrées</h2><table><thead><tr><th>Prestation</th><th>Mode</th><th>Tarif</th></tr></thead><tbody>${pricingRows.map((row) => `<tr><td><strong>${escapeDocumentText(row.name)}</strong><small>${escapeDocumentText(row.detail)}</small></td><td>${escapeDocumentText(row.mode)}</td><td><strong>${escapeDocumentText(money(row.amount))} HT</strong>${row.vat == null ? "" : `<small>TVA ${escapeDocumentText(row.vat)} %</small>`}</td></tr>`).join("") || `<tr><td colspan="3">Aucune condition tarifaire active enregistrée.</td></tr>`}</tbody></table></section><p class="notice">Ce document est un récapitulatif informatif établi à partir des factures émises. Il ne remplace ni les factures officielles ni les justificatifs fiscaux originaux.</p><footer>Document préparé depuis ARD Manager · ${escapeDocumentText(new Date().toLocaleDateString("fr-FR"))}</footer><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));<\/script></body></html>`);
    popup.document.close();
    setStatementMessage("Récapitulatif ouvert : choisissez « Enregistrer au format PDF » dans la fenêtre d’impression.");
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Suivi financier</span>
          <h1>Facturation</h1>
        </div>
        <label className="period-picker">
          Mois affiché
          <input type="month" value={period} onChange={(event) => setPeriod(event.target.value)} />
        </label>
      </div>
      <div className="stats portal-stats">
        <article>
          <span>Prestations programmées</span>
          <strong>
            {money(upcoming.reduce((s, m) => s + (m.amount_ex_vat || 0), 0))}
          </strong>
          <small>HT, avant émission</small>
        </article>
        <article>
          <span>À régler</span>
          <strong>
            {money(
              groups[0].items.reduce(
                (sum, invoice) =>
                  sum +
                  Math.max(
                    0,
                    Number(invoice.total_inc_vat || 0) -
                      Number(invoice.amount_paid || 0),
                  ),
                0,
              ),
            )}
          </strong>
          <small>TTC</small>
        </article>
        <article>
          <span>Réglé</span>
          <strong>
            {money(
              periodInvoices.reduce(
                (sum, invoice) => sum + Number(invoice.amount_paid || 0),
                0,
              ),
            )}
          </strong>
          <small>TTC</small>
        </article>
      </div>
      <ClientAnnualBillingChart
        invoices={data.invoices}
        year={Number(period.slice(0, 4)) || currentYear}
      />
      <section className="panel client-accounting-statement">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Aide comptable et fiscale</span>
            <h2>Créer un récapitulatif de factures</h2>
            <p>Choisissez une période pour obtenir la liste des factures et les totaux HT, TVA et TTC.</p>
          </div>
        </div>
        <div className="client-statement-controls">
          <label>Du<input type="date" value={statementFrom} onChange={(event) => setStatementFrom(event.target.value)} /></label>
          <label>Au<input type="date" value={statementTo} onChange={(event) => setStatementTo(event.target.value)} /></label>
          <button type="button" className="portal-button" onClick={printAccountingStatement}>Créer le récapitulatif PDF</button>
        </div>
        <small>Le document est informatif et ne remplace pas les factures officielles.</small>
        {statementMessage && <p className="form-message">{statementMessage}</p>}
      </section>
      <section className="panel">
        <h2>Prestations qui alimenteront une prochaine facture</h2>
        <div className="billing-lines">
          {upcoming.map((m) => {
            const property = data.properties.find((item) => item.id === m.property_id),
              serviceLines = (data.missionServiceLines || [])
                .filter((item) => item.mission_id === m.id)
                .sort((left, right) => left.position - right.position),
              serviceSummary = serviceLines.length
                ? serviceLines.map((item) => item.label_snapshot).join(" · ")
                : "Contenu de la prestation à confirmer";
            return (
              <div key={m.id} className="billing-mission-row">
                <span className="billing-mission-identity">
                  <strong>{property?.name || "Bien à préciser"}</strong>
                  <small className="billing-mission-services">{serviceSummary}</small>
                  <small>
                    {dateTime(m.starts_at)} · {m.location || property?.address || "Adresse à préciser"}
                  </small>
                </span>
                <b>{money(m.amount_ex_vat || 0)} HT</b>
                <StatusPill value={m.billing_status} />
                <button
                  type="button"
                  className="secondary compact"
                  onClick={() => setSelectedMission(m)}
                >
                  Voir la mission
                </button>
              </div>
            );
          })}
        </div>
        {!upcoming.length && (
          <p className="empty">Aucune prestation en attente de facturation.</p>
        )}
      </section>
      {groups.map((group) => (
        <section className="panel" key={group.title}>
          <h2>{group.title}</h2>
          {group.items.length ? (
            <div className="billing-lines">
              {group.items.map((i) => (
                <div key={i.id} className="invoice-summary-row">
                  <span>
                    <strong>{i.number || "Facture en préparation"}</strong>
                    <small>
                      {i.issue_on
                        ? `Émise le ${new Date(i.issue_on).toLocaleDateString("fr-FR")}`
                        : "Date d’émission à venir"}
                      {i.due_on
                        ? ` · Échéance ${new Date(i.due_on).toLocaleDateString("fr-FR")}`
                        : ""}
                    </small>
                  </span>
                  <b>
                    {group.title === "À payer"
                      ? `${money(Math.max(0, Number(i.total_inc_vat || 0) - Number(i.amount_paid || 0)))} restant`
                      : `${money(i.total_inc_vat)} TTC`}
                  </b>
                  <StatusPill value={i.status} />
                  <button className="secondary compact" onClick={() => setSelected(i)}>
                    Consulter le détail
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty">Aucune facture dans cet état.</p>
          )}
        </section>
      ))}
      {selected && <ClientInvoiceDetail invoice={selected} data={data} onClose={() => setSelected(null)} />}
      {selectedMission && (
        <MissionDetail
          mission={selectedMission}
          mode="client"
          property={data.properties.find((item) => item.id === selectedMission.property_id)}
          serviceLines={(data.missionServiceLines || []).filter(
            (item) => item.mission_id === selectedMission.id,
          )}
          onClose={() => setSelectedMission(null)}
        />
      )}
    </>
  );
}

export function ClientPropertiesRich({
  profile,
  data,
  reload,
}: {
  profile: PortalProfile;
  data: PortalData;
  reload: () => void;
}) {
  const client = data.clients[0],
    [name, setName] = useState(""),
    [address, setAddress] = useState(""),
    [contact, setContact] = useState(""),
    [access, setAccess] = useState(""),
    [propertyId, setPropertyId] = useState(""),
    [serviceName, setServiceName] = useState(""),
    [serviceDescription, setServiceDescription] = useState(""),
    [serviceNotes, setServiceNotes] = useState(""),
    [quoteResponse, setQuoteResponse] = useState<{
      requestId: string;
      reason: string;
      requestRevision: boolean;
    } | null>(null),
    [propertyChange, setPropertyChange] = useState<{
      property: PortalProperty;
      kind: "update" | "archive";
      name: string;
      address: string;
      contact: string;
      access: string;
      reason: string;
    } | null>(null),
    [busy, setBusy] = useState(""),
    [message, setMessage] = useState("");
  const properties = data.properties.filter(
    (p) => p.client_id === client?.id && p.active,
  ),
    propertyRequests = (data.propertyRequests || []).filter(
      (request) => request.client_id === client?.id,
    ),
    serviceRequests = (data.propertyServiceQuoteRequests || []).filter(
      (request) => request.client_id === client?.id,
    );
  async function addProperty(e: React.FormEvent) {
    e.preventDefault();
    if (!client) return;
    setBusy("property");
    const { error } = await supabase!.from("client_property_requests").insert({
      organization_id: profile.organization_id,
      client_id: client.id,
      requested_by: profile.id,
      name: name.trim(),
      address: address.trim(),
      contact_name: contact.trim() || null,
      access_notes: access.trim() || null,
      status: "pending",
    });
    setBusy("");
    setMessage(
      error
        ? error.message
        : "Proposition envoyée à l’administration. Le bien apparaîtra dans votre portefeuille après validation.",
    );
    if (!error) {
      setName("");
      setAddress("");
      setContact("");
      setAccess("");
      reload();
    }
  }
  async function requestServiceQuote(e: React.FormEvent) {
    e.preventDefault();
    if (!propertyId) return setMessage("Choisissez un bien.");
    if (!client) return;
    setBusy("service");
    const { error } = await supabase!
      .from("property_service_quote_requests")
      .insert({
      organization_id: profile.organization_id,
      client_id: client.id,
      property_id: propertyId,
      requested_by: profile.id,
      service_name: serviceName.trim(),
      requested_description: serviceDescription.trim(),
      client_notes: serviceNotes.trim() || null,
      status: "pending",
    });
    setBusy("");
    setMessage(
      error
        ? error.message
        : "Demande de devis envoyée. L’administration définira le tarif et le mode de facturation.",
    );
    if (!error) {
      setServiceName("");
      setServiceDescription("");
      setServiceNotes("");
      reload();
    }
  }
  function openPropertyChange(
    property: PortalProperty,
    kind: "update" | "archive",
  ) {
    setPropertyChange({
      property,
      kind,
      name: property.name,
      address: property.address,
      contact: property.contact_name || "",
      access: property.access_notes || "",
      reason: "",
    });
    setMessage("");
  }
  async function submitPropertyChange(e: React.FormEvent) {
    e.preventDefault();
    if (!propertyChange) return;
    setBusy("property-change");
    const { error } = await supabase!.rpc(
      "submit_client_property_change_request",
      {
        p_property_id: propertyChange.property.id,
        p_request_kind: propertyChange.kind,
        p_name: propertyChange.name.trim(),
        p_address: propertyChange.address.trim(),
        p_contact_name: propertyChange.contact.trim() || null,
        p_access_notes: propertyChange.access.trim() || null,
        p_requested_reason: propertyChange.reason.trim() || null,
      },
    );
    setBusy("");
    setMessage(
      error
        ? error.message
        : propertyChange.kind === "archive"
          ? "Demande de retrait envoyée. Le bien reste actif jusqu’à la décision de l’administration."
          : "Proposition de modification envoyée. Les informations officielles restent inchangées jusqu’à validation.",
    );
    if (!error) {
      setPropertyChange(null);
      reload();
    }
  }
  async function respondToQuote(
    requestId: string,
    accept: boolean,
    reason = "",
    requestRevision = false,
  ) {
    setBusy(requestId);
    const { error } = await supabase!.rpc(
      "respond_property_service_quote_request",
      {
        p_request_id: requestId,
        p_accept: accept,
        p_reason: reason.trim() || null,
        p_request_revision: requestRevision,
      },
    );
    setBusy("");
    setMessage(
      error
        ? error.message
        : accept
          ? "Devis accepté. La prestation est maintenant rattachée au bien."
          : requestRevision
            ? "Votre retour a été transmis. ARD va préparer une nouvelle proposition."
            : "Votre refus motivé a été transmis à ARD.",
    );
    if (!error) {
      setQuoteResponse(null);
      reload();
    }
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Portefeuille conciergerie</span>
          <h1>Mes biens et prestations</h1>
        </div>
      </div>
      <section className="panel">
        <h2>Biens gérés</h2>
        {properties.length ? (
          <div className="property-grid">
            {properties.map((p) => (
              <article key={p.id}>
                <div>
                  <span className="eyebrow">Bien</span>
                  <h3>{p.name}</h3>
                  <p>{p.address}</p>
                  {p.contact_name && <small>Contact : {p.contact_name}</small>}
                </div>
                <div className="property-card-actions">
                  <MapsLink location={p.address} label="Voir sur Maps" />
                  <button
                    type="button"
                    className="portal-button secondary compact"
                    onClick={() => openPropertyChange(p, "update")}
                  >
                    Proposer une modification
                  </button>
                  <button
                    type="button"
                    className="portal-button danger compact"
                    onClick={() => openPropertyChange(p, "archive")}
                  >
                    Demander le retrait
                  </button>
                </div>
                <h4>Prestations</h4>
                {data.propertyServices
                  .filter((s) => s.property_id === p.id && s.active)
                  .map((s) => (
                    <div className="property-service" key={s.id}>
                      <span>
                        {s.name}
                        <small>
                          {s.billing_mode === "hourly"
                            ? "Horaire"
                            : s.billing_mode === "fixed"
                              ? "Forfait"
                              : s.billing_mode === "per_unit"
                                ? "À l’unité"
                                : "Mensuel"}
                        </small>
                      </span>
                      <strong>{money(s.unit_price_ex_vat)} HT</strong>
                    </div>
                  ))}
                {!data.propertyServices.some(
                  (s) => s.property_id === p.id && s.active,
                ) && <p className="empty">Aucune prestation configurée.</p>}
              </article>
            ))}
          </div>
        ) : (
          <p className="empty">Aucun bien enregistré.</p>
        )}
      </section>
      <section className="panel">
        <h2>Proposer un nouveau bien</h2>
        <p className="section-intro">
          Préremplissez les informations utiles. L’administration les contrôle
          avant d’ajouter officiellement le bien à votre portefeuille.
        </p>
        <form className="form-grid" onSubmit={addProperty}>
          <label>
            Nom du bien
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Villa des Pins"
              required
            />
          </label>
          <label>
            Contact sur place
            <input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          </label>
          <label className="wide">
            Adresse complète
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
            />
          </label>
          <label className="wide">
            Consignes d’accès
            <textarea
              value={access}
              onChange={(e) => setAccess(e.target.value)}
            />
          </label>
          <button disabled={busy === "property"}>Envoyer la proposition</button>
        </form>
        {!!propertyRequests.length && (
          <div className="request-history">
            <h3>Suivi des propositions</h3>
            {propertyRequests.map((request) => (
              <article className="workflow-row" key={request.id}>
                <span>
                  <strong>
                    {request.request_kind === "archive"
                      ? "Retrait demandé"
                      : request.request_kind === "update"
                        ? "Modification proposée"
                        : "Nouveau bien proposé"}
                    {" · "}{request.name}
                  </strong>
                  <small>{request.address}</small>
                  {request.requested_reason && <small>{request.requested_reason}</small>}
                  {request.admin_notes && <small>{request.admin_notes}</small>}
                </span>
                <StatusPill value={request.status} />
              </article>
            ))}
          </div>
        )}
      </section>
      {propertyChange && (
        <div className="portal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPropertyChange(null);
        }}>
          <form
            className="portal-detail property-change-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={propertyChange.kind === "archive" ? "Demander le retrait du bien" : "Proposer une modification du bien"}
            onSubmit={submitPropertyChange}
          >
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Demande soumise à ARD</span>
                <h2>{propertyChange.kind === "archive" ? "Retirer ce bien" : "Modifier ce bien"}</h2>
              </div>
              <button type="button" className="icon-close" onClick={() => setPropertyChange(null)}>×</button>
            </div>
            {propertyChange.kind === "update" ? (
              <div className="form-grid">
                <label>Nom du bien<input required value={propertyChange.name} onChange={(event) => setPropertyChange((current) => current ? {...current,name:event.target.value} : current)} /></label>
                <label>Contact sur place<input value={propertyChange.contact} onChange={(event) => setPropertyChange((current) => current ? {...current,contact:event.target.value} : current)} /></label>
                <label className="wide">Adresse complète<input required value={propertyChange.address} onChange={(event) => setPropertyChange((current) => current ? {...current,address:event.target.value} : current)} /></label>
                <label className="wide">Consignes d’accès<textarea value={propertyChange.access} onChange={(event) => setPropertyChange((current) => current ? {...current,access:event.target.value} : current)} /></label>
                <label className="wide">Motif ou précision pour l’administration<textarea value={propertyChange.reason} onChange={(event) => setPropertyChange((current) => current ? {...current,reason:event.target.value} : current)} /></label>
              </div>
            ) : (
              <>
                <div className="detail-note">
                  <strong>{propertyChange.property.name}</strong>
                  <p>{propertyChange.property.address}</p>
                </div>
                <label className="modal-field">Pourquoi ne souhaitez-vous plus d’intervention sur ce bien ?<textarea required minLength={5} value={propertyChange.reason} onChange={(event) => setPropertyChange((current) => current ? {...current,reason:event.target.value} : current)} /></label>
                <p className="section-intro">Le bien ne sera pas supprimé immédiatement. ARD contrôlera d’abord les missions et interventions récurrentes encore prévues.</p>
              </>
            )}
            <div className="detail-actions">
              <button type="button" className="portal-button secondary" onClick={() => setPropertyChange(null)}>Annuler</button>
              <button disabled={busy === "property-change"}>{busy === "property-change" ? "Envoi…" : "Envoyer la demande"}</button>
            </div>
          </form>
        </div>
      )}
      <section className="panel">
        <h2>Demander un devis pour une nouvelle prestation</h2>
        <p className="section-intro">
          Décrivez le besoin et choisissez le bien concerné. Le prix et le mode
          de facturation seront définis par l’administration. La durée prévue
          reste une donnée d’organisation interne.
        </p>
        <form className="form-grid" onSubmit={requestServiceQuote}>
          <label>
            Bien
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              required
            >
              <option value="">Choisir</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Prestation
            <input
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              placeholder="Ménage de sortie"
              required
            />
          </label>
          <label className="wide">
            Description précise du besoin
            <textarea
              value={serviceDescription}
              onChange={(e) => setServiceDescription(e.target.value)}
              placeholder="Pièces concernées, fréquence souhaitée, contraintes particulières…"
              required
            />
          </label>
          <label className="wide">
            Informations complémentaires
            <textarea
              value={serviceNotes}
              onChange={(e) => setServiceNotes(e.target.value)}
            />
          </label>
          <button disabled={busy === "service"}>Demander le devis</button>
        </form>
        {!!serviceRequests.length && (
          <div className="request-history">
            <h3>Suivi de mes demandes</h3>
            {serviceRequests.map((request) => {
              const property = properties.find(
                  (item) => item.id === request.property_id,
                ),
                quote = (data.quotes || []).find(
                  (item) => item.id === request.quote_id,
                );
              return (
                <article className="quote-request-card" key={request.id}>
                  <div>
                    <span className="eyebrow">{property?.name || "Bien"}</span>
                    <h3>{request.service_name}</h3>
                    <p>{request.requested_description}</p>
                    {request.admin_response && (
                      <small>Réponse : {request.admin_response}</small>
                    )}
                    {request.client_response_reason && (
                      <small>
                        Votre retour : {request.client_response_reason}
                      </small>
                    )}
                  </div>
                  <div className="quote-request-summary">
                    <StatusPill value={request.status} />
                    {quote && (
                      <>
                        <strong>{money(quote.total_inc_vat)} TTC</strong>
                        <small>
                          {quote.number}
                          {quote.valid_until
                            ? ` · valable jusqu’au ${new Date(quote.valid_until).toLocaleDateString("fr-FR")}`
                            : ""}
                        </small>
                      </>
                    )}
                    {request.status === "quoted" && quote && (
                      <div className="row-actions">
                        <button
                          type="button"
                          className="danger compact"
                          disabled={busy === request.id}
                          onClick={() =>
                            setQuoteResponse({
                              requestId: request.id,
                              reason: "",
                              requestRevision: true,
                            })
                          }
                        >
                          Discuter ou refuser
                        </button>
                        <button
                          type="button"
                          className="success compact"
                          disabled={busy === request.id}
                          onClick={() => respondToQuote(request.id, true)}
                        >
                          Accepter le devis
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {message && (
          <p
            className={`notice ${message.includes("envoyée") || message.includes("accepté") || message.includes("rattachée") || message.includes("informée") ? "success" : "error"}`}
          >
            {message}
          </p>
        )}
      </section>
      {quoteResponse && (
        <div
          className="portal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setQuoteResponse(null);
          }}
        >
          <form
            className="portal-detail service-proposal-response-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Expliquer votre réponse au devis"
            onSubmit={(event) => {
              event.preventDefault();
              void respondToQuote(
                quoteResponse.requestId,
                false,
                quoteResponse.reason,
                quoteResponse.requestRevision,
              );
            }}
          >
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Votre retour aide ARD à ajuster le devis</span>
                <h2>Que souhaitez-vous faire ?</h2>
              </div>
              <button type="button" className="icon-close" onClick={() => setQuoteResponse(null)}>×</button>
            </div>
            <div className="proposal-response-choice">
              <label>
                <input
                  type="radio"
                  name="quote-response"
                  checked={quoteResponse.requestRevision}
                  onChange={() => setQuoteResponse((current) => current ? {...current,requestRevision:true} : current)}
                />
                <span>
                  <strong>Je souhaite une nouvelle proposition</strong>
                  <small>ARD pourra revoir le prix, le périmètre ou les conditions.</small>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="quote-response"
                  checked={!quoteResponse.requestRevision}
                  onChange={() => setQuoteResponse((current) => current ? {...current,requestRevision:false} : current)}
                />
                <span>
                  <strong>Je ne souhaite pas poursuivre</strong>
                  <small>Le devis sera clôturé après transmission de votre motif.</small>
                </span>
              </label>
            </div>
            <label className="modal-field">
              Pourquoi ce devis ne vous convient-il pas ?
              <textarea
                required
                minLength={5}
                rows={5}
                value={quoteResponse.reason}
                onChange={(event) => setQuoteResponse((current) => current ? {...current,reason:event.target.value} : current)}
                placeholder="Ex. budget à revoir, périmètre à simplifier, autre date souhaitée…"
              />
            </label>
            <div className="detail-actions">
              <button type="button" className="secondary" onClick={() => setQuoteResponse(null)}>Annuler</button>
              <button
                type="submit"
                disabled={busy === quoteResponse.requestId || quoteResponse.reason.trim().length < 5}
              >
                {busy === quoteResponse.requestId
                  ? "Transmission…"
                  : quoteResponse.requestRevision
                    ? "Demander une nouvelle proposition"
                    : "Confirmer mon refus"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

type FieldPosition={latitude:number;longitude:number;accuracy:number|null};
function currentFieldPosition():Promise<FieldPosition>{
  return new Promise((resolve,reject)=>{
    if(!navigator.geolocation){reject(new Error("La géolocalisation n’est pas disponible sur cet appareil."));return;}
    navigator.geolocation.getCurrentPosition(
      (position)=>resolve({latitude:position.coords.latitude,longitude:position.coords.longitude,accuracy:Number.isFinite(position.coords.accuracy)?position.coords.accuracy:null}),
      ()=>reject(new Error("Autorisez ponctuellement la position pour valider cette étape. ARD Manager ne vous suit pas en continu.")),
      {enableHighAccuracy:true,timeout:15000,maximumAge:0},
    );
  });
}

function requestEmergencyTaskNote(label:string,initialValue:string):Promise<string|null>{
  return new Promise((resolve)=>{
    const dialog=document.createElement("dialog"),form=document.createElement("form"),title=document.createElement("h2"),hint=document.createElement("p"),field=document.createElement("label"),textarea=document.createElement("textarea"),actions=document.createElement("div"),cancel=document.createElement("button"),confirm=document.createElement("button");
    dialog.className="emergency-task-note-dialog";
    dialog.setAttribute("aria-labelledby","emergency-task-note-title");
    form.method="dialog";
    title.id="emergency-task-note-title";
    title.textContent="Observation de l’étape";
    hint.textContent=label;
    field.textContent="Ce qui a été constaté ou réalisé";
    textarea.value=initialValue;
    textarea.required=true;
    textarea.minLength=3;
    textarea.rows=5;
    cancel.type="button";
    cancel.className="secondary";
    cancel.textContent="Annuler";
    confirm.type="submit";
    confirm.textContent="Valider l’étape";
    actions.className="detail-actions";
    field.append(textarea);
    actions.append(cancel,confirm);
    form.append(title,hint,field,actions);
    dialog.append(form);
    document.body.append(dialog);
    let settled=false;
    const finish=(value:string|null)=>{if(settled)return;settled=true;dialog.close();dialog.remove();resolve(value);};
    cancel.addEventListener("click",()=>finish(null));
    dialog.addEventListener("cancel",(event)=>{event.preventDefault();finish(null);});
    form.addEventListener("submit",(event)=>{event.preventDefault();const note=textarea.value.trim();if(note.length<3){textarea.setCustomValidity("Ajoutez une observation précise.");textarea.reportValidity();return;}finish(note);});
    textarea.addEventListener("input",()=>textarea.setCustomValidity(""));
    dialog.showModal();
    textarea.focus();
  });
}

function WorkerEmergencyPanel({ profile, worker, data }: { profile: PortalProfile; worker: PortalWorker | null; data: PortalData }) {
  const [available,setAvailable]=useState(true),[fieldAvailable,setFieldAvailable]=useState(true),[canReport,setCanReport]=useState(false),[rows,setRows]=useState<any[]>([]),[rotation,setRotation]=useState<any|null>(null),[interventions,setInterventions]=useState<any[]>([]),[tasks,setTasks]=useState<any[]>([]),[evidence,setEvidence]=useState<any[]>([]),[message,setMessage]=useState(""),[busy,setBusy]=useState(false),
    [taskNotes,setTaskNotes]=useState<Record<string,string>>({}),
    [report,setReport]=useState<{missionId:string;title:string;description:string;severity:string}|null>(null),
    [action,setAction]=useState<{row:any;kind:"escalate"|"resolve";note:string;cause:string}|null>(null),
    [departure,setDeparture]=useState<{row:any;originKind:string;originAddress:string}|null>(null),
    [completion,setCompletion]=useState<{intervention:any;summary:string;remaining:string;cause:string}|null>(null);
  const identityId=worker?.profile_id||profile.id;
  const load=async()=>{
    const [emergencyResult,rotationResult,interventionResult,taskResult,evidenceResult,capabilityResult]=await Promise.all([
      supabase!.from("operational_emergencies").select("id,mission_id,title,description,severity,status,rotation_id,current_assignee_profile_id,n1_profile_id,n2_profile_id,reported_by,occurred_at,acknowledgement_due_at,acknowledged_at,escalated_at,root_cause,recurrence_key,resolved_at,site_address,site_contact_name,site_contact_phone,access_instructions,expected_action,equipment_notes").order("occurred_at",{ascending:false}),
      supabase!.rpc("list_emergency_on_call_rotations_safe"),
      supabase!.from("operational_emergency_interventions").select("id,emergency_id,assignee_profile_id,origin_kind,origin_address,destination_address,status,departed_at,arrived_at,site_departed_at,return_arrived_at,actual_minutes,employee_payable_minutes,completed_at").order("created_at",{ascending:false}),
      supabase!.from("operational_emergency_intervention_tasks").select("id,intervention_id,label,position,is_required,minimum_photo_count,completed_at,notes").order("position"),
      supabase!.from("operational_emergency_intervention_evidence").select("id,intervention_id,task_id,stage,original_name,captured_at").order("captured_at"),
      supabase!.rpc("get_my_operational_emergency_reporting_capability"),
    ]),error=emergencyResult.error||rotationResult.error;
    if(error&&/schema cache|does not exist|could not find|PGRST205|42P01/i.test(error.message)){setAvailable(false);return;}
    if(error){setMessage(error.message);return;}
    const now=new Date();
    setRows((emergencyResult.data||[]).filter((item:any)=>[item.reported_by,item.current_assignee_profile_id,item.n1_profile_id,item.n2_profile_id].includes(identityId)));
    setRotation((rotationResult.data||[]).find((item:any)=>new Date(item.starts_at)<=now&&new Date(item.ends_at)>now)||null);
    setCanReport(!capabilityResult.error&&capabilityResult.data===true);
    const fieldError=interventionResult.error||taskResult.error||evidenceResult.error;
    if(fieldError&&/schema cache|does not exist|could not find|PGRST205|42P01/i.test(fieldError.message||"")){setFieldAvailable(false);setInterventions([]);setTasks([]);setEvidence([]);}
    else if(fieldError)setMessage(fieldError.message);
    else {setFieldAvailable(true);setInterventions(interventionResult.data||[]);setTasks(taskResult.data||[]);setEvidence(evidenceResult.data||[]);}
  };
  useEffect(()=>{void load();},[identityId]);
  useEffect(()=>{
    if(!departure)return;
    const address=departureAddress(departure.row,departure.originKind);
    setDeparture((current)=>current?{...current,originAddress:address}:current);
  },[departure?.originKind,departure?.row?.id]);
  useEffect(()=>{
    if(!completion||completion.summary)return;
    const summary=tasks.filter((item)=>item.intervention_id===completion.intervention.id&&item.completed_at).sort((left,right)=>left.position-right.position).map((item)=>item.notes?`${item.label} : ${item.notes}`:item.label).join("\n");
    if(summary)setCompletion((current)=>current?{...current,summary}:current);
  },[completion?.intervention?.id,tasks]);
  if(!available||!worker)return null;
  const assignedMissionIds=new Set(data.assignments.filter((item)=>item.worker_id===worker.id&&item.status!=="cancelled").map((item)=>item.mission_id)),
    missions=data.missions.filter((item)=>assignedMissionIds.has(item.id)&&item.status!=="cancelled"),
    openRows=rows.filter((item)=>!["resolved","closed"].includes(item.status));
  async function submitReport(event:React.FormEvent){event.preventDefault();if(!report||!canReport)return;const mission=missions.find((item)=>item.id===report.missionId),property=data.properties.find((item)=>item.id===mission?.property_id);setBusy(true);const result=await supabase!.rpc("report_operational_emergency_v3",{p_client_id:mission?.client_id||null,p_property_id:mission?.property_id||null,p_mission_id:report.missionId||null,p_title:report.title,p_description:report.description,p_severity:report.severity,p_emergency_kind:"general",p_occurred_at:new Date().toISOString(),p_recurrence_key:null,p_site_address:property?.address||mission?.location||null,p_site_contact_name:property?.contact_name||null,p_site_contact_phone:null,p_access_instructions:property?.access_notes||null,p_expected_action:null,p_equipment_notes:null});setBusy(false);setMessage(result.error?result.error.message:"Urgence enregistrée par le responsable habilité : l’astreinte N1 est alertée.");if(!result.error){setReport(null);await load();}}
  async function acknowledge(row:any){setBusy(true);const result=await supabase!.rpc("acknowledge_operational_emergency",{p_emergency_id:row.id,p_note:"Prise en charge depuis l’espace terrain."});setBusy(false);setMessage(result.error?result.error.message:"Prise en charge N1 horodatée.");if(!result.error)await load();}
  async function submitAction(event:React.FormEvent){event.preventDefault();if(!action)return;setBusy(true);const result=action.kind==="escalate"?await supabase!.rpc("escalate_operational_emergency",{p_emergency_id:action.row.id,p_reason:action.note}):await supabase!.rpc("resolve_operational_emergency",{p_emergency_id:action.row.id,p_resolution_summary:action.note,p_root_cause:action.cause,p_recurrence_key:action.row.recurrence_key||null,p_next_review_on:null});setBusy(false);setMessage(result.error?result.error.message:action.kind==="escalate"?"Urgence escaladée au N2.":"Urgence résolue et documentée.");if(!result.error){setAction(null);await load();}}
  async function withPosition(action:(position:FieldPosition)=>PromiseLike<any>){setBusy(true);setMessage("");try{const position=await currentFieldPosition(),result=await action(position);setMessage(result.error?result.error.message:"Étape horodatée et ajoutée au compte rendu.");if(!result.error){await load();return true;}return false;}catch(error){setMessage(error instanceof Error?error.message:"Position impossible à obtenir.");return false;}finally{setBusy(false);}}
  function departureAddress(row:any,kind:string){
    if(kind==="home")return worker?.address||"";
    if(kind==="office")return [data.organization?.address,data.organization?.postal_code,data.organization?.city].filter(Boolean).join(" ");
    if(kind==="current_mission"){
      const linked=missions.find((item)=>item.id===row.mission_id),active=missions.find((item)=>["assigned","accepted","in_progress","planned"].includes(item.status));
      const mission=linked||active,property=data.properties.find((item)=>item.id===mission?.property_id);
      return property?.address||mission?.location||"";
    }
    return "";
  }
  async function startDeparture(event:React.FormEvent){event.preventDefault();if(!departure)return;const done=await withPosition((position:FieldPosition)=>supabase!.rpc("start_operational_emergency_intervention",{p_emergency_id:departure.row.id,p_origin_kind:departure.originKind,p_origin_address:departure.originAddress,p_latitude:position.latitude,p_longitude:position.longitude,p_accuracy_m:position.accuracy}));if(done)setDeparture(null);}
  async function markStage(intervention:any,stage:string){await withPosition((position:FieldPosition)=>supabase!.rpc("mark_operational_emergency_intervention_stage",{p_intervention_id:intervention.id,p_stage:stage,p_latitude:position.latitude,p_longitude:position.longitude,p_accuracy_m:position.accuracy}));}
  async function toggleTask(task:any){if(task.completed_at||busy)return;const note=await requestEmergencyTaskNote(task.label,taskNotes[task.id]??task.notes??"");if(note===null)return;setTaskNotes((current)=>({...current,[task.id]:note}));setBusy(true);const result=await supabase!.rpc("set_operational_emergency_intervention_task",{p_task_id:task.id,p_completed:true,p_notes:note});setBusy(false);setMessage(result.error?result.error.message:"Étape validée et ajoutée au compte rendu.");if(!result.error)await load();}
  async function uploadTaskEvidence(intervention:any,task:any,file:File){if(!["image/jpeg","image/png","image/webp"].includes(file.type)||!file.size||file.size>10*1024*1024){setMessage("La preuve doit être une photo JPEG, PNG ou WebP de 10 Mo maximum.");return;}setBusy(true);try{const position=await currentFieldPosition(),safe=file.name.normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-zA-Z0-9._-]/g,"-"),path=`${profile.organization_id}/${profile.id}/emergencies/${intervention.id}/${task.id}/${Date.now()}-${safe}`,stored=await supabase!.storage.from("mission-evidence").upload(path,file,{upsert:false});if(stored.error){setMessage(stored.error.message);return;}const linked=await supabase!.rpc("register_operational_emergency_evidence_v2",{p_intervention_id:intervention.id,p_task_id:task.id,p_stage:task.position===3?"site_departure":"work",p_storage_path:path,p_original_name:file.name,p_mime_type:file.type,p_size_bytes:file.size,p_latitude:position.latitude,p_longitude:position.longitude,p_accuracy_m:position.accuracy});if(linked.error){await supabase!.storage.from("mission-evidence").remove([path]);setMessage(linked.error.message);}else{setMessage(`Photo ajoutée à l’étape « ${task.label} » du compte rendu.`);await load();}}catch(error){setMessage(error instanceof Error?error.message:"Photo impossible à enregistrer.");}finally{setBusy(false);}}
  async function uploadSiteDeparture(intervention:any,file:File){const interventionTasks=tasks.filter((item)=>item.intervention_id===intervention.id).sort((a,b)=>a.position-b.position),target=interventionTasks.find((item)=>!item.completed_at)||interventionTasks[interventionTasks.length-1];if(!target){setMessage("Aucune étape active à documenter.");return;}await uploadTaskEvidence(intervention,target,file);}
  async function submitCompletion(event:React.FormEvent){event.preventDefault();if(!completion)return;setBusy(true);const result=await supabase!.rpc("complete_operational_emergency_intervention",{p_intervention_id:completion.intervention.id,p_action_summary:completion.summary,p_remaining_actions:completion.remaining||null,p_root_cause:completion.cause});setBusy(false);setMessage(result.error?result.error.message:`Intervention clôturée : ${result.data?.employee_payable_minutes||result.data?.actual_minutes||"durée"} minutes réelles transmises pour la paie.`);if(!result.error){setCompletion(null);await load();}}
  return <section className="panel worker-emergency-panel" data-testid="worker-emergency-panel"><div className="section-heading"><div><span className="eyebrow">Sécurité et continuité</span><h2>Urgences opérationnelles</h2><p className="section-intro">{canReport?"Vous êtes habilité à décider du déclenchement d’une urgence. Les interventions confiées restent guidées et tracées.":"En cas d’incident, appelez votre responsable. Lui seul décide si l’astreinte doit être déclenchée."}</p></div>{canReport&&<button type="button" onClick={()=>setReport({missionId:missions[0]?.id||"",title:"",description:"",severity:"moderate"})}>Déclarer une urgence</button>}</div><p className="emergency-privacy-note"><strong>Pas de suivi continu.</strong> Votre position est enregistrée uniquement lorsque vous appuyez sur « Je pars », « Je suis arrivé », « Je quitte les lieux » ou « Je suis de retour ». Elle sert à sécuriser votre compte rendu et à calculer votre temps réel.</p>{rotation&&<div className="worker-on-call"><span>Astreinte en cours</span><strong>{rotation.name}</strong><small>{canReport?"Après déclaration tracée, le dossier alerte automatiquement le bon niveau d’astreinte.":"Pour signaler un incident, contactez votre responsable habituel. Le numéro privé de l’astreinte n’est pas affiché."}</small></div>}{openRows.map((row)=>{const intervention=interventions.find((item)=>item.emergency_id===row.id),interventionTasks=tasks.filter((item)=>item.intervention_id===intervention?.id),departureEvidence=evidence.filter((item)=>item.intervention_id===intervention?.id&&item.stage==="site_departure"),isAssignee=(row.current_assignee_profile_id||row.n1_profile_id)===identityId;return <article className="worker-emergency-row worker-emergency-journey" key={row.id}><div className="worker-emergency-brief"><strong>{row.title}</strong><small>{row.severity} · {row.status}</small>{row.site_address&&<span>📍 {row.site_address}</span>}{row.site_contact_name&&<span>Contact : {row.site_contact_name}{row.site_contact_phone?` · ${row.site_contact_phone}`:""}</span>}{row.expected_action&&<span><b>Attendu :</b> {row.expected_action}</span>}{row.access_instructions&&<span><b>Accès :</b> {row.access_instructions}</span>}{row.equipment_notes&&<span><b>À prévoir :</b> {row.equipment_notes}</span>}{row.site_address&&<a className="button-link compact" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(row.site_address)}`} target="_blank" rel="noreferrer">Ouvrir l’itinéraire Google Maps</a>}{rotation?.contact_phone&&row.reported_by===identityId&&row.rotation_id===rotation.id&&<a className="worker-emergency-call" href={`tel:${rotation.contact_phone}`}>Appeler le N1 pour cette urgence</a>}</div><div className="row-actions">{row.status==="reported"&&[row.n1_profile_id,row.current_assignee_profile_id,rotation?.backup_profile_id].includes(identityId)&&<button type="button" disabled={busy} onClick={()=>void acknowledge(row)}>Prendre en charge</button>}{["reported","acknowledged_n1"].includes(row.status)&&row.n1_profile_id===identityId&&<button type="button" className="secondary" onClick={()=>setAction({row,kind:"escalate",note:"",cause:""})}>Escalader N2</button>}{fieldAvailable&&isAssignee&&["acknowledged_n1","escalated_n2"].includes(row.status)&&!intervention&&<button type="button" onClick={()=>setDeparture({row,originKind:"current_mission",originAddress:""})}>Je pars maintenant</button>}{!fieldAvailable&&["acknowledged_n1","escalated_n2"].includes(row.status)&&[row.n1_profile_id,row.n2_profile_id].includes(identityId)&&<button type="button" onClick={()=>setAction({row,kind:"resolve",note:"",cause:""})}>Résoudre</button>}</div>{intervention&&<div className="emergency-journey"><div className="emergency-journey-status"><span className={intervention.departed_at?"done":""}>Départ</span><span className={intervention.arrived_at?"done":""}>Sur place</span><span className={intervention.site_departed_at?"done":""}>Départ des lieux</span><span className={intervention.return_arrived_at?"done":""}>Retour</span></div>{intervention.status==="en_route"&&<button type="button" disabled={busy} onClick={()=>void markStage(intervention,"arrival")}>Je suis arrivé sur les lieux</button>}{intervention.status==="on_site"&&<><div className="emergency-task-list">{interventionTasks.map((task)=><label key={task.id}><input type="checkbox" checked={!!task.completed_at} disabled={busy} onChange={()=>void toggleTask(task)}/><span>{task.label}{task.is_required&&<small>Obligatoire</small>}</span></label>)}</div><label className="emergency-photo-action"><span>Photo horodatée de départ des lieux</span><input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" disabled={busy} onChange={(event)=>{const file=event.target.files?.[0];if(file)void uploadSiteDeparture(intervention,file);event.currentTarget.value="";}}/><small>{departureEvidence.length?`${departureEvidence.length} photo(s) ajoutée(s)`:"Obligatoire avant de quitter les lieux"}</small></label><button type="button" disabled={busy||!departureEvidence.length||interventionTasks.some((task)=>task.is_required&&!task.completed_at)} onClick={()=>void markStage(intervention,"site_departure")}>Je quitte les lieux</button></>}{intervention.status==="returning"&&!intervention.return_arrived_at&&<button type="button" disabled={busy} onClick={()=>void markStage(intervention,"return_arrival")}>Je suis de retour au point de départ</button>}{intervention.status==="returning"&&intervention.return_arrived_at&&<button type="button" onClick={()=>setCompletion({intervention,summary:"",remaining:"",cause:""})}>Finaliser mon compte rendu</button>}</div>}</article>;})}{message&&<p className={`notice ${/ajoutée|horodatée|escaladée|clôturée|Étape/.test(message)?"success":"error"}`}>{message}</p>}{canReport&&report&&<div className="portal-overlay"><form className="portal-detail" role="dialog" aria-modal="true" aria-label="Déclarer une urgence opérationnelle" onSubmit={submitReport}><div className="portal-detail-head"><h2>Déclarer une urgence</h2><button type="button" className="icon-close" onClick={()=>setReport(null)}>×</button></div><div className="form-grid"><label className="wide">Mission<select value={report.missionId} onChange={(event)=>setReport({...report,missionId:event.target.value})}><option value="">Sans mission</option>{missions.map((mission)=><option key={mission.id} value={mission.id}>{mission.client?.name||mission.client_display_name||mission.location}</option>)}</select></label><label className="wide">Titre<input required minLength={3} value={report.title} onChange={(event)=>setReport({...report,title:event.target.value})}/></label><label>Gravité<select value={report.severity} onChange={(event)=>setReport({...report,severity:event.target.value})}><option value="low">Faible</option><option value="moderate">Modérée</option><option value="high">Élevée</option><option value="critical">Critique</option></select></label><label className="wide">Description<textarea required minLength={5} rows={5} value={report.description} onChange={(event)=>setReport({...report,description:event.target.value})}/></label></div><p className="notice">Cette habilitation permet de décider du déclenchement. La déclaration crée une trace et alerte le niveau d’astreinte prévu.</p><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setReport(null)}>Annuler</button><button disabled={busy}>{busy?"Transmission…":"Déclarer et alerter le N1"}</button></div></form></div>}{departure&&<div className="portal-overlay"><form className="portal-detail" role="dialog" aria-modal="true" aria-label="Démarrer l’intervention d’urgence" onSubmit={startDeparture}><div className="portal-detail-head"><div><span className="eyebrow">Compte rendu guidé</span><h2>Je pars maintenant</h2></div><button type="button" className="icon-close" onClick={()=>setDeparture(null)}>×</button></div><p>Cette validation enregistre ponctuellement l’heure et votre position de départ. Aucun suivi continu n’est activé.</p><div className="form-grid"><label>Lieu de départ<select value={departure.originKind} onChange={(event)=>setDeparture({...departure,originKind:event.target.value})}><option value="current_mission">Chantier en cours</option><option value="home">Domicile</option><option value="office">Siège / dépôt</option><option value="other">Autre</option></select></label><label className="wide">Adresse de départ<input required minLength={3} value={departure.originAddress} onChange={(event)=>setDeparture({...departure,originAddress:event.target.value})}/></label></div><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setDeparture(null)}>Annuler</button><button disabled={busy}>{busy?"Localisation…":"Valider mon départ"}</button></div></form></div>}{completion&&<div className="portal-overlay"><form className="portal-detail" role="dialog" aria-modal="true" aria-label="Finaliser le compte rendu d’urgence" onSubmit={submitCompletion}><div className="portal-detail-head"><div><span className="eyebrow">Retour enregistré</span><h2>Finaliser mon compte rendu</h2></div><button type="button" className="icon-close" onClick={()=>setCompletion(null)}>×</button></div><p>Votre durée réelle est calculée automatiquement entre le départ et le retour. Vous ne saisissez aucune durée manuellement.</p><div className="form-grid"><label className="wide">Actions réalisées<textarea required minLength={10} rows={4} value={completion.summary} onChange={(event)=>setCompletion({...completion,summary:event.target.value})}/></label><label className="wide">Actions restant à mener<textarea rows={3} value={completion.remaining} onChange={(event)=>setCompletion({...completion,remaining:event.target.value})}/></label><label className="wide">Cause constatée<input required minLength={3} value={completion.cause} onChange={(event)=>setCompletion({...completion,cause:event.target.value})}/></label></div><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setCompletion(null)}>Annuler</button><button disabled={busy}>{busy?"Clôture…":"Clôturer et transmettre"}</button></div></form></div>}{action&&<div className="portal-overlay"><form className="portal-detail" role="dialog" aria-modal="true" aria-label={action.kind==="escalate"?"Escalader l’urgence au N2":"Résoudre l’urgence terrain"} onSubmit={submitAction}><div className="portal-detail-head"><h2>{action.kind==="escalate"?"Escalader au N2":"Résoudre l’urgence"}</h2><button type="button" className="icon-close" onClick={()=>setAction(null)}>×</button></div><div className="form-grid"><label className="wide">{action.kind==="escalate"?"Motif":"Résolution"}<textarea required minLength={5} value={action.note} onChange={(event)=>setAction({...action,note:event.target.value})}/></label>{action.kind==="resolve"&&<label className="wide">Cause racine<input required minLength={3} value={action.cause} onChange={(event)=>setAction({...action,cause:event.target.value})}/></label>}</div><div className="detail-actions"><button type="button" className="secondary" onClick={()=>setAction(null)}>Annuler</button><button disabled={busy}>{busy?"Enregistrement…":"Confirmer"}</button></div></form></div>}</section>;
}

export function WorkerDashboardRich({
  profile,
  worker,
  data,
  reload,
  onNavigate,
}: {
  profile: PortalProfile;
  worker: PortalWorker | null;
  data: PortalData;
  reload: () => void;
  onNavigate: (
    target: "missions" | "documents" | "compensation" | "agenda" | "journey",
  ) => void;
}) {
  const [selected, setSelected] = useState<PortalMission | null>(null),
    [completion, setCompletion] = useState<PortalMission | null>(null),
    [message, setMessage] = useState(""),
    headquarters = [
      data.organization?.address,
      data.organization?.postal_code,
      data.organization?.city,
    ]
      .filter(Boolean)
      .join(" "),
    assignments = data.assignments.filter(
      (a) =>
        a.worker_id === worker?.id &&
        !["cancelled", "refused"].includes(a.status),
    ),
    assigned = new Map(assignments.map((a) => [a.mission_id, a])),
    reports = new Map(data.reports.map((r) => [r.mission_id, r])),
    todayKey = dayKey(new Date()),
    upcoming = data.missions.filter((m) => {
      const assignment = assigned.get(m.id);
      return (
        missionDay(m) >= todayKey &&
        m.status !== "cancelled" &&
        !!assignment &&
        ["accepted", "assigned"].includes(assignment.status)
      );
    }),
    today = upcoming.filter((m) => missionDay(m) === todayKey),
    hours = upcoming.reduce(
      (sum, mission) =>
        sum +
        (assigned.get(mission.id)?.assigned_hours ||
          mission.planned_hours ||
          0),
      0,
    ),
    offers = assignments.filter((a) => a.status === "proposed").length,
    missing = data.requirements.filter((r) =>
      ["required", "rejected", "expired"].includes(r.status),
    ).length,
    currentPeriod = parisCalendarMonth(),
    monthAssignments = assignments
      .filter((a) => ["accepted", "assigned"].includes(a.status))
      .filter((a) => {
        const m = data.missions.find((x) => x.id === a.mission_id);
        return (
          !!m &&
          m.ends_at.slice(0, 7) === currentPeriod &&
          m.status !== "cancelled" &&
          (new Date(m.ends_at) <= new Date() || !!reports.get(m.id))
        );
      }),
    monthHours = monthAssignments.reduce((sum, a) => {
      const m = data.missions.find((x) => x.id === a.mission_id);
      return (
        sum +
        (reports.get(a.mission_id)?.actual_hours ||
          a.assigned_hours ||
          m?.planned_hours ||
          0)
      );
    }, 0),
    monthAmount = contractorMonthRevenue(
      data,
      worker,
      Number(currentPeriod.slice(0, 4)),
      Number(currentPeriod.slice(5, 7)) - 1,
    ),
    employeeWorkedHours = (data.times || [])
      .filter(
        (entry) =>
          entry.worker_id === worker?.id &&
          entry.work_date.slice(0, 7) === currentPeriod &&
          entry.status !== "rejected",
      )
      .reduce((sum, entry) => sum + Number(entry.worked_minutes || 0) / 60, 0),
    employeeTarget = Number(worker?.monthly_hours || 0),
    employeeProgress = employeeTarget
      ? Math.min(100, (employeeWorkedHours / employeeTarget) * 100)
      : 0,
    qualityMetric = (data.workerQualityMetrics || []).find(
      (metric) => metric.worker_id === worker?.id,
    );
  const missionChecklistAvailable = !(data.unavailableCapabilities || []).includes("mission_checklists");
  const prepareMissionCompletion = async (mission: PortalMission) => {
    if (missionChecklistAvailable) {
      const started = await supabase!.rpc("begin_mission", { p_mission_id: mission.id });
      if (started.error) {
        setMessage(started.error.message);
        return;
      }
    }
    setCompletion(mission);
    setSelected(null);
  };
  return (
    <>
      <div className="portal-hero worker">
        <div>
          <span className="eyebrow">Espace terrain</span>
          <h1>Bonjour {worker?.first_name || ""}</h1>
          <p>Votre journée, vos consignes et vos comptes rendus.</p>
        </div>
        <div className="worker-next">
          <span>Aujourd’hui</span>
          <strong>{today.length} mission(s)</strong>
          <small>{worker?.kind === "employee"
            ? `${today.reduce((s, m) => s + (assigned.get(m.id)?.assigned_hours || m.planned_hours || 0), 0)} h d’objectif`
            : "Planning opérationnel publié"}</small>
        </div>
      </div>
      {(offers > 0 || missing > 0) && (
        <div className="portal-alerts">
          {offers > 0 && (
            <div>
              🔔 <strong>{offers} mission(s) à accepter ou refuser</strong>
            </div>
          )}
          {missing > 0 && (
            <div>
              📄 <strong>{missing} document(s) attendu(s)</strong>
            </div>
          )}
        </div>
      )}
      <WorkerOfflineDay
        profileId={profile.id}
        role={profile.role === "contractor" ? "contractor" : "employee"}
        workDate={todayKey}
        onlineMissionCount={today.length}
      />
      <WorkerEmergencyPanel profile={profile} worker={worker} data={data} />
      <MyOperatingProcedures profile={profile} />
      <div className="stats portal-stats">
        {worker?.kind === "employee" ? (
          <button
            type="button"
            className="earning-card portal-stat-link"
            onClick={() => onNavigate("agenda")}
          >
            <span>Objectif contractuel du mois</span>
            <strong>{formatDurationHours(employeeWorkedHours)} / {formatDurationHours(employeeTarget)}</strong>
            <small>{employeeProgress.toFixed(1)} % réalisé · voir mon agenda →</small>
          </button>
        ) : (
          <button
            type="button"
            className="earning-card portal-stat-link"
            onClick={() => onNavigate("compensation")}
          >
            <span>Ce mois-ci</span>
            <strong>{money(monthAmount)} HT</strong>
            <small>
              {formatDurationHours(monthHours)} réalisées · voir le détail à facturer →
            </small>
          </button>
        )}
        <button type="button" className="portal-stat-link" onClick={() => onNavigate("missions")}>
          <span>Missions à venir</span>
          <strong>{upcoming.length}</strong>
          <small>Ouvrir mes missions →</small>
        </button>
        {worker?.kind === "employee" ? (
          <button type="button" className="portal-stat-link" onClick={() => onNavigate("missions")}>
            <span>Heures affectées</span>
            <strong>{formatDurationHours(hours)}</strong>
            <small>Voir le planning associé →</small>
          </button>
        ) : (
          <button type="button" className="portal-stat-link" onClick={() => onNavigate("missions")}>
            <span>Missions acceptées</span>
            <strong>{upcoming.length}</strong>
            <small>Consulter les consignes →</small>
          </button>
        )}
        <button type="button" className="portal-stat-link" onClick={() => onNavigate("missions")}>
          <span>Comptes rendus à faire</span>
          <strong>
            {
              data.missions.filter(
                (m) =>
                  new Date(m.ends_at) < new Date() &&
                  !reports.has(m.id) &&
                  m.status !== "cancelled",
              ).length
            }
          </strong>
          <small>Clôturer une mission →</small>
        </button>
        <button type="button" className="portal-stat-link" onClick={() => onNavigate("documents")}>
          <span>Documents déposés</span>
          <strong>{data.documents.length}</strong>
          <small>Consulter mes documents →</small>
        </button>
      </div>
      {qualityMetric && (
        <section className="panel worker-quality-summary" data-testid="worker-quality-summary">
          <div className="section-heading">
            <div><span className="eyebrow">Qualité vérifiée · année en cours</span><h2>Suivi de mes interventions</h2></div>
            <strong>{Number(qualityMetric.verified_return_rate).toLocaleString("fr-FR",{maximumFractionDigits:1})} %</strong>
          </div>
          <div className="detail-grid compact-grid">
            <div><span>Missions terminées</span><strong>{qualityMetric.completed_mission_count}</strong></div>
            <div><span>Retours contrôlés</span><strong>{qualityMetric.reviewed_claim_count}</strong></div>
            <div><span>Retours fondés</span><strong>{qualityMetric.founded_claim_count}</strong></div>
            <div><span>Retouches suivies</span><strong>{qualityMetric.touchup_count}</strong></div>
          </div>
          <small>Les déclarations client non contrôlées ne sont pas comptabilisées. Une éventuelle règle de prime restera distincte, versionnée et validée par l’administrateur.</small>
          {worker?.kind === "employee" && <button type="button" className="secondary compact" onClick={() => onNavigate("journey")}>Voir mon parcours</button>}
        </section>
      )}
      {worker?.kind === "contractor" && (
        <ContractorAnnualRevenueChart
          data={data}
          worker={worker}
          year={new Date().getFullYear()}
          compact
        />
      )}
      {message && <p className="notice success">{message}</p>}
      <RouteToday
        missions={data.missions}
        headquarters={headquarters}
        onSelect={setSelected}
      />
      <AgendaWeek missions={upcoming} onSelect={setSelected} properties={data.properties} />
      {selected && (
        <MissionDetail
          mission={selected}
          mode="worker"
          showTimeObjectives={worker?.kind === "employee"}
          property={data.properties.find((item) => item.id === selected.property_id)}
          serviceLines={(data.missionServiceLines || []).filter(
            (item) => item.mission_id === selected.id,
          )}
          assignment={assigned.get(selected.id)}
          report={reports.get(selected.id)}
          onComplete={
            (!reports.get(selected.id) ||
              reports.get(selected.id)?.submitted_by_worker_id !== worker?.id) &&
            ["accepted", "assigned"].includes(
              assigned.get(selected.id)?.status || "",
            ) &&
            !["cancelled", "completed"].includes(selected.status)
              ? () => prepareMissionCompletion(selected)
              : undefined
          }
          onClose={() => setSelected(null)}
        />
      )}
      {completion && worker && (
        <MissionCompletionDialog
          profile={profile}
          worker={worker}
          mission={completion}
          assignment={assigned.get(completion.id)}
          inventoryItems={data.inventoryItems || []}
          team={routeTeamForMission(data, worker, completion)}
          teamMembers={data.dailyRouteTeamMembers || []}
          workers={data.workers}
          assignments={data.assignments}
          teamContext={data.routeTeamContext || []}
          activityLearningAvailable={!(data.unavailableCapabilities || []).includes("mission_activity_learning")}
          missionChecklistAvailable={missionChecklistAvailable}
          reload={reload}
          onClose={() => setCompletion(null)}
          onCompleted={setMessage}
        />
      )}
    </>
  );
}

export function WorkerMissionsRich({
  profile,
  worker,
  data,
  reload,
}: {
  profile: PortalProfile;
  worker: PortalWorker | null;
  data: PortalData;
  reload: () => void;
}) {
  const [selected, setSelected] = useState<PortalMission | null>(null),
    [completion, setCompletion] = useState<PortalMission | null>(null),
    [cancellation, setCancellation] = useState<PortalMission | null>(null),
    headquarters = [
      data.organization?.address,
      data.organization?.postal_code,
      data.organization?.city,
    ]
      .filter(Boolean)
      .join(" "),
    [message, setMessage] = useState("");
  const assignments = data.assignments.filter(
      (a) => a.worker_id === worker?.id && a.status !== "cancelled",
    ),
    activeAssignments = assignments.filter((a) => a.status !== "refused"),
    assigned = new Map(activeAssignments.map((a) => [a.mission_id, a])),
    reports = new Map(data.reports.map((r) => [r.mission_id, r])),
    pendingCancellations = new Set((data.workerCancellationNotices || []).filter((notice)=>notice.status==="submitted").map((notice)=>notice.assignment_id)),
    offers = assignments.filter((a) => a.status === "proposed"),
    missionChecklistAvailable = !(data.unavailableCapabilities || []).includes("mission_checklists");
  async function prepareMissionCompletion(mission: PortalMission) {
    if (missionChecklistAvailable) {
      const started = await supabase!.rpc("begin_mission", { p_mission_id: mission.id });
      if (started.error) {
        setMessage(started.error.message);
        return;
      }
    }
    setCompletion(mission);
    setSelected(null);
  }
  async function respond(
    assignment: PortalAssignment,
    status: "accepted" | "refused",
  ) {
    const note =
      status === "refused"
        ? prompt("Vous pouvez préciser la raison de votre refus :") || null
        : null;
    const { error } = await supabase!
      .from("mission_assignments")
      .update({ status, response_notes: note })
      .eq("id", assignment.id);
    setMessage(
      error
        ? error.message
        : status === "accepted"
          ? "Mission acceptée. Elle est ajoutée à votre tournée."
          : "Mission refusée. L’administration doit maintenant décider d’une réaffectation ou de l’annulation.",
    );
    if (!error) reload();
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Terrain</span>
          <h1>Mes missions</h1>
        </div>
      </div>
      {offers.length > 0 && (
        <section className="panel offer-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Réponse attendue</span>
              <h2>Missions proposées</h2>
            </div>
            <strong>{offers.length}</strong>
          </div>
          {offers.map((a) => {
            const m = data.missions.find((item) => item.id === a.mission_id),
              property = data.properties.find((item) => item.id === m?.property_id),
              services = m ? missionServiceSummary(m, data.missionServiceLines || []) : "";
            return m ? (
              <div className="offer-row" key={a.id}>
                <button className="offer-detail" onClick={() => setSelected(m)}>
                  <span>
                    <strong>{dateTime(m.starts_at)}</strong>
                    <small>
                      {property?.name || missionClient(m)}
                      {services ? ` · ${services}` : ""}
                    </small>
                    <small>{missionClient(m)} · {m.location}</small>
                  </span>
                  <i>Voir ›</i>
                </button>
                <div className="offer-actions">
                  <button
                    className="portal-button"
                    onClick={() => respond(a, "accepted")}
                  >
                    Accepter
                  </button>
                  <button
                    className="portal-button danger"
                    onClick={() => respond(a, "refused")}
                  >
                    Refuser
                  </button>
                </div>
              </div>
            ) : null;
          })}
        </section>
      )}
      <RouteToday
        missions={data.missions.filter((m) =>
          ["accepted", "assigned"].includes(assigned.get(m.id)?.status || ""),
        )}
        headquarters={headquarters}
        onSelect={setSelected}
      />
      <AgendaWeek
        missions={data.missions.filter(
          (m) =>
            m.status !== "cancelled" &&
            ["accepted", "assigned"].includes(assigned.get(m.id)?.status || ""),
        )}
        onSelect={setSelected}
        properties={data.properties}
      />
      <section className="panel">
        <h2>Toutes mes missions acceptées</h2>
        <MissionRows
          missions={data.missions.filter((m) =>
            ["accepted", "assigned"].includes(assigned.get(m.id)?.status || ""),
          )}
          onSelect={setSelected}
          mode="worker"
          properties={data.properties}
          serviceLines={data.missionServiceLines || []}
        />
      </section>
      {message && (
        <p className={`notice ${message.includes("transmise") || message.includes("acceptée") || message.includes("refusée") ? "success" : "error"}`}>
          {message}
        </p>
      )}
      {selected && (
        <MissionDetail
          mission={selected}
          mode="worker"
          showTimeObjectives={worker?.kind === "employee"}
          property={data.properties.find((item) => item.id === selected.property_id)}
          serviceLines={(data.missionServiceLines || []).filter(
            (item) => item.mission_id === selected.id,
          )}
          assignment={assigned.get(selected.id)}
          report={reports.get(selected.id)}
          onComplete={
            (!reports.get(selected.id) ||
              reports.get(selected.id)?.submitted_by_worker_id !== worker?.id) &&
            ["accepted", "assigned"].includes(
              assigned.get(selected.id)?.status || "",
            ) &&
            !["cancelled", "completed"].includes(selected.status)
              ? () => prepareMissionCompletion(selected)
              : undefined
          }
          onCancel={
            new Date(selected.starts_at)>new Date() && ["accepted","assigned"].includes(assigned.get(selected.id)?.status||"")
              ? ()=>{setCancellation(selected);setSelected(null);}
              : undefined
          }
          cancellationPending={pendingCancellations.has(assigned.get(selected.id)?.id||"")}
          onClose={() => setSelected(null)}
        />
      )}
      {cancellation && worker && assigned.get(cancellation.id) && (
        <WorkerCancellationDialog
          mission={cancellation}
          assignment={assigned.get(cancellation.id)!}
          rule={(data.workerCancellationRules || []).find((item)=>item.worker_id===worker.id&&item.enabled)}
          documents={data.documents}
          reload={reload}
          onClose={()=>setCancellation(null)}
          onSubmitted={(text)=>{setMessage(text);setCancellation(null);}}
        />
      )}
      {completion && worker && (
        <MissionCompletionDialog
          profile={profile}
          worker={worker}
          mission={completion}
          assignment={assigned.get(completion.id)}
          inventoryItems={data.inventoryItems || []}
          team={routeTeamForMission(data, worker, completion)}
          teamMembers={data.dailyRouteTeamMembers || []}
          workers={data.workers}
          assignments={data.assignments}
          teamContext={data.routeTeamContext || []}
          activityLearningAvailable={!(data.unavailableCapabilities || []).includes("mission_activity_learning")}
          missionChecklistAvailable={missionChecklistAvailable}
          reload={reload}
          onClose={() => setCompletion(null)}
          onCompleted={setMessage}
        />
      )}
    </>
  );
}

export function ContractorBillingRich({
  profile,
  worker,
  data,
  reload,
}: {
  profile: PortalProfile;
  worker: PortalWorker | null;
  data: PortalData;
  reload: () => void;
}) {
  const today = parisCalendarDay(),
    defaultDue = (() => {
      const value = new Date(`${today}T12:00:00`);
      value.setDate(value.getDate() + 30);
      return parisCalendarDay(value);
    })(),
    [period, setPeriod] = useState(parisCalendarMonth()),
    [invoiceOpen, setInvoiceOpen] = useState(false),
    [invoiceNumber, setInvoiceNumber] = useState(""),
    [issueOn, setIssueOn] = useState(today),
    [dueOn, setDueOn] = useState(defaultDue),
    [totalExVat, setTotalExVat] = useState(0),
    [totalVat, setTotalVat] = useState(0),
    [totalIncVat, setTotalIncVat] = useState(0),
    [selectedMissionIds, setSelectedMissionIds] = useState<string[]>([]),
    [invoiceNotes, setInvoiceNotes] = useState(""),
    [invoiceFile, setInvoiceFile] = useState<File | null>(null),
    [invoiceMessage, setInvoiceMessage] = useState(""),
    [invoiceBusy, setInvoiceBusy] = useState(false),
    [statementFrom, setStatementFrom] = useState(`${new Date().getFullYear()}-01-01`),
    [statementTo, setStatementTo] = useState(today),
    [statementMessage, setStatementMessage] = useState("");
  const [henrriStatus, setHenrriStatus] = useState<{
      worker_id: string;
      company_organization_id: string | null;
      company_name: string | null;
      workspace_ready: boolean;
      environment: string;
      status: string;
      account_identifier: string | null;
      provider_name: string | null;
      last_sync_at: string | null;
      last_error: string | null;
    } | null>(null),
    [henrriClientId, setHenrriClientId] = useState(""),
    [henrriClientSecret, setHenrriClientSecret] = useState(""),
    [henrriBusy, setHenrriBusy] = useState(false),
    [henrriMessage, setHenrriMessage] = useState("");
  const supplierInvoicingAvailable = !(
    data.unavailableCapabilities || []
  ).includes("supplier_invoicing");
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!supplierInvoicingAvailable) return;
      const result = await supabase!.rpc("get_my_supplier_henrri_status");
      if (!mounted) return;
      if (result.error) {
        setHenrriMessage(
          /function|schema cache|does not exist/i.test(result.error.message)
            ? "La connexion Henrri fournisseur sera disponible après activation de la migration validée."
            : result.error.message,
        );
        return;
      }
      const row = Array.isArray(result.data) ? result.data[0] : result.data;
      setHenrriStatus(row || null);
    })();
    return () => {
      mounted = false;
    };
  }, [supplierInvoicingAvailable, worker?.id]);
  const reports = new Map(data.reports.map((r) => [r.mission_id, r])),
    missions = new Map(data.missions.map((m) => [m.id, m])),
    rateCards = new Map(
      (data.workerRateCards || []).map((rate) => [rate.id, rate]),
    );
  const eligible = data.assignments
    .filter(
      (a) =>
        a.worker_id === worker?.id &&
        ["accepted", "assigned"].includes(a.status),
    )
    .filter((a) => {
      const m = missions.get(a.mission_id);
      const report = m ? reports.get(m.id) : null;
      return (
        !!m &&
        m.ends_at.slice(0, 7) === period &&
        m.status !== "cancelled" &&
        !!report &&
        ["pending", "approved"].includes(report.status)
      );
    });
  const hourly = eligible
    .filter((a) => (a.pricing_mode || worker?.pricing_mode) === "hourly")
    .map((a) => {
      const m = missions.get(a.mission_id)!,
        worked =
          reports.get(a.mission_id)?.actual_hours ||
          a.assigned_hours ||
          m.planned_hours,
        rate = a.agreed_rate ?? worker?.hourly_rate ?? 0;
      return {
        assignment: a,
        mission: m,
        hours: worked,
        amount: a.agreed_amount ?? worked * rate,
        label: `${worked} h × ${money(rate)}`,
      };
    });
  const fixed = eligible
    .filter((a) =>
      ["fixed", "per_unit"].includes(
        a.pricing_mode || worker?.pricing_mode || "",
      ),
    )
    .map((a) => {
      const m = missions.get(a.mission_id)!,
        rateCard = a.worker_rate_card_id
          ? rateCards.get(a.worker_rate_card_id)
          : null;
      return {
        assignment: a,
        mission: m,
        hours:
          reports.get(a.mission_id)?.actual_hours ||
          a.assigned_hours ||
          m.planned_hours,
        amount: a.agreed_amount ?? rateCard?.rate ?? worker?.fixed_fee ?? 0,
        label:
          rateCard?.name ||
          (a.pricing_mode === "per_unit"
            ? "Prix à l’unité"
            : "Forfait de mission"),
      };
    });
  const monthlyAssignments = eligible.filter(
      (a) => (a.pricing_mode || worker?.pricing_mode) === "monthly",
    ),
    monthlyAmount = monthlyAssignments.length
      ? (worker?.monthly_fee ?? monthlyAssignments[0].agreed_amount ?? 0)
      : 0;
  const lines = [...hourly, ...fixed],
    total = lines.reduce((s, l) => s + l.amount, 0) + monthlyAmount,
    totalHours = eligible.reduce(
      (s, a) =>
        s +
        (reports.get(a.mission_id)?.actual_hours ||
          a.assigned_hours ||
          missions.get(a.mission_id)?.planned_hours ||
          0),
      0,
    );
  const supplierInvoices = (data.supplierInvoices || [])
      .filter((invoice) => invoice.worker_id === worker?.id)
      .sort((a, b) => b.submitted_at.localeCompare(a.submitted_at)),
    activeMissionIds = new Set(
      (data.supplierInvoiceMissions || [])
        .filter((link) =>
          supplierInvoices.some(
            (invoice) =>
              invoice.id === link.invoice_id &&
              ["submitted", "under_review", "approved", "paid"].includes(
                invoice.status,
              ),
          ),
        )
        .map((link) => link.mission_id),
    ),
    invoiceableMissions = eligible
      .map((assignment) => missions.get(assignment.mission_id))
      .filter(
        (mission): mission is PortalMission =>
          !!mission && !activeMissionIds.has(mission.id),
      ),
    servicePeriodStart = `${period}-01`,
    servicePeriodEnd = (() => {
      const [year, month] = period.split("-").map(Number);
      return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
    })();
  function printSupplierStatement() {
    if (!statementFrom || !statementTo || statementFrom > statementTo) {
      setStatementMessage("Choisissez une période valide.");
      return;
    }
    const rows = supplierInvoices.filter(
        (invoice) =>
          invoice.issue_on >= statementFrom && invoice.issue_on <= statementTo,
      ),
      totalExVat = rows.reduce((sum, invoice) => sum + Number(invoice.total_ex_vat || 0), 0),
      totalVat = rows.reduce((sum, invoice) => sum + Number(invoice.total_vat || 0), 0),
      totalIncVat = rows.reduce((sum, invoice) => sum + Number(invoice.total_inc_vat || 0), 0),
      companyName = worker?.business_name || workerName(worker),
      customerName = data.organization?.name || "ARD",
      pricingRows = (data.workerRateCards || []).filter(
        (rate) => rate.worker_id === worker?.id && rate.active,
      ),
      popup = window.open("", "_blank");
    if (!rows.length) {
      setStatementMessage("Aucune facture transmise sur cette période.");
      popup?.close();
      return;
    }
    if (!popup) {
      setStatementMessage("Autorisez l’ouverture de la fenêtre pour créer le PDF.");
      return;
    }
    popup.opener = null;
    popup.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Récapitulatif de facturation ${escapeDocumentText(companyName)}</title><style>@page{size:A4;margin:16mm}*{box-sizing:border-box}body{margin:0;color:#191b20;font:13px Arial,sans-serif}header{display:flex;justify-content:space-between;gap:24px;padding:22px;border-radius:16px;background:#191b20;color:white}header h1{margin:0 0 6px;font-size:24px}header b{color:#c6a15b;letter-spacing:.06em}header p{margin:3px 0;color:#dfe1e5}.period{margin:24px 0 14px}.period h2,.terms h2{margin:0 0 6px}.terms{margin-top:18px;padding:14px;border:1px solid #d8c28f;border-radius:13px;background:#fffdf7;break-inside:avoid}.terms table{margin-top:4px}.terms small{display:block;color:#666;margin-top:2px}table{width:100%;border-collapse:collapse}th,td{padding:10px 8px;border-bottom:1px solid #e2e4e8;text-align:right}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2){text-align:left}thead{background:#f5f1e8}.totals{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:18px}.totals div{padding:14px;border:1px solid #d8c28f;border-radius:12px;background:#fbf8f0}.totals span{display:block;color:#666;font-size:11px}.totals strong{display:block;margin-top:5px;font-size:18px}.notice{margin-top:24px;padding-top:12px;border-top:1px solid #ddd;color:#666;font-size:11px}footer{margin-top:28px;color:#777;font-size:10px;text-align:center}</style></head><body><header><div><b>${escapeDocumentText(companyName)}</b><h1>Récapitulatif de facturation</h1></div><div><p>Activité avec</p><strong>${escapeDocumentText(customerName)}</strong></div></header><section class="period"><h2>Du ${escapeDocumentText(new Date(`${statementFrom}T12:00:00`).toLocaleDateString("fr-FR"))} au ${escapeDocumentText(new Date(`${statementTo}T12:00:00`).toLocaleDateString("fr-FR"))}</h2><p>${rows.length} facture(s) transmise(s)</p></section><table><thead><tr><th>Facture</th><th>Date</th><th>HT</th><th>TVA</th><th>TTC</th></tr></thead><tbody>${rows.map((invoice) => `<tr><td>${escapeDocumentText(invoice.invoice_number)}</td><td>${escapeDocumentText(new Date(`${invoice.issue_on}T12:00:00`).toLocaleDateString("fr-FR"))}</td><td>${escapeDocumentText(money(invoice.total_ex_vat))}</td><td>${escapeDocumentText(money(invoice.total_vat))}</td><td><strong>${escapeDocumentText(money(invoice.total_inc_vat))}</strong></td></tr>`).join("")}</tbody></table><div class="totals"><div><span>Total HT</span><strong>${escapeDocumentText(money(totalExVat))}</strong></div><div><span>Total TVA</span><strong>${escapeDocumentText(money(totalVat))}</strong></div><div><span>Total TTC</span><strong>${escapeDocumentText(money(totalIncVat))}</strong></div></div><section class="terms"><h2>Conditions tarifaires enregistrées</h2><table><thead><tr><th>Condition</th><th>Mode</th><th>Tarif</th></tr></thead><tbody>${pricingRows.map((rate) => `<tr><td><strong>${escapeDocumentText(rate.name)}</strong><small>${escapeDocumentText(rate.notes || "Condition active enregistrée dans la fiche")}</small></td><td>${escapeDocumentText(rate.pricing_mode)}</td><td><strong>${escapeDocumentText(money(rate.rate))} HT${rate.unit_label ? ` / ${escapeDocumentText(rate.unit_label)}` : ""}</strong></td></tr>`).join("") || `<tr><td colspan="3">Aucune condition tarifaire active enregistrée.</td></tr>`}</tbody></table></section><p class="notice">Ce document est un récapitulatif informatif. Les forfaits sont repris au montant convenu et ne varient pas avec le temps passé. Les factures fournisseurs originales restent les pièces comptables de référence.</p><footer>Document préparé depuis ARD Manager · ${escapeDocumentText(new Date().toLocaleDateString("fr-FR"))}</footer><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));<\/script></body></html>`);
    popup.document.close();
    setStatementMessage("Récapitulatif ouvert : choisissez « Enregistrer au format PDF » dans la fenêtre d’impression.");
  }
  async function openInvoiceDocument(invoice: PortalSupplierInvoice) {
    const document = data.documents.find(
      (item) => item.id === invoice.document_id,
    );
    if (document?.storage_path) {
      const { data: signed, error } = await supabase!.storage
        .from("private-documents")
        .createSignedUrl(document.storage_path, 120);
      if (error) setInvoiceMessage(error.message);
      else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (invoice.external_pdf_url)
      window.open(invoice.external_pdf_url, "_blank", "noopener,noreferrer");
  }
  async function submitInvoice(event: React.FormEvent) {
    event.preventDefault();
    if (!worker || !invoiceFile) {
      setInvoiceMessage("Ajoutez la facture au format PDF.");
      return;
    }
    if (!selectedMissionIds.length) {
      setInvoiceMessage("Rattachez au moins une mission à la facture.");
      return;
    }
    if (Math.abs(totalIncVat - totalExVat - totalVat) > 0.02) {
      setInvoiceMessage("Le total TTC doit correspondre au montant HT + TVA.");
      return;
    }
    setInvoiceBusy(true);
    setInvoiceMessage("");
    const safeName = invoiceFile.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      storagePath = `${profile.organization_id}/${profile.id}/supplier-invoices/${Date.now()}-${safeName}`,
      uploaded = await supabase!.storage
        .from("private-documents")
        .upload(storagePath, invoiceFile, { upsert: false });
    if (uploaded.error) {
      setInvoiceBusy(false);
      setInvoiceMessage(uploaded.error.message);
      return;
    }
    const submitted = await supabase!.rpc("submit_supplier_invoice_pdf", {
      p_invoice_number: invoiceNumber.trim(),
      p_issue_on: issueOn,
      p_service_period_start: servicePeriodStart,
      p_service_period_end: servicePeriodEnd,
      p_due_on: dueOn,
      p_total_ex_vat: totalExVat,
      p_total_vat: totalVat,
      p_total_inc_vat: totalIncVat,
      p_storage_path: storagePath,
      p_original_name: invoiceFile.name,
      p_mission_ids: selectedMissionIds,
      p_contractor_notes: invoiceNotes.trim() || null,
    });
    if (submitted.error) {
      await supabase!.storage.from("private-documents").remove([storagePath]);
      setInvoiceBusy(false);
      setInvoiceMessage(submitted.error.message);
      return;
    }
    setInvoiceBusy(false);
    setInvoiceMessage(
      "Facture déposée. Elle est maintenant en attente de contrôle par ARD.",
    );
    setInvoiceOpen(false);
    setInvoiceNumber("");
    setSelectedMissionIds([]);
    setInvoiceNotes("");
    setInvoiceFile(null);
    reload();
  }
  async function connectSupplierHenrri(event: React.FormEvent) {
    event.preventDefault();
    if (!henrriStatus?.workspace_ready) {
      setHenrriMessage(
        "L’administrateur ARD doit d’abord activer votre espace entreprise fournisseur.",
      );
      return;
    }
    if (!henrriClientId.trim() || !henrriClientSecret.trim()) {
      setHenrriMessage("Saisissez le Client ID et le Secret de votre Sandbox Henrri.");
      return;
    }
    setHenrriBusy(true);
    setHenrriMessage("");
    const result = await supabase!.functions.invoke("henrri-connect", {
      body: {
        scope: "supplier_company",
        environment: "sandbox",
        client_id: henrriClientId.trim(),
        client_secret: henrriClientSecret.trim(),
      },
    });
    setHenrriBusy(false);
    if (result.error || result.data?.error) {
      setHenrriMessage(result.data?.error || result.error?.message || "Connexion Henrri impossible.");
      return;
    }
    setHenrriClientSecret("");
    setHenrriStatus((value) =>
      value
        ? {
            ...value,
            status: "connected",
            environment: "sandbox",
            account_identifier: result.data?.user?.email || value.account_identifier,
            provider_name: result.data?.company?.name
              ? `Henrri · ${result.data.company.name}`
              : "Henrri",
            last_error: null,
          }
        : value,
    );
    setHenrriMessage(
      result.data?.simulated
        ? "Connexion Henrri fournisseur simulée : aucune donnée n’est sortie du Showcase."
        : "Votre entreprise est connectée à Henrri Sandbox. Le secret est conservé uniquement côté serveur.",
    );
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Facturation sous-traitant</span>
          <h1>Ma facturation avec ARD</h1>
        </div>
        <div className="row-actions">
          <label className="period-picker">
            Mois
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </label>
          {supplierInvoicingAvailable && (
            <button
              className="portal-button"
              type="button"
              disabled={!invoiceableMissions.length}
              onClick={() => {
                setSelectedMissionIds(invoiceableMissions.map((item) => item.id));
                setTotalExVat(total);
                setTotalVat(0);
                setTotalIncVat(total);
                setInvoiceMessage("");
                setInvoiceOpen(true);
              }}
            >
              Déposer ma facture
            </button>
          )}
        </div>
      </div>
      {!supplierInvoicingAvailable && (
        <p className="notice">
          Le récapitulatif reste disponible. Le dépôt et le suivi des factures ST sont préparés mais non activés sur cette base.
        </p>
      )}
      <div className="stats portal-stats contractor-totals">
        <article>
          <span>Missions effectuées</span>
          <strong>{eligible.length}</strong>
        </article>
        <article>
          <span>Heures réalisées</span>
          <strong>{formatDurationHours(totalHours)}</strong>
        </article>
        <article>
          <span>Forfaits</span>
          <strong>
            {money(fixed.reduce((s, l) => s + l.amount, 0) + monthlyAmount)}
          </strong>
        </article>
        <article>
          <span>Total estimé à facturer</span>
          <strong>{money(total)}</strong>
          <small>hors taxes</small>
        </article>
      </div>
      <ContractorAnnualRevenueChart
        data={data}
        worker={worker}
        year={Number(period.slice(0, 4)) || new Date().getFullYear()}
      />
      <section className="panel client-accounting-statement">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Récapitulatif comptable</span>
            <h2>Exporter mes factures sur une période</h2>
            <p>Le document reprend les numéros de facture et les totaux HT, TVA et TTC transmis à ARD.</p>
          </div>
        </div>
        <div className="client-statement-controls">
          <label>Du<input type="date" value={statementFrom} onChange={(event) => setStatementFrom(event.target.value)} /></label>
          <label>Au<input type="date" value={statementTo} onChange={(event) => setStatementTo(event.target.value)} /></label>
          <button type="button" className="portal-button" onClick={printSupplierStatement}>Créer le récapitulatif PDF</button>
        </div>
        {statementMessage && <p className="form-message">{statementMessage}</p>}
      </section>
      <section className="panel supplier-henrri-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Facturation de votre entreprise</span>
            <h2>Henrri de mon entreprise</h2>
          </div>
          <StatusPill value={henrriStatus?.status || "not_configured"} />
        </div>
        <p className="section-intro">
          Cette connexion appartient exclusivement à votre entreprise. Les identifiants ne sont ni partagés avec ARD, ni enregistrés dans ce navigateur.
        </p>
        {henrriStatus?.workspace_ready ? (
          <>
            <div className="definition contractor-terms">
              <div><span>Entreprise</span><strong>{henrriStatus.company_name || "À compléter"}</strong></div>
              <div><span>Environnement</span><strong>Sandbox de test</strong></div>
              <div><span>Compte relié</span><strong>{henrriStatus.account_identifier || "Non connecté"}</strong></div>
            </div>
            <form className="form-grid" onSubmit={connectSupplierHenrri}>
              <label>Client ID Sandbox<input aria-label="Client ID Henrri de mon entreprise" autoComplete="off" value={henrriClientId} onChange={(event) => setHenrriClientId(event.target.value)} /></label>
              <label>Secret API Sandbox<input aria-label="Secret Henrri de mon entreprise" type="password" autoComplete="new-password" value={henrriClientSecret} onChange={(event) => setHenrriClientSecret(event.target.value)} /></label>
              <button disabled={henrriBusy}>{henrriBusy ? "Connexion…" : henrriStatus.status === "connected" ? "Tester à nouveau la connexion" : "Connecter ma Sandbox Henrri"}</button>
            </form>
          </>
        ) : (
          <p className="notice error">
            L’administrateur ARD doit activer votre espace entreprise fournisseur avant toute connexion Henrri.
          </p>
        )}
        {henrriMessage && <p className={`notice ${henrriMessage.includes("connectée") || henrriMessage.includes("simulée") ? "success" : "error"}`}>{henrriMessage}</p>}
      </section>
      <section className="panel">
        <h2>Détail du calcul</h2>
        <p className="section-intro">
          Ce récapitulatif reprend uniquement les missions réalisées et les
          conditions de rémunération enregistrées par ARD.
        </p>
        {lines.map((line) => (
          <div className="contractor-line" key={line.assignment.id}>
            <span>
              <strong>{missionClient(line.mission)}</strong>
              <small>
                {new Date(line.mission.starts_at).toLocaleDateString("fr-FR")} ·{" "}
                {line.label}
              </small>
            </span>
            <b>{money(line.amount)}</b>
          </div>
        ))}
        {monthlyAssignments.length > 0 && (
          <div className="contractor-line">
            <span>
              <strong>Forfait mensuel</strong>
              <small>
                {monthlyAssignments.length} mission(s) incluse(s) · compté une
                seule fois
              </small>
            </span>
            <b>{money(monthlyAmount)}</b>
          </div>
        )}
        {!eligible.length && (
          <p className="empty">Aucune mission réalisée pour ce mois.</p>
        )}
        <div className="contractor-total">
          <span>Total indicatif à reprendre sur votre facture</span>
          <strong>{money(total)} HT</strong>
        </div>
      </section>
      <section className="panel">
        <h2>Conditions enregistrées</h2>
        {(data.workerRateCards || []).filter(
          (rate) => rate.worker_id === worker?.id && rate.active,
        ).length > 0 && (
          <div className="contractor-rate-list">
            {(data.workerRateCards || [])
              .filter((rate) => rate.worker_id === worker?.id && rate.active)
              .map((rate) => (
                <div className="contractor-line" key={rate.id}>
                  <span>
                    <strong>{rate.name}</strong>
                    <small>
                      {rate.pricing_mode === "hourly"
                        ? "Taux horaire"
                        : rate.pricing_mode === "fixed"
                          ? "Forfait par intervention"
                          : "Prix à l’unité"}
                      {rate.notes ? ` · ${rate.notes}` : ""}
                    </small>
                  </span>
                  <b>
                    {money(rate.rate)}
                    {rate.pricing_mode === "hourly"
                      ? " / h"
                      : rate.unit_label
                        ? ` / ${rate.unit_label}`
                        : ""}
                  </b>
                </div>
              ))}
          </div>
        )}
        <div className="definition contractor-terms">
          <div>
            <span>Mode habituel</span>
            <strong>
              {worker?.pricing_mode === "hourly"
                ? "Taux horaire"
                : worker?.pricing_mode === "fixed"
                  ? "Forfait"
                  : worker?.pricing_mode === "monthly"
                    ? "Forfait mensuel"
                    : worker?.pricing_mode === "mixed"
                      ? "Mixte"
                      : "À définir"}
            </strong>
          </div>
          {worker?.hourly_rate != null && (
            <div>
              <span>Taux horaire</span>
              <strong>{money(worker.hourly_rate)} / h</strong>
            </div>
          )}
          {worker?.fixed_fee != null && (
            <div>
              <span>Forfait habituel</span>
              <strong>{money(worker.fixed_fee)}</strong>
            </div>
          )}
          {worker?.monthly_fee != null && (
            <div>
              <span>Forfait mensuel</span>
              <strong>{money(worker.monthly_fee)}</strong>
            </div>
          )}
        </div>
      </section>
      <section className="panel">
        <h2>Mes factures transmises</h2>
        <p className="section-intro">
          Le PDF, ses montants et les missions concernées restent reliés au même
          dossier. ARD doit valider la facture avant son paiement.
        </p>
        {supplierInvoices.length ? (
          <div className="supplier-invoice-list">
            {supplierInvoices.map((invoice) => {
              const linkedMissions = (data.supplierInvoiceMissions || [])
                .filter((link) => link.invoice_id === invoice.id)
                .map((link) => missions.get(link.mission_id))
                .filter((mission): mission is PortalMission => !!mission);
              return (
                <article key={invoice.id}>
                  <span>
                    <strong>{invoice.invoice_number}</strong>
                    <small>
                      {new Date(invoice.issue_on).toLocaleDateString("fr-FR")} ·{" "}
                      {linkedMissions.length} mission(s) · {money(invoice.total_inc_vat)} TTC
                    </small>
                    {invoice.review_notes && (
                      <small>Retour ARD : {invoice.review_notes}</small>
                    )}
                  </span>
                  <StatusPill value={invoice.status} />
                  <button
                    type="button"
                    className="portal-button secondary compact"
                    disabled={!invoice.document_id && !invoice.external_pdf_url}
                    onClick={() => openInvoiceDocument(invoice)}
                  >
                    Ouvrir
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="empty">Aucune facture transmise.</p>
        )}
        {invoiceMessage && (
          <p
            className={`notice ${invoiceMessage.startsWith("Facture déposée") ? "success" : "error"}`}
          >
            {invoiceMessage}
          </p>
        )}
      </section>
      {supplierInvoicingAvailable && invoiceOpen && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Déposer une facture sous-traitant">
          <form className="portal-detail supplier-invoice-dialog" onSubmit={submitInvoice}>
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Facture fournisseur</span>
                <h2>Déposer ma facture</h2>
              </div>
              <button
                type="button"
                className="icon-close"
                aria-label="Fermer"
                onClick={() => setInvoiceOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="section-intro">
              Période du {new Date(servicePeriodStart).toLocaleDateString("fr-FR")} au{" "}
              {new Date(servicePeriodEnd).toLocaleDateString("fr-FR")}. Les informations
              sont contrôlées par ARD avant validation.
            </p>
            <div className="form-grid">
              <label>
                Numéro de facture
                <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} required />
              </label>
              <label>
                Date d’émission
                <input type="date" value={issueOn} onChange={(e) => setIssueOn(e.target.value)} required />
              </label>
              <label>
                Date d’échéance
                <input type="date" min={issueOn} value={dueOn} onChange={(e) => setDueOn(e.target.value)} required />
              </label>
              <label>
                Total HT
                <input type="number" min="0" step="0.01" value={totalExVat} onChange={(e) => setTotalExVat(+e.target.value)} required />
              </label>
              <label>
                TVA
                <input type="number" min="0" step="0.01" value={totalVat} onChange={(e) => setTotalVat(+e.target.value)} required />
              </label>
              <label>
                Total TTC
                <input type="number" min="0" step="0.01" value={totalIncVat} onChange={(e) => setTotalIncVat(+e.target.value)} required />
              </label>
              <fieldset className="wide supplier-mission-choice">
                <legend>Missions reprises sur la facture</legend>
                {invoiceableMissions.map((mission) => (
                  <label key={mission.id}>
                    <input
                      type="checkbox"
                      checked={selectedMissionIds.includes(mission.id)}
                      onChange={(e) =>
                        setSelectedMissionIds((current) =>
                          e.target.checked
                            ? [...new Set([...current, mission.id])]
                            : current.filter((id) => id !== mission.id),
                        )
                      }
                    />
                    <span>
                      <strong>{missionClient(mission)}</strong>
                      <small>{new Date(mission.starts_at).toLocaleDateString("fr-FR")} · {mission.location}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              <label className="wide">
                Facture PDF
                <input type="file" accept="application/pdf" onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} required />
              </label>
              <label className="wide">
                Note facultative
                <textarea value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} />
              </label>
            </div>
            {Math.abs(totalExVat - total) > 0.02 && (
              <p className="notice error">
                Écart avec le récapitulatif ARD : {money(total)} HT attendu, {money(totalExVat)} HT saisi. Le contrôle reste manuel.
              </p>
            )}
            {invoiceMessage && <p className="notice error">{invoiceMessage}</p>}
            <div className="row-actions supplier-invoice-actions">
              <button type="button" className="portal-button secondary" onClick={() => setInvoiceOpen(false)}>Annuler</button>
              <button className="portal-button" disabled={invoiceBusy}>{invoiceBusy ? "Dépôt en cours…" : "Transmettre pour contrôle"}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export function AdminSupplierInvoicesRich({
  data,
  reload,
  focusInvoiceId,
  onFocusConsumed,
}: {
  data: PortalData;
  reload: () => void;
  focusInvoiceId?: string | null;
  onFocusConsumed?: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null),
    [selectedAdjustmentIds, setSelectedAdjustmentIds] = useState<string[]>([]),
    [reviewNotes, setReviewNotes] = useState(""),
    [paidOn, setPaidOn] = useState(parisCalendarDay()),
    [paymentReference, setPaymentReference] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const invoices = (data.supplierInvoices || []).slice().sort((a, b) =>
      b.submitted_at.localeCompare(a.submitted_at),
    ),
    selected = invoices.find((invoice) => invoice.id === selectedId) || null,
    selectedWorker = data.workers.find(
      (worker) => worker.id === selected?.worker_id,
    ),
    selectedMissions = selected
      ? (data.supplierInvoiceMissions || [])
          .filter((link) => link.invoice_id === selected.id)
          .map((link) => data.missions.find((mission) => mission.id === link.mission_id))
          .filter((mission): mission is PortalMission => !!mission)
      : [],
    selectedMissionIds = new Set(selectedMissions.map((mission)=>mission.id)),
    eligibleAdjustments = selected
      ? (data.missionFinancialAdjustments || []).filter((adjustment)=>
          adjustment.worker_id===selected.worker_id &&
          selectedMissionIds.has(adjustment.mission_id) &&
          ["supplier_surcharge","supplier_penalty","supplier_deduction"].includes(adjustment.adjustment_kind) &&
          ["approved","applied"].includes(adjustment.status) &&
          !adjustment.supplier_invoice_id)
      : [],
    selectedAdjustmentEffect = eligibleAdjustments.filter((item)=>selectedAdjustmentIds.includes(item.id)).reduce((sum,item)=>
      sum+(item.adjustment_kind==="supplier_surcharge"?item.amount_ex_vat:-item.amount_ex_vat),0),
    pendingCount = invoices.filter((invoice) =>
      ["submitted", "under_review"].includes(invoice.status),
    ).length;
  useEffect(() => {
    if (!focusInvoiceId) return;
    const invoice = invoices.find((item) => item.id === focusInvoiceId);
    if (!invoice) return;
    setSelectedId(invoice.id);
    setReviewNotes(invoice.review_notes || "");
    setSelectedAdjustmentIds([]);
    setMessage("");
    onFocusConsumed?.();
  }, [focusInvoiceId, data.supplierInvoices, onFocusConsumed]);
  async function openInvoiceDocument(invoice: PortalSupplierInvoice) {
    const document = data.documents.find((item) => item.id === invoice.document_id);
    if (document?.storage_path) {
      const { data: signed, error } = await supabase!.storage
        .from("private-documents")
        .createSignedUrl(document.storage_path, 120);
      if (error) setMessage(error.message);
      else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (invoice.external_pdf_url)
      window.open(invoice.external_pdf_url, "_blank", "noopener,noreferrer");
  }
  async function review(approve: boolean) {
    if (!selected) return;
    if (!approve && !reviewNotes.trim()) {
      setMessage("Indiquez le motif du refus pour que le sous-traitant puisse corriger.");
      return;
    }
    setBusy(true);
    const result = await supabase!.rpc("review_supplier_invoice_with_adjustments", {
      p_invoice_id: selected.id,
      p_approve: approve,
      p_review_notes: reviewNotes.trim() || null,
      p_adjustment_ids: approve ? selectedAdjustmentIds : [],
    });
    setBusy(false);
    setMessage(
      result.error
        ? result.error.message
        : approve
          ? "Facture sous-traitant validée."
          : "Facture refusée avec motif transmis.",
    );
    if (!result.error) {
      setSelectedId(null);
      setSelectedAdjustmentIds([]);
      setReviewNotes("");
      reload();
    }
  }
  async function markPaid() {
    if (!selected) return;
    setBusy(true);
    const result = await supabase!.rpc("mark_supplier_invoice_paid", {
      p_invoice_id: selected.id,
      p_paid_on: paidOn,
      p_payment_reference: paymentReference.trim() || null,
    });
    setBusy(false);
    setMessage(result.error ? result.error.message : "Paiement de la facture enregistré.");
    if (!result.error) {
      setSelectedId(null);
      setPaymentReference("");
      reload();
    }
  }
  return (
    <>
      <section className="panel supplier-invoice-admin">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Achats et sous-traitance</span>
            <h2>Factures fournisseurs à contrôler</h2>
            <p>
              {pendingCount} facture(s) en attente · PDF/Henrri, montants et missions
              sont contrôlés dans un seul dossier.
            </p>
          </div>
        </div>
        {invoices.length ? (
          <div className="supplier-invoice-list">
            {invoices.map((invoice) => {
              const worker = data.workers.find((item) => item.id === invoice.worker_id),
                missionCount = (data.supplierInvoiceMissions || []).filter(
                  (link) => link.invoice_id === invoice.id,
                ).length;
              return (
                <button
                  type="button"
                  className="supplier-invoice-row"
                  key={invoice.id}
                  onClick={() => {
                    setSelectedId(invoice.id);
                    setReviewNotes(invoice.review_notes || "");
                    setSelectedAdjustmentIds([]);
                    setMessage("");
                  }}
                >
                  <span>
                    <strong>{invoice.invoice_number} · {workerName(worker)}</strong>
                    <small>
                      {new Date(invoice.issue_on).toLocaleDateString("fr-FR")} · {missionCount} mission(s) · {money(invoice.total_inc_vat)} TTC
                    </small>
                  </span>
                  <StatusPill value={invoice.status} />
                </button>
              );
            })}
          </div>
        ) : (
          <p className="empty">Aucune facture fournisseur transmise.</p>
        )}
        {message && (
          <p className={`notice ${message.includes("validée") || message.includes("enregistré") || message.includes("transmis") ? "success" : "error"}`}>
            {message}
          </p>
        )}
      </section>
      {selected && (
        <div className="portal-overlay" role="dialog" aria-modal="true" aria-label="Contrôler la facture sous-traitant">
          <section className="portal-detail supplier-invoice-dialog">
            <div className="portal-detail-head">
              <div>
                <span className="eyebrow">Contrôle fournisseur</span>
                <h2>{selected.invoice_number}</h2>
                <small>{workerName(selectedWorker)}</small>
              </div>
              <button type="button" className="icon-close" aria-label="Fermer" onClick={() => setSelectedId(null)}>×</button>
            </div>
            <div className="detail-grid">
              <div><span>Source</span><strong>{selected.source === "henrri" ? "Henrri" : "PDF déposé"}</strong></div>
              <div><span>État</span><StatusPill value={selected.status} /></div>
              <div><span>Émise le</span><strong>{new Date(selected.issue_on).toLocaleDateString("fr-FR")}</strong></div>
              <div><span>Échéance</span><strong>{new Date(selected.due_on).toLocaleDateString("fr-FR")}</strong></div>
              <div><span>Total HT</span><strong>{money(selected.total_ex_vat)}</strong></div>
              <div><span>TVA</span><strong>{money(selected.total_vat)}</strong></div>
              <div><span>Total TTC</span><strong>{money(selected.total_inc_vat)}</strong></div>
              <div><span>Période</span><strong>{new Date(selected.service_period_start).toLocaleDateString("fr-FR")} → {new Date(selected.service_period_end).toLocaleDateString("fr-FR")}</strong></div>
            </div>
            <section className="detail-note">
              <strong>Missions rattachées</strong>
              {selectedMissions.map((mission) => (
                <p key={mission.id}>{new Date(mission.starts_at).toLocaleDateString("fr-FR")} · {missionClient(mission)} · {mission.location}</p>
              ))}
              {!selectedMissions.length && <p>À contrôler : aucune mission liée.</p>}
            </section>
            {selected.contractor_notes && <section className="detail-note"><strong>Note du sous-traitant</strong><p>{selected.contractor_notes}</p></section>}
            {!!eligibleAdjustments.length && <section className="detail-note supplier-adjustment-review">
              <strong>Ajustements fournisseur disponibles</strong>
              <p>Choisissez explicitement les surcoûts ou déductions déjà validés à rattacher à cette facture. Aucun montant n’est sélectionné automatiquement.</p>
              {eligibleAdjustments.map((adjustment)=><label key={adjustment.id}>
                <input type="checkbox" checked={selectedAdjustmentIds.includes(adjustment.id)} onChange={(event)=>setSelectedAdjustmentIds((current)=>event.target.checked?[...new Set([...current,adjustment.id])]:current.filter((id)=>id!==adjustment.id))} />
                <span><strong>{adjustment.adjustment_kind==="supplier_surcharge"?"Surcoût":"Déduction"} · {money(adjustment.amount_ex_vat)} HT</strong><small>{adjustment.reason}</small></span>
              </label>)}
              <p><strong>Impact sélectionné : {selectedAdjustmentEffect>=0?"+":"−"}{money(Math.abs(selectedAdjustmentEffect))} HT</strong></p>
            </section>}
            <button type="button" className="portal-button secondary" disabled={!selected.document_id && !selected.external_pdf_url} onClick={() => openInvoiceDocument(selected)}>
              Ouvrir la facture
            </button>
            {["submitted", "under_review"].includes(selected.status) && (
              <>
                <label className="modal-field">
                  Note de contrôle / motif de refus
                  <textarea value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} />
                </label>
                <div className="row-actions supplier-invoice-actions">
                  <button type="button" className="portal-button danger" disabled={busy} onClick={() => review(false)}>Refuser et demander correction</button>
                  <button type="button" className="portal-button" disabled={busy} onClick={() => review(true)}>Valider la facture</button>
                </div>
              </>
            )}
            {selected.status === "approved" && (
              <div className="form-grid supplier-payment-form">
                <label>Date de paiement<input type="date" value={paidOn} onChange={(event) => setPaidOn(event.target.value)} /></label>
                <label>Référence bancaire<input value={paymentReference} onChange={(event) => setPaymentReference(event.target.value)} /></label>
                <button type="button" disabled={busy} onClick={markPaid}>Marquer payée</button>
              </div>
            )}
            {selected.review_notes && <p className="notice">Dernier contrôle : {selected.review_notes}</p>}
            {message && <p className="notice error">{message}</p>}
          </section>
        </div>
      )}
    </>
  );
}

export function ContractorAvailabilityRich({
  profile,
  worker,
  data,
  reload,
}: {
  profile: PortalProfile;
  worker: PortalWorker | null;
  data: PortalData;
  reload: () => void;
}) {
  const [from, setFrom] = useState(""),
    [to, setTo] = useState(""),
    [availability, setAvailability] = useState<"available" | "unavailable">(
      "unavailable",
    ),
    [notes, setNotes] = useState(""),
    [message, setMessage] = useState("");
  const items = data.availability
    .filter((a) => a.worker_id === worker?.id)
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!worker) return;
    const start = new Date(from),
      end = new Date(to);
    if (!(end > start))
      return setMessage("La fin du créneau doit être postérieure au début.");
    const { error } = await supabase!.from("worker_availability").insert({
      organization_id: profile.organization_id,
      worker_id: worker.id,
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
      availability,
      notes: notes || null,
    });
    setMessage(
      error
        ? error.message
        : availability === "unavailable"
          ? "Indisponibilité enregistrée. Elle bloquera les propositions sur ce créneau."
          : "Disponibilité enregistrée.",
    );
    if (!error) {
      setFrom("");
      setTo("");
      setNotes("");
      reload();
    }
  }
  async function remove(id: string) {
    const { error } = await supabase!
      .from("worker_availability")
      .delete()
      .eq("id", id);
    setMessage(error ? error.message : "Créneau supprimé.");
    if (!error) reload();
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Organisation</span>
          <h1>Mes disponibilités</h1>
        </div>
      </div>
      <section className="panel">
        <h2>Ajouter un créneau</h2>
        <p className="section-intro">
          Une indisponibilité empêche automatiquement ARD de vous proposer une
          mission qui chevauche ce créneau.
        </p>
        <form className="form-grid" onSubmit={save}>
          <label>
            Je suis
            <select
              value={availability}
              onChange={(e) =>
                setAvailability(e.target.value as "available" | "unavailable")
              }
            >
              <option value="unavailable">Indisponible</option>
              <option value="available">Disponible</option>
            </select>
          </label>
          <label>
            Début
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              required
            />
          </label>
          <label>
            Fin
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
            />
          </label>
          <label className="wide">
            Précision
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <button>Enregistrer le créneau</button>
        </form>
        {message && (
          <p
            className={`notice ${message.includes("enregistrée") || message.includes("supprimé") ? "success" : "error"}`}
          >
            {message}
          </p>
        )}
      </section>
      <section className="panel">
        <h2>Créneaux déclarés</h2>
        {items.length ? (
          <div className="availability-list">
            {items.map((item) => (
              <div key={item.id}>
                <StatusPill value={item.availability} />
                <span>
                  <strong>
                    {dateTime(item.starts_at)} → {dateTime(item.ends_at)}
                  </strong>
                  <small>{item.notes || "Sans précision"}</small>
                </span>
                <button
                  className="portal-button danger compact"
                  onClick={() => remove(item.id)}
                >
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty">Aucun créneau déclaré.</p>
        )}
      </section>
    </>
  );
}

export function ContractorDocumentsRich({
  profile,
  worker,
  data,
  reload,
}: {
  profile: PortalProfile;
  worker: PortalWorker | null;
  data: PortalData;
  reload: () => void;
}) {
  const [requirement, setRequirement] = useState(""),
    [kind, setKind] = useState(worker?.kind === "employee" ? "other" : "contractor_invoice"),
    [period, setPeriod] = useState(parisCalendarMonth()),
    [expires, setExpires] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const requirements = data.requirements.filter(
      (r) => r.worker_id === worker?.id,
    ),
    contracts = data.contracts.filter((c) => c.worker_id === worker?.id),
    missing = requirements.filter((r) =>
      ["required", "rejected", "expired"].includes(r.status),
    );
  async function upload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return setMessage("Choisissez un fichier PDF ou une image.");
    setBusy(true);
    const safe = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      path = `${profile.organization_id}/${profile.id}/${Date.now()}-${safe}`;
    const stored = await supabase!.storage
      .from("private-documents")
      .upload(path, file, { upsert: false });
    if (stored.error) {
      setBusy(false);
      return setMessage(stored.error.message);
    }
    const req = requirements.find((r) => r.id === requirement),
      saved = await supabase!.from("documents").insert({
        organization_id: profile.organization_id,
        owner_profile_id: profile.id,
        kind: req?.kind || kind,
        period: period || null,
        storage_path: path,
        original_name: file.name,
        expires_on: expires || null,
        requirement_id: requirement || null,
        status: "submitted",
        created_by: profile.id,
      });
    setBusy(false);
    setMessage(
      saved.error ? saved.error.message : "Document déposé. ARD a été informé.",
    );
    if (!saved.error) {
      setFile(null);
      setRequirement("");
      setExpires("");
      reload();
    }
  }
  async function openDocument(doc: PortalDocument) {
    if (!doc.storage_path) return;
    const { data, error } = await supabase!.storage
      .from("private-documents")
      .createSignedUrl(doc.storage_path, 120);
    if (error) setMessage(error.message);
    else window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">{worker?.kind === "employee" ? "Dossier personnel" : "Conformité et échanges"}</span>
          <h1>Mes documents</h1>
        </div>
      </div>
      {missing.length > 0 && (
        <section className="panel compliance-alert">
          <h2>Documents attendus</h2>
          {missing.map((r) => (
            <div key={r.id}>
              <span>
                <strong>{r.label}</strong>
                <small>
                  {r.due_on
                    ? `À déposer avant le ${new Date(r.due_on).toLocaleDateString("fr-FR")}`
                    : "Date à définir"}
                  {r.notes ? ` · ${r.notes}` : ""}
                </small>
              </span>
              <StatusPill value={r.status} />
              <button
                className="portal-button secondary compact"
                onClick={() => {
                  setRequirement(r.id);
                  setKind(r.kind);
                }}
              >
                Déposer
              </button>
            </div>
          ))}
        </section>
      )}
      <section className="panel">
        <h2>Contrat avec ARD</h2>
        {contracts.length ? (
          <div className="contract-list">
            {contracts.map((c) => (
              <div key={c.id}>
                {(() => {
                  const signature=(data.signatureEnvelopes||[]).find((item)=>item.source_type==="worker_contract"&&item.source_id===c.id);
                  return signature?<small>Signature électronique : {statusLabel(signature.status)}{signature.provider?` · ${signature.provider}`:" · en préparation"}</small>:null;
                })()}
                <span>
                  <strong>{c.title}</strong>
                  <small>
                    {c.starts_on
                      ? `Début ${new Date(c.starts_on).toLocaleDateString("fr-FR")}`
                      : ""}
                    {c.ends_on
                      ? ` · Fin ${new Date(c.ends_on).toLocaleDateString("fr-FR")}`
                      : ""}
                  </small>
                </span>
                <StatusPill value={c.status} />
                {c.rendered_content && (
                  <details className="contract-preview">
                    <summary>Voir le contrat prérempli</summary>
                    <div>{c.rendered_content}</div>
                  </details>
                )}
                {c.document_id && (
                  <button
                    className="portal-button secondary compact"
                    onClick={() => {
                      const doc = data.documents.find(
                        (d) => d.id === c.document_id,
                      );
                      if (doc) openDocument(doc);
                    }}
                  >
                    Consulter
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="empty">Aucun contrat n’a encore été déposé par ARD.</p>
        )}
      </section>
      <section className="panel">
        <h2>Déposer un document</h2>
        <form className="form-grid" onSubmit={upload}>
          <label>
            Document attendu
            <select
              value={requirement}
              onChange={(e) => {
                setRequirement(e.target.value);
                const r = requirements.find((x) => x.id === e.target.value);
                if (r) setKind(r.kind);
              }}
            >
              <option value="">Dépôt libre</option>
              {requirements
                .filter((r) => r.status !== "validated")
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Type
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              disabled={!!requirement}
            >
              {worker?.kind !== "employee" && <option value="contractor_invoice">Facture sous-traitant</option>}
              <option value="signed_contract">Contrat signé</option>
              {worker?.kind !== "employee" && <option value="urssaf_vigilance">Attestation de vigilance URSSAF</option>}
              {worker?.kind !== "employee" && <option value="professional_liability">RC professionnelle</option>}
              {worker?.kind === "employee" && <option value="medical_certificate">Justificatif médical</option>}
              {worker?.kind === "employee" && <option value="training_certificate">Attestation de formation</option>}
              <option value="other">Autre document</option>
            </select>
          </label>
          <label>
            Période
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </label>
          <label>
            Date d’expiration
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
            />
          </label>
          <label className="wide">
            Fichier PDF ou image
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
          </label>
          <button disabled={busy}>
            {busy ? "Dépôt en cours…" : "Déposer le document"}
          </button>
        </form>
        {message && (
          <p
            className={`notice ${message.startsWith("Document déposé") ? "success" : "error"}`}
          >
            {message}
          </p>
        )}
      </section>
      <section className="panel">
        <h2>Documents déjà déposés</h2>
        {data.documents.length ? (
          <div className="document-list">
            {data.documents.map((d) => (
              <div key={d.id}>
                <span>
                  <strong>{d.original_name}</strong>
                  <small>
                    {statusLabel(d.kind)}
                    {d.period ? ` · ${d.period}` : ""}
                    {d.expires_on
                      ? ` · expire le ${new Date(d.expires_on).toLocaleDateString("fr-FR")}`
                      : ""}
                  </small>
                </span>
                <StatusPill value={d.status} />
                <button
                  className="portal-button secondary compact"
                  onClick={() => openDocument(d)}
                >
                  Ouvrir
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty">Aucun document déposé.</p>
        )}
      </section>
    </>
  );
}

export function ClientDocumentsRich({
  profile,
  data,
  reload,
}: {
  profile: PortalProfile;
  data: PortalData;
  reload: () => void;
}) {
  const client = data.clients[0],
    contracts = (data.clientContracts || []).filter(
      (item) => item.client_id === client?.id,
    ),
    requirements = (data.clientRequirements || []).filter(
      (item) => item.client_id === client?.id,
    ),
    missing = requirements.filter((item) =>
      ["required", "rejected", "expired"].includes(item.status),
    );
  const [requirementId, setRequirementId] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function openDocument(document: PortalDocument) {
    if (!document.storage_path) return;
    const { data: signed, error } = await supabase!.storage
      .from("private-documents")
      .createSignedUrl(document.storage_path, 120);
    if (error) setMessage(error.message);
    else window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
  }
  async function upload(e: React.FormEvent) {
    e.preventDefault();
    const requirement = requirements.find((item) => item.id === requirementId);
    if (!file || !requirement)
      return setMessage("Choisissez la pièce attendue et un fichier.");
    setBusy(true);
    const safeName = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "-"),
      storagePath = `${profile.organization_id}/${profile.id}/${Date.now()}-${safeName}`,
      stored = await supabase!.storage
        .from("private-documents")
        .upload(storagePath, file, { upsert: false });
    if (stored.error) {
      setBusy(false);
      setMessage(stored.error.message);
      return;
    }
    const saved = await supabase!.from("documents").insert({
      organization_id: profile.organization_id,
      owner_profile_id: profile.id,
      kind: requirement.kind,
      storage_path: storagePath,
      original_name: file.name,
      status: "submitted",
      client_requirement_id: requirement.id,
      created_by: profile.id,
    });
    setBusy(false);
    setMessage(
      saved.error
        ? saved.error.message
        : "Document déposé. Il est maintenant en attente de validation par ARD.",
    );
    if (!saved.error) {
      setFile(null);
      setRequirementId("");
      reload();
    }
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Relation contractuelle</span>
          <h1>Documents & contrat</h1>
        </div>
      </div>
      <section className="panel">
        <h2>Contrat avec ARD</h2>
        {contracts.map((contract) => (
          <div className="workflow-row" key={contract.id}>
            <span>
              <strong>{contract.title}</strong>
              <small>
                Version {contract.template_version || "—"}
                {contract.ends_on
                  ? ` · échéance ${new Date(contract.ends_on).toLocaleDateString("fr-FR")}`
                  : " · sans échéance renseignée"}
              </small>
            </span>
            <StatusPill value={contract.status} />
            {(() => {
              const signature=(data.signatureEnvelopes||[]).find((item)=>item.source_type==="client_contract"&&item.source_id===contract.id);
              return signature?<small>Signature électronique : {statusLabel(signature.status)}{signature.provider?` · ${signature.provider}`:" · en préparation"}</small>:null;
            })()}
            {contract.rendered_content && (
              <details className="contract-preview">
                <summary>Voir le contrat prérempli</summary>
                <div>{contract.rendered_content}</div>
              </details>
            )}
            {contract.document_id && (
              <button
                className="portal-button secondary compact"
                onClick={() => {
                  const document = data.documents.find(
                    (item) => item.id === contract.document_id,
                  );
                  if (document) openDocument(document);
                }}
              >
                Consulter
              </button>
            )}
          </div>
        ))}
        {!contracts.length && (
          <p className="empty">Aucun contrat n’a encore été préparé.</p>
        )}
      </section>
      <section className="panel">
        <h2>Pièces attendues</h2>
        {requirements.map((requirement) => (
          <div className="workflow-row" key={requirement.id}>
            <span>
              <strong>{requirement.label}</strong>
              <small>
                {requirement.due_on
                  ? `À déposer avant le ${new Date(requirement.due_on).toLocaleDateString("fr-FR")}`
                  : "Aucune échéance renseignée"}
              </small>
            </span>
            <StatusPill value={requirement.status} />
          </div>
        ))}
        {!requirements.length && (
          <p className="empty">Aucune pièce demandée.</p>
        )}
      </section>
      {missing.length > 0 && (
        <section className="panel">
          <h2>Déposer une pièce</h2>
          <form className="form-grid" onSubmit={upload}>
            <label>
              Pièce attendue
              <select
                value={requirementId}
                onChange={(e) => setRequirementId(e.target.value)}
                required
              >
                <option value="">Choisir</option>
                {missing.map((requirement) => (
                  <option key={requirement.id} value={requirement.id}>
                    {requirement.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Fichier PDF ou image
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
              />
            </label>
            <button disabled={busy}>
              {busy ? "Dépôt en cours…" : "Déposer pour validation"}
            </button>
          </form>
          {message && (
            <p
              className={`notice ${message.startsWith("Document déposé") ? "success" : "error"}`}
            >
              {message}
            </p>
          )}
        </section>
      )}
    </>
  );
}

export function ProfileEditorRich({
  profile,
  client,
  worker,
  calendarConnection,
  reload,
}: {
  profile: PortalProfile;
  client?: PortalClient;
  worker?: PortalWorker | null;
  calendarConnection?: PortalCalendarConnection | null;
  reload: () => void;
}) {
  const [displayName, setDisplayName] = useState(profile.display_name || ""),
    [email, setEmail] = useState(profile.email || ""),
    [phone, setPhone] = useState(profile.phone || ""),
    [calendar, setCalendar] = useState(!!profile.calendar_enabled),
    [clientName, setClientName] = useState(client?.name || ""),
    [address, setAddress] = useState(client?.address || worker?.address || ""),
    [siret, setSiret] = useState(client?.siret || worker?.siret || ""),
    [vat, setVat] = useState(client?.vat_number || worker?.vat_number || ""),
    [notes, setNotes] = useState(client?.notes || worker?.profile_notes || ""),
    [business, setBusiness] = useState(worker?.business_name || ""),
    [firstName, setFirstName] = useState(worker?.first_name || ""),
    [lastName, setLastName] = useState(worker?.last_name || ""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [calendarBusy, setCalendarBusy] = useState(false),
    [calendarMessage, setCalendarMessage] = useState(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("calendar") === "connected")
        return "Google Agenda est maintenant connecté à votre profil.";
      return params.get("calendar_message") || "";
    });
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    if (!client && !worker) {
      const { error } = await supabase!
        .from("profiles")
        .update({ display_name: displayName, email, phone })
        .eq("id", profile.id);
      setBusy(false);
      setMessage(
        error
          ? error.message
          : "Vos coordonnées personnelles sont enregistrées.",
      );
      if (!error) reload();
      return;
    }
    const { error } = await supabase!.from("profile_change_requests").insert({
      organization_id: profile.organization_id,
      requester_profile_id: profile.id,
      target_type: client ? "client" : "worker",
      target_id: client?.id || worker!.id,
      profile_changes: {
        display_name: displayName,
        email,
        phone,
      },
      target_changes: client
        ? {
            name: clientName,
            address,
            siret,
            vat_number: vat,
            notes,
          }
        : {
            first_name: firstName,
            last_name: lastName,
            business_name: business,
            address,
            siret,
            vat_number: vat,
            profile_notes: notes,
          },
    });
    setBusy(false);
    setMessage(
      error
        ? error.message
        : "Votre demande a été transmise à ARD pour vérification.",
    );
    if (!error) reload();
  }
  async function saveCalendarPreference(enabled: boolean) {
    setCalendarBusy(true);
    setCalendarMessage("");
    const profileUpdate = await supabase!
      .from("profiles")
      .update({ calendar_enabled: enabled })
      .eq("id", profile.id);
    if (profileUpdate.error) {
      setCalendarBusy(false);
      setCalendarMessage(profileUpdate.error.message);
      return;
    }
    if (client) {
      const clientUpdate = await supabase!
        .from("clients")
        .update({ calendar_enabled: enabled })
        .eq("id", client.id);
      if (clientUpdate.error) {
        setCalendarBusy(false);
        setCalendarMessage(clientUpdate.error.message);
        return;
      }
    }
    setCalendar(enabled);
    setCalendarBusy(false);
    setCalendarMessage(
      enabled
        ? "Synchronisation automatique activée pour vos missions confirmées."
        : "Synchronisation automatique désactivée. Le bouton d’ajout ponctuel reste disponible.",
    );
    reload();
  }
  async function connectGoogleCalendar() {
    setCalendarMessage("");
    const showcase = new URLSearchParams(window.location.search).has("showcase");
    if (showcase) {
      setCalendarBusy(true);
      const now = new Date().toISOString(),
        result = await supabase!.from("calendar_connections").upsert(
          {
            organization_id: profile.organization_id,
            profile_id: profile.id,
            provider: "google",
            status: "connected",
            calendar_id: "primary",
            calendar_name: "Agenda principal",
            google_account_email: profile.email || "compte-google@demo.ard",
            sync_enabled: true,
            connected_at: now,
            last_sync_at: now,
            last_error: null,
          },
          { onConflict: "profile_id,provider" },
        );
      setCalendarBusy(false);
      if (result.error) return setCalendarMessage(result.error.message);
      setCalendar(true);
      setCalendarMessage("Compte Google connecté en mode démonstration. Aucun événement externe n’a été créé.");
      reload();
      return;
    }
    setCalendarBusy(true);
    const started = await supabase!.functions.invoke(
      "google-calendar-oauth-start",
      { body: { return_to: window.location.href } },
    );
    setCalendarBusy(false);
    if (started.error || !started.data?.authorization_url) {
      setCalendarMessage(
        started.data?.error ||
          started.error?.message ||
          "La connexion Google sera disponible dès que les identifiants OAuth Google Cloud auront été renseignés dans l’environnement sécurisé.",
      );
      return;
    }
    window.location.assign(started.data.authorization_url);
  }
  async function disconnectGoogleCalendar() {
    setCalendarBusy(true);
    setCalendarMessage("");
    const showcase = new URLSearchParams(window.location.search).has("showcase");
    if (showcase) {
      const removed = await supabase!
        .from("calendar_connections")
        .delete()
        .eq("profile_id", profile.id)
        .eq("provider", "google");
      if (!removed.error) {
        await supabase!.from("profiles").update({ calendar_enabled: false }).eq("id", profile.id);
        if (client)
          await supabase!.from("clients").update({ calendar_enabled: false }).eq("id", client.id);
      }
      setCalendarBusy(false);
      if (removed.error) return setCalendarMessage(removed.error.message);
      setCalendar(false);
      setCalendarMessage("Compte Google déconnecté en mode démonstration.");
      reload();
      return;
    }
    const result = await supabase!.functions.invoke("google-calendar-disconnect", {
      body: {},
    });
    setCalendarBusy(false);
    if (result.error || !result.data?.disconnected) {
      setCalendarMessage(
        result.data?.error || result.error?.message || "Déconnexion Google impossible.",
      );
      return;
    }
    setCalendar(false);
    setCalendarMessage("Compte Google déconnecté et autorisation locale supprimée.");
    reload();
  }
  return (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">Coordonnées et préférences</span>
          <h1>Mon profil</h1>
        </div>
      </div>
      <section className="panel calendar-settings-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Préférence personnelle</span>
            <h2>Google Agenda</h2>
            <p>
              Connectez votre propre agenda. Seules les missions confirmées qui
              vous concernent seront synchronisées.
            </p>
          </div>
          <StatusPill value={calendarConnection?.status || "not_connected"} />
        </div>
        {calendarConnection?.status === "connected" ? (
          <div className="calendar-connection-summary">
            <div>
              <span>Compte connecté</span>
              <strong>{calendarConnection.google_account_email || "Compte Google"}</strong>
            </div>
            <div>
              <span>Agenda choisi</span>
              <strong>{calendarConnection.calendar_name || "Agenda principal"}</strong>
            </div>
            <div>
              <span>Dernière synchronisation</span>
              <strong>{calendarConnection.last_sync_at ? dateTime(calendarConnection.last_sync_at) : "Pas encore effectuée"}</strong>
            </div>
          </div>
        ) : (
          <p className="empty">
            Aucun compte Google n’est encore connecté à ce profil.
          </p>
        )}
        <div className="calendar-preference-actions">
          <label className="check">
            <input
              type="checkbox"
              checked={calendar}
              disabled={calendarBusy || calendarConnection?.status !== "connected"}
              onChange={(event) => saveCalendarPreference(event.target.checked)}
            />{" "}
            Ajouter automatiquement mes missions confirmées à cet agenda
          </label>
          <button
            type="button"
            className="portal-button secondary"
            disabled={calendarBusy}
            onClick={connectGoogleCalendar}
          >
            {calendarBusy
              ? "Connexion…"
              : calendarConnection?.status === "connected"
                ? "Reconnecter ou changer d’agenda"
                : "Connecter Google Agenda"}
          </button>
          {calendarConnection?.status === "connected" && (
            <button
              type="button"
              className="portal-button danger"
              disabled={calendarBusy}
              onClick={disconnectGoogleCalendar}
            >
              Déconnecter Google Agenda
            </button>
          )}
        </div>
        <small>
          Le bouton « Ajouter à Google Agenda » reste disponible dans chaque
          mission pour un ajout ponctuel, même si la synchronisation automatique
          est désactivée.
        </small>
        {calendarConnection?.last_error && (
          <p className="notice error">Dernière erreur : {calendarConnection.last_error}</p>
        )}
        {calendarMessage && (
          <p className={`notice ${calendarMessage.includes("activée") || calendarMessage.includes("désactivée") || calendarMessage.includes("démonstration") || calendarMessage.includes("connecté") ? "success" : "error"}`}>
            {calendarMessage}
          </p>
        )}
      </section>
      <section className="panel">
        <form className="form-grid" onSubmit={save}>
          <label>
            Nom affiché
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Téléphone
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          {client && (
            <>
              <label>
                Raison sociale / nom client
                <input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                />
              </label>
            </>
          )}
          {worker && (
            <>
              <label>
                Prénom
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </label>
              <label>
                Nom
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </label>
              {worker.kind === "contractor" && (
                <label>
                  Raison sociale
                  <input
                    value={business}
                    onChange={(e) => setBusiness(e.target.value)}
                  />
                </label>
              )}
            </>
          )}
          {(client || worker?.kind === "contractor") && (
            <>
              <label className="wide">
                Adresse professionnelle / d’intervention
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </label>
              <label>
                SIRET
                <input
                  value={siret}
                  onChange={(e) => setSiret(e.target.value)}
                />
              </label>
              <label>
                N° TVA
                <input value={vat} onChange={(e) => setVat(e.target.value)} />
              </label>
              <label className="wide">
                Informations utiles
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
            </>
          )}
          <button disabled={busy}>
            {busy
              ? "Envoi…"
              : client || worker
                ? "Envoyer mes modifications pour validation"
                : "Enregistrer mes coordonnées"}
          </button>
        </form>
        {message && (
          <p
            className={`notice ${message.startsWith("Votre demande") || message.includes("enregistrées") ? "success" : "error"}`}
          >
            {message}
          </p>
        )}
      </section>
      {worker && (
        <section className="panel definition">
          <div>
            <span>Profil</span>
            <strong>
              {worker.kind === "employee" ? "Employé" : "Sous-traitant"}
            </strong>
          </div>
          <div>
            <span>Journée habituelle</span>
            <strong>{worker.daily_hours} h</strong>
          </div>
          {worker.kind === "employee" && (
            <div>
              <span>Objectif mensuel</span>
              <strong>{worker.monthly_hours} h</strong>
            </div>
          )}
        </section>
      )}
    </>
  );
}
